import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button, ProgressBar, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { Fn_FillListData, Fn_AddEditData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import { toast } from "react-toastify";
import usePayrollParams from "../utils/usePayrollParams";
import useSession from "../utils/useSession";
import { isNotInUse, filterActiveMasters } from "../utils/masterFilters";
import { decryptPassword } from "../../helpers/SecurityHelper";
import {
  getCalculatedMonthDays,
  getWeeklyHolidayCount,
  isSundayIncludeForEmployee,
  isDirectWagesForEmployee,
  reconstructAllowancesFromRecord,
  reconstructDeductionsFromRecord,
  sortEmployeesByParams,
  getEmployeeDisplayLabel,
  isDailyWageEmployee,
  is26DaysForEmployee,
  computeArrearPF,
  computeESI,
  computeESIAllowancesAmount
} from "./PayrollHelpers";

const parseBoolFlag = (val, defaultVal = false) => {
  if (val === undefined || val === null || val === "") return defaultVal;
  return val === true || val === 1 || String(val).toLowerCase() === "true" || String(val) === "1";
};

// ─── react-select Custom Styles (Compact 28px Form Look) ──────────────────
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "28px",
    height: "28px",
    fontSize: "12px",
    borderRadius: "6px",
    borderColor: state.isFocused ? "#7c69ef" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(124,105,239,0.2)" : "none",
    "&:hover": { borderColor: "#7c69ef" },
    background: "#ffffff",
    padding: "0 2px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "28px",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
    padding: "0px",
    fontSize: "12px",
    color: "#1e293b",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "28px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px 4px",
    color: "#64748b",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "2px 4px",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontSize: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "220px",
  }),
  option: (provided, state) => ({
    ...provided,
    padding: "7px 12px",
    fontSize: "12px",
    fontWeight: state.isSelected ? 700 : state.isFocused ? 600 : 400,
    backgroundColor: state.isSelected
      ? "#5b47d6 !important"
      : state.isFocused
      ? "#7c69ef !important"
      : "#ffffff !important",
    color: state.isSelected || state.isFocused ? "#ffffff !important" : "#1e293b !important",
    cursor: "pointer",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#1e293b",
    fontWeight: 600,
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "12px",
    color: "#94a3b8",
  }),
};

// Helper to format option label based on active radio search criteria
const getEmployeeOptionLabel = (e, searchMode) => {
  if (!e) return "";
  const name = e.Name || "";
  const father = e.FatherName ? ` (Father: ${e.FatherName})` : "";
  const card = String(e.EmployeeCode || e.CardNo || "").trim();
  const uan = String(e.UAN || "").trim();
  const pf = String(e.PFNo || "").trim();
  const esi = String(e.ESINo || "").trim();

  switch (searchMode) {
    case "UAN":
      return `${uan ? `[UAN: ${uan}] ` : "[No UAN] "}${name}${father}${card ? ` [Card: ${card}]` : ""}`;
    case "PFNo":
      return `${pf ? `[PF: ${pf}] ` : "[No PF] "}${name}${father}${card ? ` [Card: ${card}]` : ""}`;
    case "ESINo":
      return `${esi ? `[ESI: ${esi}] ` : "[No ESI] "}${name}${father}${card ? ` [Card: ${card}]` : ""}`;
    case "Card":
      return `${card ? `[Card: ${card}] ` : ""}${name}${father}`;
    case "Father":
      return `${e.FatherName ? `[Father: ${e.FatherName}] ` : ""}${name}${card ? ` [Card: ${card}]` : ""}`;
    case "Name":
    default:
      return `${name}${father}${card ? ` [Card: ${card}]` : ""}`;
  }
};

// Filter across all fields regardless of which radio button is active
const filterEmployeeOption = (candidate, input) => {
  if (!input) return true;
  const q = input.toLowerCase().trim();
  const emp = candidate?.data?.employee;
  if (!emp) return (candidate?.label || "").toLowerCase().includes(q);

  const name = (emp.Name || "").toLowerCase();
  const father = (emp.FatherName || "").toLowerCase();
  const card = String(emp.EmployeeCode || emp.CardNo || "").toLowerCase();
  const uan = String(emp.UAN || "").toLowerCase();
  const pf = String(emp.PFNo || "").toLowerCase();
  const esi = String(emp.ESINo || "").toLowerCase();
  const sr = String(emp.SerialNo || "").toLowerCase();

  return (
    name.includes(q) ||
    father.includes(q) ||
    card.includes(q) ||
    uan.includes(q) ||
    pf.includes(q) ||
    esi.includes(q) ||
    sr.includes(q)
  );
};

// ─── Compact inline styles (modern slate & indigo palette) ──────────────────────────
const S = {
  page: { fontFamily: "'Poppins', sans-serif", fontSize: "12.5px", color: "#1e293b" },
  card: { border: "1px solid #e2e8f0", borderRadius: "10px", marginBottom: 0, background: "#ffffff", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" },
  cardBody: { padding: "12px 16px" },
  label: { fontSize: "12px", fontWeight: 500, marginBottom: 0, whiteSpace: "nowrap", lineHeight: "28px", color: "#475569" },
  input: { height: "28px", fontSize: "12px", padding: "2px 7px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#ffffff", color: "#1e293b", transition: "all 0.15s" },
  inputRO: { height: "28px", fontSize: "12px", padding: "2px 7px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569" },
  select: { height: "28px", fontSize: "12px", padding: "0 7px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#ffffff", color: "#1e293b" },
  row: { display: "flex", alignItems: "center", marginBottom: "4px", gap: "6px" },
  tab: { cursor: "pointer", padding: "8px 18px", fontSize: "12.5px", fontWeight: 500, border: "none", borderBottom: "2.5px solid transparent", background: "transparent", marginRight: "4px", userSelect: "none", color: "#64748b", transition: "all 0.15s" },
  tabActive: { cursor: "pointer", padding: "8px 18px", fontSize: "12.5px", fontWeight: 600, border: "none", borderBottom: "2.5px solid #7c69ef", background: "transparent", marginRight: "4px", userSelect: "none", color: "#7c69ef" },
  btn: { fontSize: "12px", padding: "6px 18px", fontWeight: 600, borderRadius: "6px", cursor: "pointer", border: "1px solid #cbd5e1", background: "#ffffff", color: "#334155", transition: "all 0.15s" },
  sectionLine: { borderBottom: "1px solid #e2e8f0", marginBottom: "8px", paddingBottom: "6px" },
  summaryLabel: { fontSize: "12.5px", fontWeight: 600, color: "#475569" },
  summaryVal: { fontSize: "13px", fontWeight: 700, color: "#1e293b", background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "3px 12px", minWidth: "110px", textAlign: "right", display: "inline-block" },
};

const LF = ({ label, children, lw = 120 }) => (
  <div style={S.row}>
    <label style={{ ...S.label, width: lw, minWidth: lw, textAlign: "right", paddingRight: "4px" }}>{label}</label>
    {children}
  </div>
);

export const RecordLockContext = React.createContext(false);

const I = React.memo(React.forwardRef(({
  value,
  onChange,
  w = 80,
  readOnly = false,
  disabled = false,
  step,
  name,
  style,
  onKeyDown,
  onFocus,
  ...rest
}, ref) => {
  const isRecordLocked = React.useContext(RecordLockContext);
  const effectiveReadOnly = readOnly || isRecordLocked;
  const effectiveDisabled = disabled || isRecordLocked;
  return (
    <input
      ref={ref}
      type="number"
      min="0"
      style={{ ...(effectiveReadOnly ? S.inputRO : S.input), width: w, ...style }}
      value={value}
      onChange={(e) => {
        if (!effectiveReadOnly && onChange) {
          const val = parseFloat(e.target.value);
          if (!isNaN(val) && val < 0) {
            e.target.value = 0;
          }
          onChange(e);
        }
      }}
      readOnly={effectiveReadOnly}
      disabled={effectiveDisabled}
      step={step}
      name={name}
      onFocus={(e) => {
        if (typeof e.target.select === "function") {
          e.target.select();
        }
        if (onFocus) {
          onFocus(e);
        }
      }}
      onKeyDown={(e) => {
        if (effectiveReadOnly) {
          return;
        }
        if (e.key === "-" || e.key === "e" || e.key === "E") {
          e.preventDefault();
        } else if (onKeyDown) {
          onKeyDown(e);
        }
      }}
      {...rest}
    />
  );
}));
I.displayName = "I";

const to2Dec = (val) => {
  const num = parseFloat(val);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const getMonthIndex = (monthVal) => {
  if (monthVal === undefined || monthVal === null || monthVal === "") {
    return new Date().getMonth();
  }
  if (typeof monthVal === "string") {
    const idx = MONTH_NAMES.findIndex(m => m.toLowerCase() === monthVal.toLowerCase());
    if (idx >= 0) return idx;
    const num = parseInt(monthVal, 10);
    if (!isNaN(num) && num >= 1 && num <= 12) return num - 1;
  } else if (typeof monthVal === "number" && monthVal >= 1 && monthVal <= 12) {
    return monthVal - 1;
  }
  return new Date().getMonth();
};

// Helper to parse date into Date object (handles ISO YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, etc.)
const parseEffDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return isNaN(dateVal.getTime()) ? null : dateVal;
  const s = String(dateVal).split("T")[0].split(" ")[0].trim();
  if (!s) return null;
  // YYYY-MM-DD or YYYY/MM/DD
  const m1 = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m1) {
    const y = parseInt(m1[1], 10);
    const m = parseInt(m1[2], 10) - 1;
    const d = parseInt(m1[3], 10);
    const dateObj = new Date(y, m, d);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  }
  // DD-MM-YYYY or DD/MM/YYYY
  const m2 = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m2) {
    const d = parseInt(m2[1], 10);
    const m = parseInt(m2[2], 10) - 1;
    const y = parseInt(m2[3], 10);
    const dateObj = new Date(y, m, d);
    return isNaN(dateObj.getTime()) ? null : dateObj;
  }
  const direct = new Date(s);
  return isNaN(direct.getTime()) ? null : direct;
};

// Helper to check if employee is eligible for the selected month/year period
// (Employees who left in prior months are excluded from future months, but appear in their exit month and past months)
export const isEmployeeEligibleForPeriod = (emp, selectedMonth, selectedYear, savedRecords = []) => {
  if (!emp) return false;
  // If employee already has a saved record for this month/year, always include
  if (savedRecords && savedRecords.some(r => String(r.F_EmployeeMaster) === String(emp.Id))) {
    return true;
  }

  const periodMonth = typeof selectedMonth === "number" ? selectedMonth : getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || new Date().getFullYear();
  const periodStart = new Date(periodYear, periodMonth, 1);
  const periodEnd = new Date(periodYear, periodMonth + 1, 0, 23, 59, 59, 999);

  let joinList = [];
  try { joinList = emp.JoinJson ? JSON.parse(emp.JoinJson) : []; } catch {}
  const latestJoin = Array.isArray(joinList) && joinList.length > 0 ? joinList[joinList.length - 1] : null;

  // Extract join and left dates
  const joinDateVal = emp.JoinDate || emp.Join_JoinDate || latestJoin?.JoinDate;
  const leftDateVal = emp.LeftDate || emp.Join_LeftDate || latestJoin?.LeftDate;

  const joinDate = parseEffDate(joinDateVal);
  const leftDate = parseEffDate(leftDateVal);

  // 1. If employee joined after the selected period end, they are not eligible for this period
  if (joinDate && joinDate > periodEnd) {
    return false;
  }

  // 2. If employee has a left date:
  // - If leftDate < periodStart (left in a prior month) -> exclude from future months
  // - If leftDate >= periodStart (left during or after this period) -> include in current and past months
  if (leftDate) {
    if (leftDate < periodStart) {
      return false;
    }
    return true;
  }

  // 3. Fallback check for Not In Use / Status Left without explicit date
  const status = String(emp.Status || emp.WorkingStatus || "").toLowerCase().trim();
  if (["left", "resigned", "terminated"].includes(status) && !leftDate) {
    return false;
  }

  if (isNotInUse(emp)) {
    return false;
  }

  return true;
};

// ─── Allowance calculation helpers ─────────────────────────────────────────
// For each Allowance Master, pick the latest entry whose EffectiveFrom <= selected Month/Year (with fallback to latest)
const getEffectiveAllowances = (allowanceList, selectedMonth, selectedYear) => {
  if (!allowanceList || !Array.isArray(allowanceList) || allowanceList.length === 0) {
    return [];
  }

  const periodMonth = typeof selectedMonth === "number" ? selectedMonth : getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || 0;

  // Group all entries by Allowance Master ID
  const groups = {};
  allowanceList.forEach((a, idx) => {
    if (!a) return;
    const masterId = a.F_AllowanceMaster || a.AllowanceMasterId || a.Id;
    if (!masterId || masterId === "none") return;
    const key = String(masterId);
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...a, _origIndex: idx });
  });

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a.EffectiveFrom || a.EffectiveDate || a.Date || "";
      const dateB = b.EffectiveFrom || b.EffectiveDate || b.Date || "";
      const dA = parseEffDate(dateA);
      const dB = parseEffDate(dateB);
      const tA = dA ? dA.getTime() : 0;
      const tB = dB ? dB.getTime() : 0;
      if (tA !== tB) {
        return tB - tA; // Latest date first
      }
      return (b._origIndex ?? 0) - (a._origIndex ?? 0); // Highest index first
    });
  };

  const result = [];

  Object.values(groups).forEach(entries => {
    if (entries.length === 0) return;

    if (periodYear > 0 && periodMonth >= 0) {
      const eligible = entries.filter(e => {
        const dateVal = e.EffectiveFrom || e.EffectiveDate || e.Date || "";
        const d = parseEffDate(dateVal);
        if (!d) return true;
        const y = d.getFullYear();
        const m = d.getMonth();
        return (y < periodYear) || (y === periodYear && m <= periodMonth);
      });

      if (eligible.length > 0) {
        const sortedEligible = sortLatestFirst(eligible);
        result.push(sortedEligible[0]);
        return;
      }
    }

    // Fallback: Pick latest version by EffectiveFrom date / last entry
    const sortedAll = sortLatestFirst(entries);
    result.push(sortedAll[0]);
  });

  return result;
};

// Compute a single allowance's effective amount based on Calculation type and WorkingDaysApply
const computeSingleAllowance = (a, monthDays, paidDays, basicSalary) => {
  if (!a) return 0;
  if (a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return 0;
  let base = parseFloat(a.Amount) || 0;
  const calc = String(a.Calculation || "").toUpperCase().trim();
  
  if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
    base = (base / 100) * (parseFloat(basicSalary) || 0);
  } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
    return Math.round(base * (parseFloat(paidDays) || 0) * 100) / 100;
  }
  
  const isWorkDays = a.IsWorkingDaysApply === true || a.IsWorkingDaysApply === "true" || a.IsWorkingDaysApply === 1;
  const mDays = parseFloat(monthDays) || 0;
  const pDays = parseFloat(paidDays) || 0;
  if (isWorkDays && mDays > 0) {
    base = (base / mDays) * pDays;
  }
  
  return Math.round(base * 100) / 100;
};

// MANUAL / DAILY / PERCENTAGE → Amount (with proper calculation logic)
const computeAllowancesTotal = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
  });
  return Math.round(total * 100) / 100;
};

// Sum of computed amounts for allowances where IsPFApply = true → added to PF wage base
const computePFAllowancesAmount = (allowanceList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveAllowances(allowanceList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(a => {
    const isPF = parseBoolFlag(a.IsPFApply, false);
    if (isPF) {
      total += computeSingleAllowance(a, monthDays, paidDays, basicSalary);
    }
  });
  return Math.round(total * 100) / 100;
};


// ─── Deduction calculation helpers (same pattern as Allowances) ─────────────
const getEffectiveDeductions = (deductionList, selectedMonth, selectedYear) => {
  if (!deductionList || !Array.isArray(deductionList) || deductionList.length === 0) {
    return [];
  }

  const periodMonth = typeof selectedMonth === "number" ? selectedMonth : getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || 0;

  // Group all entries by Deduction Master ID
  const groups = {};
  deductionList.forEach((d, idx) => {
    if (!d) return;
    const masterId = d.F_DeductionMaster || d.DeductionMasterId || d.Id;
    if (!masterId || masterId === "none") return;
    const key = String(masterId);
    if (!groups[key]) groups[key] = [];
    groups[key].push({ ...d, _origIndex: idx });
  });

  const sortLatestFirst = (list) => {
    return [...list].sort((a, b) => {
      const dateA = a.EffectiveFrom || a.EffectiveDate || a.Date || "";
      const dateB = b.EffectiveFrom || b.EffectiveDate || b.Date || "";
      const dA = parseEffDate(dateA);
      const dB = parseEffDate(dateB);
      const tA = dA ? dA.getTime() : 0;
      const tB = dB ? dB.getTime() : 0;
      if (tA !== tB) {
        return tB - tA; // Latest date first
      }
      return (b._origIndex ?? 0) - (a._origIndex ?? 0); // Highest index first
    });
  };

  const result = [];

  Object.values(groups).forEach(entries => {
    if (entries.length === 0) return;

    if (periodYear > 0 && periodMonth >= 0) {
      const eligible = entries.filter(e => {
        const dateVal = e.EffectiveFrom || e.EffectiveDate || e.Date || "";
        const dt = parseEffDate(dateVal);
        if (!dt) return true;
        const y = dt.getFullYear();
        const m = dt.getMonth();
        return (y < periodYear) || (y === periodYear && m <= periodMonth);
      });

      if (eligible.length > 0) {
        const sortedEligible = sortLatestFirst(eligible);
        result.push(sortedEligible[0]);
        return;
      }
    }

    // Fallback: Pick latest version by EffectiveFrom date / last entry
    const sortedAll = sortLatestFirst(entries);
    result.push(sortedAll[0]);
  });

  return result;
};

const computeSingleDeduction = (d, monthDays, paidDays, basicSalary) => {
  if (!d) return 0;
  if (d.IsApplicable === false || d.IsApplicable === "false" || d.IsApplicable === 0 || d.IsApplicable === "0") return 0;
  let base = parseFloat(d.Amount) || 0;
  const calc = String(d.Calculation || "").toUpperCase().trim();
  if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
    base = (base / 100) * (parseFloat(basicSalary) || 0);
  } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
    return Math.round(base * (parseFloat(paidDays) || 0) * 100) / 100;
  }
  const isWorkDays = d.IsWorkingDaysApply === true || d.IsWorkingDaysApply === "true" || d.IsWorkingDaysApply === 1;
  const mDays = parseFloat(monthDays) || 0;
  const pDays = parseFloat(paidDays) || 0;
  if (isWorkDays && mDays > 0) {
    base = (base / mDays) * pDays;
  }
  return Math.round(base * 100) / 100;
};

const computeDeductionsTotal = (deductionList, monthDays, paidDays, basicSalary, selectedMonth, selectedYear) => {
  const effective = getEffectiveDeductions(deductionList, selectedMonth, selectedYear);
  let total = 0;
  effective.forEach(d => {
    total += computeSingleDeduction(d, monthDays, paidDays, basicSalary);
  });
  return Math.round(total * 100) / 100;
};

// Pick latest SalaryJson entry whose EffectiveDate <= selected Month/Year (with master employee fallback)
const getEffectiveSalary = (salaryList, selectedMonth, selectedYear, emp = null) => {
  const periodMonth = typeof selectedMonth === "number" ? selectedMonth : getMonthIndex(selectedMonth);
  const periodYear = parseInt(selectedYear, 10) || new Date().getFullYear();

  const getEmpFallback = () => {
    if (!emp) return null;
    const bSal = parseFloat(emp.BasicSalary || emp.Sal_BasicSalary || emp.Basic || emp.RatePerDay || emp.Salary || 0);
    const bType = parseInt(emp.F_BasicTypeMaster || emp.BasicType || 1, 10) || 1;
    if (bSal > 0) {
      return { BasicSalary: bSal, F_BasicTypeMaster: bType, EffectiveDate: "" };
    }
    return null;
  };

  let list = salaryList;
  if (typeof list === "string") {
    try { list = JSON.parse(list); } catch { list = []; }
  }

  if (!list || !Array.isArray(list) || list.length === 0) {
    return getEmpFallback();
  }

  const validEntries = list.filter(s => s && (parseFloat(s.BasicSalary) > 0 || s.F_BasicTypeMaster));
  const poolToUse = validEntries.length > 0 ? validEntries : list;

  const eligible = poolToUse.filter(s => {
    const eff = parseEffDate(s.EffectiveDate || s.EffectiveFrom || s.Date);
    if (!eff) return true;
    const y = eff.getFullYear();
    const m = eff.getMonth();
    return (y < periodYear) || (y === periodYear && m <= periodMonth);
  });

  const pool = eligible.length > 0 ? eligible : poolToUse;

  const best = pool.reduce((acc, s) => {
    if (!acc) return s;
    const dA = parseEffDate(acc.EffectiveDate || acc.EffectiveFrom || acc.Date);
    const dB = parseEffDate(s.EffectiveDate || s.EffectiveFrom || s.Date);
    const tA = dA ? dA.getTime() : 0;
    const tB = dB ? dB.getTime() : 0;
    if (tB !== tA) return tB > tA ? s : acc;
    const salB = parseFloat(s.BasicSalary) || 0;
    const salA = parseFloat(acc.BasicSalary) || 0;
    if (salB > 0 && salA === 0) return s;
    return acc;
  }, null);

  if ((!best || !(parseFloat(best.BasicSalary) > 0)) && emp) {
    const fallback = getEmpFallback();
    if (fallback) {
      return {
        ...fallback,
        ...(best || {}),
        BasicSalary: fallback.BasicSalary,
        F_BasicTypeMaster: best?.F_BasicTypeMaster || fallback.F_BasicTypeMaster
      };
    }
  }

  return best || getEmpFallback();
};

// Compute Overtime amount from OvertimeHours using BasicType and payrollParams
// Monthly(1): basicSalary / divisorDays / 8 * multiplier * otHours
// Per Day(2): basicSalary / 8 * multiplier * otHours  (basicSalary is daily rate)
// Hourly(3):  basicSalary * multiplier * otHours       (basicSalary is already per-hour rate)
// Compute Overtime amount from OvertimeHours using BasicType and payrollParams
// Monthly(1): (GrossMonthly / divisorDays / 8) * multiplier * otHours
// Per Day(2): (GrossDaily / 8) * multiplier * otHours
// Hourly(3):  GrossHourly * multiplier * otHours
const computeOvertime = (salaryEntry, otHours, monthDays, payrollParams, allowancesOrTotal = 0, selectedMonth = null, selectedYear = null, basicSalaryForAllowances = null) => {
  const isOTEnabled = !!(
    payrollParams?.IsOvertime || 
    payrollParams?.OverTime || 
    String(payrollParams?.IsOvertime).toLowerCase() === "true" || 
    payrollParams?.IsOvertime === 1
  );
  if (!payrollParams || !isOTEnabled) { return 0; }
  if (!salaryEntry) { return 0; }
  const hours = parseFloat(otHours) || 0;
  if (hours <= 0) { return 0; }
  const basicSalary = parseFloat(salaryEntry.BasicSalary) || 0;
  if (basicSalary <= 0) { return 0; }
  const type       = salaryEntry.F_BasicTypeMaster;

  const isOTOn26 = !!(
    payrollParams.IsOTOn26Days === true ||
    payrollParams.IsOTOn26Days === 1 ||
    String(payrollParams.IsOTOn26Days).toLowerCase() === "true" ||
    String(payrollParams.IsOTOn26Days) === "1" ||
    payrollParams.OvertimeOn26Days === true ||
    payrollParams.OvertimeOn26Days === 1 ||
    String(payrollParams.OvertimeOn26Days).toLowerCase() === "true" ||
    payrollParams.IsOvertimeOn26Days === true ||
    String(payrollParams.IsOvertimeOn26Days).toLowerCase() === "true"
  );
  const divisor    = isOTOn26 ? 26 : (parseFloat(monthDays) || 26);

  const isDoubleOT = !!(
    payrollParams.IsDoubleOT === true ||
    payrollParams.IsDoubleOT === 1 ||
    String(payrollParams.IsDoubleOT).toLowerCase() === "true" ||
    String(payrollParams.IsDoubleOT) === "1" ||
    payrollParams.DoubleOvertimeAmount === true ||
    payrollParams.DoubleOvertimeAmount === 1 ||
    String(payrollParams.DoubleOvertimeAmount).toLowerCase() === "true" ||
    payrollParams.IsDoubleOvertimeAmount === true ||
    String(payrollParams.IsDoubleOvertimeAmount).toLowerCase() === "true"
  );
  const multiplier = isDoubleOT ? 2 : 1;
  const stdHours   = parseFloat(payrollParams.WorkingHours) || 8;

  const isOTOnGross = !!(
    payrollParams.IsOTOnGross || 
    payrollParams.OvertimeOnGrossWages || 
    payrollParams.IsOvertimeOnGrossWages ||
    String(payrollParams.IsOTOnGross).toLowerCase() === "true" ||
    String(payrollParams.OvertimeOnGrossWages).toLowerCase() === "true"
  );

  let baseRate = basicSalary;

  if (isOTOnGross) {
    if (Array.isArray(allowancesOrTotal)) {
      const effectiveAllowances = getEffectiveAllowances(allowancesOrTotal, selectedMonth, selectedYear);
      const allowanceBase = basicSalaryForAllowances !== null && basicSalaryForAllowances !== undefined
        ? (parseFloat(basicSalaryForAllowances) || 0)
        : basicSalary;

      if (type === 2) {
        // Daily rate: Basic is daily. Calculate daily allowances rate
        let dailyAllowances = 0;
        effectiveAllowances.forEach(a => {
          if (!a || a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return;
          const amt = parseFloat(a.Amount) || 0;
          const calc = String(a.Calculation || "").toUpperCase().trim();
          if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
            dailyAllowances += (amt / 100) * allowanceBase;
          } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
            dailyAllowances += amt;
          } else {
            dailyAllowances += divisor > 0 ? (amt / divisor) : 0;
          }
        });
        baseRate = basicSalary + dailyAllowances;
      } else if (type === 3) {
        // Hourly rate: Basic is hourly. Calculate hourly allowances rate
        let hourlyAllowances = 0;
        effectiveAllowances.forEach(a => {
          if (!a || a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return;
          const amt = parseFloat(a.Amount) || 0;
          const calc = String(a.Calculation || "").toUpperCase().trim();
          if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
            hourlyAllowances += (amt / 100) * allowanceBase;
          } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
            hourlyAllowances += amt / stdHours;
          } else {
            hourlyAllowances += divisor > 0 ? (amt / divisor / stdHours) : 0;
          }
        });
        baseRate = basicSalary + hourlyAllowances;
      } else {
        // Monthly rate (type 1 or default): Basic is monthly. Calculate monthly allowances rate
        let monthlyAllowances = 0;
        effectiveAllowances.forEach(a => {
          if (!a || a.IsApplicable === false || a.IsApplicable === "false" || a.IsApplicable === 0 || a.IsApplicable === "0") return;
          const amt = parseFloat(a.Amount) || 0;
          const calc = String(a.Calculation || "").toUpperCase().trim();
          if (calc === "PERCENTAGE" || calc === "PERCENT" || calc === "%") {
            monthlyAllowances += (amt / 100) * allowanceBase;
          } else if (calc === "DAILY" || calc === "PER DAY" || calc === "PERDAY") {
            monthlyAllowances += amt * divisor;
          } else {
            monthlyAllowances += amt;
          }
        });
        baseRate = basicSalary + monthlyAllowances;
      }
    } else {
      // Fallback if numeric allowances total was passed
      const numAllowances = parseFloat(allowancesOrTotal) || 0;
      if (numAllowances > 0) {
        if (type === 2) {
          const dailyAl = numAllowances > (basicSalary * 2) && divisor > 0
            ? (numAllowances / divisor)
            : numAllowances;
          baseRate = basicSalary + dailyAl;
        } else if (type === 3) {
          const hourlyAl = numAllowances > (basicSalary * 10) && divisor > 0
            ? (numAllowances / divisor / stdHours)
            : numAllowances;
          baseRate = basicSalary + hourlyAl;
        } else {
          baseRate = basicSalary + numAllowances;
        }
      }
    }
  }

  let hourlyRate = 0;
  if (type === 2) {
    hourlyRate = baseRate / stdHours;
  } else if (type === 3) {
    hourlyRate = baseRate;
  } else {
    hourlyRate = baseRate / divisor / stdHours;
  }
  const result = Math.round(hourlyRate * multiplier * hours * 100) / 100;
  return result;
};

// Calculate working days based on PayrollParameter settings
const computeWorkingDays = (monthDays, payrollParams, employee = null) => {
  if (!payrollParams) return 26; // default
  
  // If 26 working days applies (global or employee-wise)
  if (is26DaysForEmployee(payrollParams, employee)) return 26;
  if (payrollParams.Is26DaysWorking) return 26;
  
  // If Sunday Include is enabled or monthDays already excluded Sundays, return monthDays
  // (getCalculatedMonthDays already removes exact calendar Sundays when IsSundayInclude is false)
  return monthDays;
};

// Get working hours/minutes display for hourly payment
const getWorkingHoursDisplay = (workingHours, workingMinutes, payrollParams, basicType) => {
  if (!payrollParams?.IsHourlyPayment && basicType !== 3) return "";
  
  const hours = parseInt(workingHours) || 0;
  const minutes = parseInt(workingMinutes) || 0;
  
  // Only show if values are set
  if (hours === 0 && minutes === 0) return "";
  
  return `${hours}h ${minutes}m`;
};

// F_BasicTypeMaster: 1=Monthly (as-is), 2=Per Day (*WorkingDays), 3=Hourly (*WorkingDays*8)
const computeBasic = (salaryEntry, workingDays, payrollParams, employee = null) => {
  const raw  = parseFloat(salaryEntry?.BasicSalary || employee?.BasicSalary || employee?.Sal_BasicSalary || employee?.Salary || 0) || 0;
  const type = parseInt(salaryEntry?.F_BasicTypeMaster || employee?.F_BasicTypeMaster || employee?.BasicType || 1, 10) || 1;
  
  // Monthly: use as-is
  if (type === 1) return raw;
  
  // Per Day: multiply by working days
  if (type === 2) return Math.round(raw * workingDays * 100) / 100;
  
  // Hourly: multiply by working days * stdHours hours per day
  if (type === 3) {
    const stdHours = parseFloat(payrollParams?.WorkingHours) || 8;
    return Math.round(raw * workingDays * stdHours * 100) / 100;
  }
  
  return raw;
};

// Wages calculation based on PaidDays, WorkingHours, BasicType and rounding parameters
// WorkingHours & WorkingMinutes represent hours worked in addition to working days (days ke alawa extra hours)
const computeWages = (salaryEntry, paidDays, monthDays, workingHours = 0, workingMinutes = 0, payrollParams = null, employee = null) => {
  const basicSalary = parseFloat(salaryEntry?.BasicSalary || employee?.BasicSalary || employee?.Sal_BasicSalary || employee?.Salary || 0) || 0;
  const type = parseInt(salaryEntry?.F_BasicTypeMaster || employee?.F_BasicTypeMaster || employee?.BasicType || 1, 10) || 1;
  const stdHours = parseFloat(payrollParams?.WorkingHours) || 8;
  const extraHours = (parseFloat(workingHours) || 0) + ((parseFloat(workingMinutes) || 0) / 60);
  
  // Monthly: BasicSalary * (PaidDays / MonthDays) + (hourly pay for extra hours if IsHourlyPayment)
  if (type === 1) {
    if (monthDays === 0) return 0;
    const dayWages = (basicSalary * paidDays) / monthDays;
    const hourWages = (payrollParams?.IsHourlyPayment && extraHours > 0)
      ? ((basicSalary / monthDays / stdHours) * extraHours)
      : 0;
    const val = dayWages + hourWages;
    return payrollParams?.IsWagesRoundedOff ? Math.round(val) : Math.round(val * 100) / 100;
  }
  
  // Per Day: BasicSalary * PaidDays + (hourly pay for extra hours if IsHourlyPayment)
  if (type === 2) {
    const dayWages = basicSalary * paidDays;
    const hourWages = (payrollParams?.IsHourlyPayment && extraHours > 0)
      ? ((basicSalary / stdHours) * extraHours)
      : 0;
    const val = dayWages + hourWages;
    return payrollParams?.IsWagesRoundedOff ? Math.round(val) : Math.round(val * 100) / 100;
  }
  
  // Hourly: BasicSalary * totalHours where totalHours = (PaidDays * stdHours) + extraHours
  if (type === 3) {
    const totalHours = (paidDays * stdHours) + extraHours;
    const val = basicSalary * totalHours;
    return payrollParams?.IsWagesRoundedOff ? Math.round(val) : Math.round(val * 100) / 100;
  }
  
  return payrollParams?.IsWagesRoundedOff ? Math.round(basicSalary) : basicSalary;
};

const MONTH_NAMES_PY = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Determine leave calendar cycle date range & list of past months strictly prior to current payroll month
const getLeaveCycleMonths = (fromM, toM, currentMonthNum, currentYear) => {
  const fMonth = parseInt(fromM) || 1;
  const tMonth = parseInt(toM) || 12;

  let cycleStartYear = currentYear;
  let cycleEndYear = currentYear;

  if (fMonth > tMonth) {
    if (currentMonthNum >= fMonth) {
      cycleStartYear = currentYear;
      cycleEndYear = currentYear + 1;
    } else {
      cycleStartYear = currentYear - 1;
      cycleEndYear = currentYear;
    }
  }

  const pastMonthsList = [];
  if (fMonth <= tMonth) {
    for (let m = fMonth; m < currentMonthNum; m++) {
      pastMonthsList.push({ year: currentYear, month: m });
    }
  } else {
    if (currentYear === cycleStartYear) {
      for (let m = fMonth; m < currentMonthNum; m++) {
        pastMonthsList.push({ year: cycleStartYear, month: m });
      }
    } else {
      for (let m = fMonth; m <= 12; m++) {
        pastMonthsList.push({ year: cycleStartYear, month: m });
      }
      for (let m = 1; m < currentMonthNum; m++) {
        pastMonthsList.push({ year: cycleEndYear, month: m });
      }
    }
  }

  return { cycleStartYear, cycleEndYear, pastMonthsList };
};

// Extract days taken for a specific leave from a saved payroll transaction record
const extractLeaveDaysFromRecord = (rec, leave) => {
  if (!rec) return 0;
  if (rec.LeaveDetailJson) {
    try {
      const arr = typeof rec.LeaveDetailJson === "string" ? JSON.parse(rec.LeaveDetailJson) : rec.LeaveDetailJson;
      if (Array.isArray(arr)) {
        const item = arr.find(x => String(x.LeaveId) === String(leave.Id) || (x.LeaveName && String(x.LeaveName).trim().toLowerCase() === String(leave.Name).trim().toLowerCase()));
        if (item) return parseFloat(item.Taken) || 0;
      }
    } catch (_) {}
  }
  const lName = (leave.Name || "").toUpperCase();
  if (lName.includes("CL") || lName.includes("CASUAL")) return parseFloat(rec.CL) || 0;
  if (lName.includes("ML") || lName.includes("MED") || lName.includes("SICK")) return parseFloat(rec.ML) || 0;
  if (lName.includes("PL") || lName.includes("PRIV") || lName.includes("EARN")) return parseFloat(rec.LeaveWithWages) || 0;
  return 0;
};

const getInitialDynFields = () => {
  const fields = {};
  for (let i = 1; i <= 10; i++) {
    fields[`Allowance${i}Name`] = "";
    fields[`OriginalAllowance${i}`] = 0;
    fields[`CalculatedAllowance${i}`] = 0;
    fields[`Deduction${i}Name`] = "";
    fields[`OriginalDeduction${i}`] = 0;
    fields[`CalculatedDeduction${i}`] = 0;
  }
  return fields;
};

// ─── Module-level Caches for fast navigation & low latency ───
let cachedStaticMasters = null;
const cachedFirmMasters = new Map();
const cachedEmployeeData = new Map();
const cachedLeaveHistory = new Map();

const Payroll = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("entry");

  // ── Session: Firm / Month / Year from useSession hook ──
  const { firm: sessionFirm, firmId, month: sessionMonth, year: sessionYear } = useSession();
  const selectedMonth = sessionMonth || "";
  const selectedYear = parseInt(sessionYear || "0", 10);

  const activeFirmId = sessionFirm?.Id || firmId || 0;
  const initialEmployees = activeFirmId ? (cachedEmployeeData.get(activeFirmId) || []) : [];
  const firmCached = activeFirmId ? cachedFirmMasters.get(activeFirmId) : null;

  const isSessionReady = !!sessionFirm?.Id && !!selectedMonth && !!selectedYear;

  const [state, setState] = useState({
    employees: initialEmployees,
    selectedEmployee: null,
    isLoading: initialEmployees.length === 0,
  });
  const [rawAllowances, setRawAllowances] = useState([]);
  const [rawDeductions, setRawDeductions] = useState([]);
  const [allowanceMasterList, setAllowanceMasterList] = useState(cachedStaticMasters?.allowanceMasterList || []);
  const [deductionMasterList, setDeductionMasterList] = useState(cachedStaticMasters?.deductionMasterList || []);
  const [leaveMasterList, setLeaveMasterList] = useState(firmCached?.leaveMasterList || []); // LeaveMaster from firm
  const [leaveValues, setLeaveValues] = useState({}); // {leaveId: days}
  const [dynamicLeaveBalances, setDynamicLeaveBalances] = useState({}); // { [leaveId]: { OpBal, Entitled, PastEnjoyed, CurrentTaken, TotalEnjoyed, Bal } }
  const [pfRate, setPfRate] = useState(firmCached?.pfRate || null);
  const [esiRate, setEsiRate] = useState(firmCached?.esiRate || null);
  const [bonusRate, setBonusRate] = useState(firmCached?.bonusRate || null);
  const [gratuityRate, setGratuityRate] = useState(firmCached?.gratuityRate || null);
  const [payrollParams, setPayrollParams] = useState(firmCached?.payrollParams || null);
  const payrollParamsRef = React.useRef(payrollParams);
  const selectedEmployeeRef = React.useRef(state.selectedEmployee);
  const selectedMonthRef = React.useRef(selectedMonth);
  const selectedYearRef = React.useRef(selectedYear);
  const lastDaysRef = React.useRef(null);
  useEffect(() => {
    payrollParamsRef.current = payrollParams;
  }, [payrollParams]);
  useEffect(() => {
    selectedEmployeeRef.current = state.selectedEmployee;
  }, [state.selectedEmployee]);
  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);
  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);
  const [basicTypeMaster, setBasicTypeMaster] = useState(cachedStaticMasters?.basicTypeMaster || []);
  const [reasonOfLeftList, setReasonOfLeftList] = useState(cachedStaticMasters?.reasonOfLeftList || []);
  const [departmentList, setDepartmentList] = useState(firmCached?.departmentList || []);
  const departmentListRef = React.useRef(departmentList);
  useEffect(() => {
    departmentListRef.current = departmentList;
  }, [departmentList]);
  const leaveValuesRef = React.useRef(leaveValues);
  useEffect(() => {
    leaveValuesRef.current = leaveValues;
  }, [leaveValues]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [panelSearch, setPanelSearch] = useState("");
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [isRecalculatingAll, setIsRecalculatingAll] = useState(false);
  const [recalcProgress, setRecalcProgress] = useState({ current: 0, total: 0, empName: "" });
  const [searchBy, setSearchBy] = useState("Name"); // "Name" | "UAN" | "PFNo" | "ESINo" | "Card" | "Father"
  const empSelectRef = React.useRef(null);
  const userClearedRef = React.useRef(false);
  const [arrearModalData, setArrearModalData] = useState(null);
  

  // ─── Helper to focus Employee Dropdown (especially in Manual Entry mode) ───
  const focusEmployeeDropdown = useCallback((delay = 200) => {
    setTimeout(() => {
      if (empSelectRef.current) {
        empSelectRef.current.focus();
      }
      const empInput = document.querySelector('.payroll-form input[id^="react-select-"]');
      if (empInput) {
        empInput.focus();
      }
    }, delay);
  }, []);

  // ─── Delete All & Password Security Modal State ───
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllTarget, setDeleteAllTarget] = useState({ items: [], totalCount: 0 });
  const [securityPassword, setSecurityPassword] = useState("");
  const [deleteSecurityPassword, setDeleteSecurityPassword] = useState(firmCached?.deleteSecurityPassword || "015889");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [deleteAllProgress, setDeleteAllProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [showDeleteAllResultModal, setShowDeleteAllResultModal] = useState(false);
  const [deleteAllResults, setDeleteAllResults] = useState({ successCount: 0, failedCount: 0, failedItems: [] });
  const passwordInputRef = useRef(null);

  // ── Multi ESI / Multi PF filters ─────────────────────────────────────────
  const { isMultiESI, isMultiPF, esiNoList, pfNoList } = usePayrollParams(sessionFirm?.Id || firmId);
  const [filterEsiNo, setFilterEsiNo] = useState(""); // ESI No. filter for employee dropdown
  const [filterPfNo,  setFilterPfNo]  = useState(""); // PF No. filter for employee dropdown
  const [f, setF] = useState({
    F_EmployeeMaster: "", EmployeeName: "", FatherName: "", Department: "", JoinDate: "",
    BasicSalary: 0, HRA: 0, Conveyance: 0, CCA: 0, Medical: 0, Retention: 0,
    MonthDays: 31,
    WorkingDays: 0, WorkingHours: 0, WorkingMinutes: 0, LeaveWages: 0, FestivalHoliday: 0, WeeklyHoliday: 0, OvertimeHours: 0,
    Arrear: 0, AdvanceBonus: 0, ExtraPay: 0, ExtraPay1: 0,
    PFRefund: 0, Recovery: 0, Advance: 0, TDS: 0,
    PFReason: "", PFLast: "", ESIReason: "", ESILast: "",
    PL_Bal: 0, CL_Bal: 0, ML_Bal: 0, PL_Enjoy: 0, CL_Enjoy: 0, ML_Enjoy: 0, // CL_Bal/ML_Bal mapped to Leave1/Leave2 balance
    Wages: 0, OverTime: 0, EmplrPF: 0, Pension: 0, PF: 0, ArrearPF: 0, ArrERPF: 0, ArrPens: 0, EmplrESI: 0, ESI: 0,
    Bonus: 0, Gratuity: 0, // Employer-paid amounts (Bonus, Gratuity not deducted)
    AdminCharges: 0, AC21: 0, AC22: 0, // Employer-only PF charges (NOT deducted from employee)
    PFApplicable: false, ESIApplicable: false, PensionApplicable: true,
    EmpleeDedOverLimit: false, EmplrPFOverLimit: false, PensionOverLimit: false, HigherPension: false,
    FixedPFSalary: 0, DateOfBirth: "", IsDeferredPension: false, PensionAge: "", ExtraPF: 0, PFCalculationType: "Percentage",
    PaidDay: 0,
    TotalAllowances: 0, TotalDeductions: 0, GrossAmount: 0, TotalDeduction: 0, NetAmount: 0,
    LeaveWithWages: 0,
    LeaveFrom1: "", LeaveTo1: "", LeaveFrom2: "", LeaveTo2: "", LeaveFrom3: "", LeaveTo3: "",
    AbsentDays: 0,
    AbsentFrom1: "", AbsentTo1: "", AbsentFrom2: "", AbsentTo2: "", AbsentFrom3: "", AbsentTo3: "",
    BasicTypeName: "", WorkingHoursDisplay: "", // New fields
    ...getInitialDynFields(),
  });

  const set = React.useCallback((k, v) => {
    setF(p => ({ ...p, [k]: v }));
  }, []);
  const setNum = React.useCallback((k, v) => {
    if (v === "") {
      setF(p => ({ ...p, [k]: "" }));
      return;
    }
    let numericVal = parseFloat(v);
    if (isNaN(numericVal) || numericVal < 0) {
      numericVal = 0;
    }
    const isMidDecimal = typeof v === 'string' && (v.endsWith('.') || (v.includes('.') && v.endsWith('0')));
    const finalVal = isMidDecimal ? v : numericVal;

    setF(p => {
      const monthDays = parseFloat(p.MonthDays) || 0;
      let nextP = p;
      const dayFieldLabels = {
        WorkingDays: "Working Days",
        LeaveWages: "Leave Wages",
        LeaveWithWages: "Leave with Wages",
        FestivalHoliday: "Festival Holiday",
        WeeklyHoliday: "Weekly Holiday",
      };

      if (k in dayFieldLabels) {
        if (k === 'WeeklyHoliday') {
          const isNoWH = !!(payrollParamsRef.current?.IsNoWeeklyHolidayDailyWages || payrollParamsRef.current?.NoWeeklyHolidayForDailyWages);
          const salList = (() => { try { return JSON.parse(selectedEmployeeRef.current?.SalaryJson || "[]"); } catch { return []; } })();
          const salEntry = getEffectiveSalary(salList, selectedMonthRef.current, selectedYearRef.current);
          const isDaily = isDailyWageEmployee(p, salEntry) || isDailyWageEmployee(selectedEmployeeRef.current, salEntry);
          if (isNoWH && isDaily) {
            toast.warning("Weekly Holiday is not applicable for Daily Wage employees as per Payroll Parameters.");
            return { ...p, WeeklyHoliday: 0 };
          }
        }

        if (monthDays > 0) {
          const wd = k === 'WorkingDays' ? numericVal : (parseFloat(p.WorkingDays) || 0);
          const lw = k === 'LeaveWages' ? numericVal : (parseFloat(p.LeaveWages) || 0);
          const lww = k === 'LeaveWithWages' ? numericVal : (parseFloat(p.LeaveWithWages) || 0);
          const fh = k === 'FestivalHoliday' ? numericVal : (parseFloat(p.FestivalHoliday) || 0);
          const wh = k === 'WeeklyHoliday' ? numericVal : (parseFloat(p.WeeklyHoliday) || 0);
          const dynamicLeaves = Object.values(leaveValuesRef.current || {}).reduce((s, val) => s + (parseFloat(val) || 0), 0);

          const otherDays = (k === 'WorkingDays' ? 0 : wd)
            + (k === 'LeaveWages' ? 0 : lw)
            + (k === 'LeaveWithWages' ? 0 : (lww > 0 && lw === 0 ? lww : 0))
            + (k === 'FestivalHoliday' ? 0 : fh)
            + (k === 'WeeklyHoliday' ? 0 : wh)
            + dynamicLeaves;

          const totalDays = otherDays + numericVal;

          if (totalDays > monthDays) {
            const maxAllowed = Math.max(0, Math.round((monthDays - otherDays) * 100) / 100);
            toast.error(
              `Total days (${Math.round(totalDays * 100) / 100}) cannot exceed Month Days (${monthDays})! Maximum allowed for ${dayFieldLabels[k]} is ${maxAllowed}.`
            );
            nextP = {
              ...p,
              [k]: maxAllowed
            };
          } else {
            nextP = {
              ...p,
              [k]: finalVal
            };
          }
        } else {
          nextP = {
            ...p,
            [k]: finalVal
          };
        }
      } else if (k === 'MonthDays') {
        const newMonthDays = numericVal;
        const wd = parseFloat(p.WorkingDays) || 0;
        const lw = parseFloat(p.LeaveWages) || 0;
        const fh = parseFloat(p.FestivalHoliday) || 0;
        const wh = parseFloat(p.WeeklyHoliday) || 0;
        const dynamicLeaves = Object.values(leaveValuesRef.current || {}).reduce((s, val) => s + (parseFloat(val) || 0), 0);
        const totalCurrentDays = wd + lw + fh + wh + dynamicLeaves;

        if (newMonthDays > 0 && totalCurrentDays > newMonthDays) {
          const otherDays = lw + fh + wh + dynamicLeaves;
          const adjustedWD = Math.max(0, Math.round((newMonthDays - otherDays) * 100) / 100);
          toast.warning(
            `Month Days (${newMonthDays}) is less than Total Days entered (${Math.round(totalCurrentDays * 100) / 100}). Adjusting Working Days to ${adjustedWD}.`
          );
          nextP = {
            ...p,
            MonthDays: finalVal,
            WorkingDays: adjustedWD
          };
        } else {
          nextP = { ...p, MonthDays: finalVal };
        }
      } else if (k === 'WorkingHours') {
        nextP = { ...p, WorkingHours: finalVal };
      } else if (k === 'BasicSalary') {
        const isBasicEditable = !!(payrollParamsRef.current?.IsBasicEditable || payrollParamsRef.current?.BasicEditable);
        if (!isBasicEditable) {
          toast.warning("Basic Salary is not editable as per Payroll Parameters.");
          return p;
        }
        nextP = { ...p, BasicSalary: finalVal };
      } else if (k === 'Wages') {
        const isDirectWages = isDirectWagesForEmployee(payrollParamsRef.current, selectedEmployeeRef.current, departmentListRef.current);
        const isWagesEditable = isDirectWages || !!(payrollParamsRef.current?.IsBasicEditable || payrollParamsRef.current?.BasicEditable);
        if (!isWagesEditable) {
          toast.warning("Wages is not editable as per Payroll Parameters.");
          return p;
        }
        nextP = { ...p, Wages: finalVal };
      } else {
        nextP = { ...p, [k]: finalVal };
      }

      if (['WorkingDays', 'WorkingHours', 'WorkingMinutes', 'LeaveWages', 'FestivalHoliday', 'WeeklyHoliday'].includes(k)) {
        lastDaysRef.current = {
          WorkingDays: nextP.WorkingDays,
          WorkingHours: nextP.WorkingHours,
          WorkingMinutes: nextP.WorkingMinutes,
          LeaveWages: nextP.LeaveWages,
          FestivalHoliday: nextP.FestivalHoliday,
          WeeklyHoliday: nextP.WeeklyHoliday,
        };
      }

      return nextP;
    });
  }, []);

  const handleArrearBlur = useCallback((val) => {
    const arrearVal = parseFloat(val) || 0;
    if (arrearVal <= 0 || !f.PFApplicable) {
      setF(p => ({ ...p, ArrearPF: 0, ArrERPF: 0, ArrPens: 0 }));
      return;
    }

    const res = computeArrearPF(arrearVal, f.Pension, pfRate, f.PensionApplicable, payrollParams, {
      emp: state.selectedEmployee || f,
      selectedMonth,
      selectedYear,
      isHigherPen: f.HigherPension || f.PensionOverLimit,
    });

    if (res.IsExceeding && !f.HigherPension && !f.PensionOverLimit) {
      setArrearModalData({
        arrear: arrearVal,
        normalPension: res.NormalPension,
        fullArrPens: res.FullArrPens,
        cappedArrPens: res.CappedArrPens,
        combinedPension: res.CombinedPension,
        pensionCeilingLimit: res.PensionCeilingLimit,
        arrearPF: res.ArrearPF,
        totalEmployerArrear: res.TotalEmployerArrear,
        empName: state.selectedEmployee?.EmployeeName || f.EmployeeName || "",
        cardNo: state.selectedEmployee?.CardNo || state.selectedEmployee?.EmpCode || f.F_EmployeeMaster || "",
      });
    } else {
      setF(p => ({
        ...p,
        ArrearPF: res.ArrearPF,
        ArrPens: res.ArrPens,
        ArrERPF: res.ArrERPF,
      }));
    }
  }, [f.PFApplicable, f.Pension, f.PensionApplicable, f.HigherPension, f.PensionOverLimit, f.EmployeeName, f.F_EmployeeMaster, pfRate, payrollParams, state.selectedEmployee, selectedMonth, selectedYear]);

  const handleArrearModalChoice = useCallback((choice) => {
    if (!arrearModalData) return;
    const { arrearPF, totalEmployerArrear, fullArrPens, cappedArrPens } = arrearModalData;
    if (choice === 'full') {
      setF(p => ({
        ...p,
        ArrearPF: arrearPF,
        ArrPens: fullArrPens,
        ArrERPF: Math.max(0, totalEmployerArrear - fullArrPens),
      }));
    } else {
      setF(p => ({
        ...p,
        ArrearPF: arrearPF,
        ArrPens: cappedArrPens,
        ArrERPF: Math.max(0, totalEmployerArrear - cappedArrPens),
      }));
    }
    setArrearModalData(null);
  }, [arrearModalData]);

  const resetForm = useCallback(() => {
    setF({
      F_EmployeeMaster: "", EmployeeName: "", FatherName: "", Department: "", JoinDate: "",
      BasicSalary: 0, HRA: 0, Conveyance: 0, CCA: 0, Medical: 0, Retention: 0,
      MonthDays: 31,
      WorkingDays: 0, WorkingHours: 0, WorkingMinutes: 0, LeaveWages: 0, FestivalHoliday: 0, WeeklyHoliday: 0, OvertimeHours: 0,
      Arrear: 0, AdvanceBonus: 0, ExtraPay: 0, ExtraPay1: 0,
      PFRefund: 0, Recovery: 0, Advance: 0, TDS: 0, PFReason: "", PFLast: "", ESIReason: "", ESILast: "",
      PL_Bal: 0, CL_Bal: 0, ML_Bal: 0, PL_Enjoy: 0, CL_Enjoy: 0, ML_Enjoy: 0,
      Wages: 0, OverTime: 0, EmplrPF: 0, Pension: 0, PF: 0, ArrearPF: 0, ArrERPF: 0, ArrPens: 0, EmplrESI: 0, ESI: 0,
      Bonus: 0, Gratuity: 0,
      AdminCharges: 0, AC21: 0, AC22: 0,
      PFApplicable: false, ESIApplicable: false, PensionApplicable: true,
      EmpleeDedOverLimit: false, EmplrPFOverLimit: false, PensionOverLimit: false, HigherPension: false,
      FixedPFSalary: 0, DateOfBirth: "", IsDeferredPension: false, PensionAge: "", ExtraPF: 0, PFCalculationType: "Percentage",
      PaidDay: 0, TotalAllowances: 0, TotalDeductions: 0, GrossAmount: 0, TotalDeduction: 0, NetAmount: 0,
      LeaveWithWages: 0,
      LeaveFrom1: "", LeaveTo1: "", LeaveFrom2: "", LeaveTo2: "", LeaveFrom3: "", LeaveTo3: "",
      AbsentDays: 0,
      AbsentFrom1: "", AbsentTo1: "", AbsentFrom2: "", AbsentTo2: "", AbsentFrom3: "", AbsentTo3: "",
      BasicTypeName: "", WorkingHoursDisplay: "",
      ...getInitialDynFields(),
    });
    setRawAllowances([]);
    setRawDeductions([]);
    setLeaveValues(prev => {
      const reset = {};
      Object.keys(prev).forEach(k => { reset[k] = 0; });
      return reset;
    });
    setDynamicLeaveBalances({});
    setState(p => ({ ...p, selectedEmployee: null }));
    setSelectedRecordId(null);
    setActiveTab('entry');
  }, []);

  const handleCancel = useCallback(() => {
    userClearedRef.current = true;
    resetForm();
    const isAutoPayrollOn = !!(payrollParams?.IsAutoPayroll || payrollParams?.CreatePayrollAutomatically);
    if (!isAutoPayrollOn) {
      focusEmployeeDropdown(150);
    }
    toast.info("Cleared employee and payroll form.");
  }, [resetForm, payrollParams, focusEmployeeDropdown]);

  const loadEmployees = async (activeFirmId) => {
    try {
      const fid = activeFirmId || sessionFirm?.Id || firmId || 0;
      if (!fid) {
        setState(p => ({ ...p, employees: [], isLoading: false }));
        return [];
      }
      const data = await Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic/0/${fid}`);
      const list = data || [];
      cachedEmployeeData.set(fid, list);
      setState(p => ({ ...p, employees: list, isLoading: false }));
      return list;
    } catch {
      setState(p => ({ ...p, isLoading: false }));
      return [];
    }
  };

  // Log and fetch payroll session records for current firm, month, and year
  const logPayrollSession = useCallback(async () => {
    const activeFirmId = sessionFirm?.Id || firmId || 0;
    if (!activeFirmId || !selectedMonth || !selectedYear) {
      setSavedRecords([]);
      return [];
    }
    const monthNum = getMonthIndex(selectedMonth) + 1;
    const monthStr = String(monthNum).padStart(2, '0');
    try {
      const formData = new FormData();
      formData.append("Id", 0);
      formData.append("F_FirmMaster", activeFirmId);
      formData.append("PayrollMonth", monthStr);
      formData.append("PayrollYear", selectedYear);
      const res = await Fn_GetReport(
        dispatch,
        () => {}, // Do not pass setState
        "data",
        'GetPayrollTransaction/0/token',
        { arguList: { id: 0, formData: formData } },
        true
      );
      if (res && Array.isArray(res)) {
        setSavedRecords(res);
        return res;
      } else {
        setSavedRecords([]);
        return [];
      }
    } catch (err) {
      console.error("Error loading payroll records:", err);
      setSavedRecords([]);
      return [];
    }
  }, [dispatch, sessionFirm?.Id, firmId, selectedMonth, selectedYear]);

  // Unified batch loader that uses cache and parallel requests for fastest page load
  const loadAllPayrollData = useCallback(async (forcedFirmId) => {
    const fid = forcedFirmId || sessionFirm?.Id || firmId || 0;
    if (!fid) {
      setState(p => ({ ...p, employees: [], selectedEmployee: null, isLoading: false }));
      setSavedRecords([]);
      setLeaveMasterList([]);
      setDepartmentList([]);
      setPfRate(null);
      setEsiRate(null);
      setBonusRate(null);
      setGratuityRate(null);
      setPayrollParams(null);
      resetForm();
      return;
    }

    const hasCachedEmps = cachedEmployeeData.has(fid);
    if (!hasCachedEmps) {
      setState(p => ({ ...p, isLoading: true }));
    }
    
    try {
      // 1. Static Masters (fetch concurrently once per session)
      let staticPromise = Promise.resolve(cachedStaticMasters);
      if (!cachedStaticMasters) {
        staticPromise = Promise.all([
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/Id/0`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/Id/0`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BasicTypeMaster/Id/0`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/ReasonOfLeftMaster/Id/0`).catch(() => []),
        ]).then(([allowances, deductions, basicTypes, reasons]) => {
          const res = {
            allowanceMasterList: filterActiveMasters(allowances || []),
            deductionMasterList: filterActiveMasters(deductions || []),
            basicTypeMaster: filterActiveMasters(basicTypes || []),
            reasonOfLeftList: filterActiveMasters(reasons || []),
          };
          cachedStaticMasters = res;
          return res;
        });
      }

      // 2. Firm Masters (fetch concurrently once per firm)
      let firmPromise = Promise.resolve(cachedFirmMasters.get(fid));
      if (!cachedFirmMasters.has(fid)) {
        firmPromise = Promise.all([
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/TBL.F_FirmMaster/${fid}`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/LeaveMaster/TBL.F_FirmMaster/${fid}`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PfParamFetch/TBL.F_FirmMaster/${fid}`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EsiParamFetch/TBL.F_FirmMaster/${fid}`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BonusParamFetch/TBL.F_FirmMaster/${fid}`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/GratuityParamFetch/TBL.F_FirmMaster/${fid}`).catch(() => []),
          Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${fid}`).catch(() => []),
        ]).then(async ([depts, leaves, pf, esi, bonus, gratuity, params]) => {
          const activeLeaves = filterActiveMasters(leaves || []);
          const sortedPf = (pf || []).sort((a, b) => new Date(a.PFDate) - new Date(b.PFDate));
          const sortedEsi = (esi || []).sort((a, b) => new Date(a.ESIDate) - new Date(b.ESIDate));
          const sortedBonus = (bonus || []).sort((a, b) => new Date(a.BonusDate) - new Date(b.BonusDate));
          const sortedGratuity = (gratuity || []).sort((a, b) => new Date(a.GratuityDate) - new Date(b.GratuityDate));
          const pParam = (params && Array.isArray(params) && params.length > 0) ? params[0] : null;

          let decPass = "015889";
          if (pParam?.DeletePassword) {
            try {
              const dec = await decryptPassword(pParam.DeletePassword);
              if (dec && dec.trim()) decPass = dec.trim();
            } catch (_) {}
          }

          const res = {
            departmentList: depts || [],
            leaveMasterList: activeLeaves,
            pfRate: sortedPf.length > 0 ? sortedPf[sortedPf.length - 1] : null,
            esiRate: sortedEsi.length > 0 ? sortedEsi[sortedEsi.length - 1] : null,
            bonusRate: sortedBonus.length > 0 ? sortedBonus[sortedBonus.length - 1] : null,
            gratuityRate: sortedGratuity.length > 0 ? sortedGratuity[sortedGratuity.length - 1] : null,
            payrollParams: pParam,
            deleteSecurityPassword: decPass,
          };
          cachedFirmMasters.set(fid, res);
          return res;
        });
      }

      // 3. Employees (fetch from API if not in cache)
      let empPromise = Promise.resolve(cachedEmployeeData.get(fid));
      if (!cachedEmployeeData.has(fid)) {
        empPromise = Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic/0/${fid}`)
          .then(data => {
            const list = data || [];
            cachedEmployeeData.set(fid, list);
            return list;
          })
          .catch(() => []);
      }

      // 4. Saved records for current month & year
      let savedRecsPromise = Promise.resolve([]);
      if (selectedMonth && selectedYear) {
        const monthNum = getMonthIndex(selectedMonth) + 1;
        const monthStr = String(monthNum).padStart(2, '0');
        const formData = new FormData();
        formData.append("Id", 0);
        formData.append("F_FirmMaster", fid);
        formData.append("PayrollMonth", monthStr);
        formData.append("PayrollYear", selectedYear);
        savedRecsPromise = Fn_GetReport(
          dispatch,
          () => {},
          "data",
          'GetPayrollTransaction/0/token',
          { arguList: { id: 0, formData: formData } },
          true
        ).catch(() => []);
      }

      // Concurrently wait for all promises
      const [staticData, firmData, empList, savedList] = await Promise.all([
        staticPromise,
        firmPromise,
        empPromise,
        savedRecsPromise,
      ]);

      if (staticData) {
        setAllowanceMasterList(staticData.allowanceMasterList);
        setDeductionMasterList(staticData.deductionMasterList);
        setBasicTypeMaster(staticData.basicTypeMaster);
        setReasonOfLeftList(staticData.reasonOfLeftList);
      }

      if (firmData) {
        setDepartmentList(firmData.departmentList);
        setLeaveMasterList(firmData.leaveMasterList);
        const init = {};
        const initBals = {};
        firmData.leaveMasterList.forEach(l => {
          init[l.Id] = 0;
          initBals[l.Id] = { OpBal: 0, Entitled: parseFloat(l.MaxLimit) || 0, PastEnjoyed: 0, CurrentTaken: 0, TotalEnjoyed: 0, Bal: parseFloat(l.MaxLimit) || 0 };
        });
        setLeaveValues(init);
        setDynamicLeaveBalances(initBals);
        setPfRate(firmData.pfRate);
        setEsiRate(firmData.esiRate);
        setBonusRate(firmData.bonusRate);
        setGratuityRate(firmData.gratuityRate);
        setPayrollParams(firmData.payrollParams);
        setDeleteSecurityPassword(firmData.deleteSecurityPassword);
      }

      const safeSaved = Array.isArray(savedList) ? savedList : [];
      setSavedRecords(safeSaved);

      setState(p => ({
        ...p,
        employees: empList || [],
        isLoading: false,
      }));
    } catch (err) {
      console.error("Error loading payroll batch data:", err);
      setState(p => ({ ...p, isLoading: false }));
    }
  }, [dispatch, sessionFirm?.Id, firmId, selectedMonth, selectedYear, resetForm]);

  // Clear stale in-memory cache on component mount so multiple devices always fetch fresh data from database
  useEffect(() => {
    cachedFirmMasters.clear();
    cachedEmployeeData.clear();
    cachedLeaveHistory.clear();
  }, []);

  // When session firm changes: reset form, dropdown filters, reload all firm-specific masters & employees
  useEffect(() => {
    const activeFirmId = sessionFirm?.Id || firmId || 0;
    cachedFirmMasters.delete(activeFirmId);
    cachedEmployeeData.delete(activeFirmId);
    cachedLeaveHistory.clear();
    lastDaysRef.current = null;
    resetForm();
    setFilterEsiNo("");
    setFilterPfNo("");
    userClearedRef.current = false;
    loadAllPayrollData(activeFirmId);
  }, [sessionFirm?.Id, firmId, loadAllPayrollData, resetForm]);

  // When month or year changes without firm changing, reload saved records for current period
  useEffect(() => {
    userClearedRef.current = false;
    logPayrollSession();
  }, [selectedMonth, selectedYear, logPayrollSession]);

  // Auto-select first pending employee when session & employees are ready and IsAutoPayroll is ON
  useEffect(() => {
    const isAutoPayrollOn = !!(payrollParams?.IsAutoPayroll || payrollParams?.CreatePayrollAutomatically);
    if (!isAutoPayrollOn || !isSessionReady || state.isLoading) return;
    if (userClearedRef.current) return;
    if (f.F_EmployeeMaster || selectedRecordId !== null) return;
    if (!state.employees || state.employees.length === 0) return;

    const sortedActive = sortEmployeesByParams(state.employees, payrollParams)
      .filter(e => isEmployeeEligibleForPeriod(e, selectedMonth, selectedYear, savedRecords))
      .filter(e => !isMultiESI || !filterEsiNo || (e.ESINo || "").trim() === filterEsiNo)
      .filter(e => !isMultiPF  || !filterPfNo  || (e.PFNo  || "").trim() === filterPfNo);

    const pending = sortedActive.filter(e => !savedRecords.some(r => String(r.F_EmployeeMaster) === String(e.Id)));
    if (pending.length > 0) {
      handleEmployeeChange(pending[0].Id);
    }
  }, [payrollParams, isSessionReady, state.isLoading, state.employees, savedRecords, f.F_EmployeeMaster, selectedRecordId, isMultiESI, filterEsiNo, isMultiPF, filterPfNo]);



  // ─── PF / Pension / EmplrPF computation (Statutory & Dynamic Engine) ────────
  // Supports:
  // 1. PFBasis Only flag from PFParameter (if true, allowances are excluded)
  // 2. New Labour Code 50% Rule & Allowance Adjustment (Section 2(y))
  // 3. Dynamic Pro-rata Ceiling: (statMax / monthDays) * paidDays
  // 4. EmplrPFOverLimit & EmpleeDedOverLimit from Employee Master
  // 5. Age 58 EPS cut-off (Pension stops, 12% to EPF) & IsDeferredPension
  // 6. PensionOverLimit & HigherPension (Supreme Court 1.16% rule)
  // 7. FixedPFSalary & ExtraPF (VPF)
  // 8. Full PF by Employer
  // 9. EDLI (AC21) with AC2122OnFixedMaxPFWages cap
  const computePF = (
    basicSalary,
    pf,
    pfAllowancesAmount = 0,
    isPensionApplicable = true,
    pparms = payrollParams,
    options = {}
  ) => {
    if (!pf) return { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };

    const emp = options.emp || state?.selectedEmployee || null;
    const paidDays = options.paidDays !== undefined ? (parseFloat(options.paidDays) || 0) : (parseFloat(f.PaidDay) || 30);
    const monthDays = options.monthDays !== undefined ? (parseFloat(options.monthDays) || 30) : (parseFloat(f.MonthDays) || 30);
    const totalAllowances = parseFloat(options.totalAllowances) || 0;
    const periodMonth = options.selectedMonth || selectedMonth;
    const periodYear = options.selectedYear || selectedYear;

    // Rates & Ceilings
    const rate        = parseFloat(pf.Rate)              || 0;
    const erRate      = parseFloat(pf.EmployerRate)      || 0;
    const penRate     = parseFloat(pf.PensionRate)       || 0;
    const maxPen      = parseFloat(pf.MaxPensionAmt)     || 0;
    const maxWagesPF  = parseFloat(pf.MaxWagesForPF)     || 0;
    const maxWagesPen = parseFloat(pf.MaxWagesForPension)|| 0;

    // 1. Pro-rata Scaling for dynamic X (Paid Days) and Y (Month Days)
    const X = paidDays >= 0 ? paidDays : 30;
    const Y = monthDays > 0 ? monthDays : 30;
    const proRataMaxPF     = maxWagesPF > 0 ? Math.round((maxWagesPF / Y) * X) : 0;
    const proRataMaxPen    = maxWagesPen > 0 ? Math.round((maxWagesPen / Y) * X) : 0;
    const proRataMaxPenAmt = maxPen > 0 ? Math.round((maxPen / Y) * X) : 0;

    // 2. Base Wage: PFBasis Check
    // When PFBasis is TRUE (ON): Calculate strictly on Basic Salary only (allowances excluded).
    // When PFBasis is FALSE (OFF): Include PF Allowances (or total allowances if pfAllowancesAmount is 0).
    const isPFBasisOnly = parseBoolFlag(pf.PFBasis, false);
    const effectivePFAllowances = pfAllowancesAmount > 0
      ? pfAllowancesAmount
      : (parseFloat(options.totalAllowances) || 0);
    let pfBase = isPFBasisOnly ? basicSalary : (basicSalary + effectivePFAllowances);

    // 3. New Labour Code 50% Rule & Allowance Adjustment (Section 2(y))
    if (pf.NewLabourCodeApplyDate && periodMonth && periodYear) {
      try {
        const lCodeDate = new Date(pf.NewLabourCodeApplyDate);
        const mIdx = getMonthIndex(periodMonth);
        const yNum = parseInt(periodYear, 10);
        const pDate = new Date(yNum, mIdx, 1);
        const minBasicPrec = parseFloat(pf.NewLabourCodeBasicPrec) || 50.0;

        if (pDate >= lCodeDate && minBasicPrec > 0) {
          // Total Gross Remuneration: includes Basic + All Allowances + Overtime + Extra Pay + Bonus + Arrears
          const totalGross = options.gross !== undefined && options.gross !== null
            ? (parseFloat(options.gross) || 0)
            : (basicSalary + totalAllowances);
          const minWageFloor = Math.round((totalGross * minBasicPrec) / 100);
          if (pfBase < minWageFloor) {
            pfBase = minWageFloor; // Adjusted from allowances/other components to meet statutory floor
          }
        }
      } catch (_) {}
    }

    // 4. FixedPFSalary support if defined on employee
    const fixedPFSal = parseFloat(emp?.FixedPFSalary) || 0;
    const actualWages = fixedPFSal > 0 ? fixedPFSal : pfBase;

    // 5. Employee PF Wages & Overlimit Check
    // Note: If Higher Pension is active, Employee contribution must also be on actual wages per EPFO Joint Option rules
    const isHigherPen = options.isHigherPen !== undefined ? !!options.isHigherPen : !!(emp?.HigherPension);
    const isEeOverLimit = options.isEeOverLimit !== undefined ? !!options.isEeOverLimit : !!(emp?.EmpleeDedOverLimit || isHigherPen);
    const eePFWages = (isEeOverLimit || isHigherPen)
      ? actualWages
      : (proRataMaxPF > 0 ? Math.min(actualWages, proRataMaxPF) : actualWages);

    let empPF = Math.round((eePFWages * rate) / 100);

    // Extra PF (VPF)
    const extraPF = parseFloat(emp?.ExtraPF) || 0;
    if (extraPF > 0) {
      if (String(emp?.PFCalculationType || "").toLowerCase() === "percentage") {
        empPF += Math.round((eePFWages * extraPF) / 100);
      } else {
        empPF += Math.round(extraPF);
      }
    }

    // 6. Employer PF Wages & Overlimit Check
    // Note: If Higher Pension is active, Employer wage base must expand to actual wages so EPS contribution can be funded
    const isErOverLimit = options.isErOverLimit !== undefined ? !!options.isErOverLimit : !!(emp?.EmplrPFOverLimit || isHigherPen);
    const erPFWages = (isErOverLimit || isHigherPen)
      ? actualWages
      : (proRataMaxPF > 0 ? Math.min(actualWages, proRataMaxPF) : actualWages);

    const totalEmployerContr = Math.round((erPFWages * erRate) / 100);

    // 7. Age 58 Cut-off for EPS
    let isRetiredFromEPS = false;
    if (emp?.DateOfBirth) {
      try {
        const dob = new Date(emp.DateOfBirth);
        const yNum = parseInt(periodYear, 10) || new Date().getFullYear();
        const mIdx = getMonthIndex(periodMonth);
        let age = yNum - dob.getFullYear();
        if (mIdx < dob.getMonth()) age--;
        const retAge = parseFloat(pparms?.RetirementAge) || 58;
        if (age >= retAge && !emp?.IsDeferredPension) {
          isRetiredFromEPS = true;
        }
      } catch (_) {}
    }

    const penApply = isPensionApplicable !== false &&
      isPensionApplicable !== 0 &&
      isPensionApplicable !== "0" &&
      String(isPensionApplicable).toLowerCase() !== "false" &&
      Boolean(isPensionApplicable) &&
      !isRetiredFromEPS;

    // 8. Pension (EPS A/C 10) Calculation (Capped at statutory ceiling ₹1,250 unless PensionOverLimit / HigherPension is ON)
    let pension = 0;
    if (penApply && penRate > 0) {
      const isPenOverLimit = options.isPenOverLimit !== undefined ? !!options.isPenOverLimit : !!(emp?.PensionOverLimit || isHigherPen);
      const penWages = (isPenOverLimit || isHigherPen)
        ? actualWages
        : (maxWagesPen > 0 ? Math.min(actualWages, maxWagesPen) : actualWages);

      pension = Math.round((penWages * penRate) / 100);

      if (!isPenOverLimit && !isHigherPen && maxPen > 0) {
        pension = Math.min(pension, maxPen);
      }

      // Higher Pension Supreme Court 1.16% rule (diverted from employer contribution on excess wages)
      const higherPenThreshold = maxWagesPen > 0 ? maxWagesPen : 15000;
      if (isHigherPen && higherPenThreshold > 0 && penWages > higherPenThreshold) {
        const additionalPen = Math.round(((penWages - higherPenThreshold) * 1.16) / 100);
        pension += additionalPen;
      }

      // Pension cannot exceed total employer contribution on these wages
      pension = Math.min(pension, totalEmployerContr);
    }

    // Employer EPF (A/C 1) = Total Employer - Pension
    let emplrPF = Math.max(totalEmployerContr - pension, 0);

    // 9. Full PF by employer parameter support
    const isFullPF = !!(pparms?.IsFullPFEmployer || pparms?.FullPFByEmployer || String(pparms?.IsFullPFEmployer).toLowerCase() === "true" || pparms?.IsFullPFEmployer === 1);
    if (isFullPF && empPF > 0) {
      emplrPF += empPF;
      empPF = 0;
    }

    // 10. Admin Charges (A/C 2) — Employer only
    const adminRate   = parseFloat(pf.AdminCharges)           || 0;
    const adminMin    = parseFloat(pf.AdminChargesMinAmount)  || 0;
    const adminCalc   = Math.round(erPFWages * adminRate / 100 * 100) / 100;
    const AdminCharges = Math.max(adminCalc, adminMin);

    // 11. AC21 (EDLI) — Employer only (Capped at 15,000 / pro-rata ceiling if AC2122OnFixedMaxPFWages is ON)
    const isEDLIFixed    = !!(pparms?.AC2122OnFixedMaxPFWages);
    const edliWages      = isEDLIFixed && proRataMaxPF > 0 ? Math.min(erPFWages, proRataMaxPF) : erPFWages;
    const ac21WithGrp    = parseFloat(pf.AC21WithGroupIns)         || 0;
    const ac21WithoutGrp = parseFloat(pf.AC21WithoutGroupIns)      || 0;
    const ac21MinAmt     = parseFloat(pf.AC21WithGroupInsMinAmt)   || 0;
    const ac21Rate       = ac21WithGrp > 0 ? ac21WithGrp : ac21WithoutGrp;
    const ac21Calc       = Math.round(edliWages * ac21Rate / 100 * 100) / 100;
    const maxEDLIAmt     = Y > 0 ? (75.0 / Y) * X : 75.0;
    const AC21           = Math.min(Math.max(ac21Calc, ac21MinAmt), maxEDLIAmt);

    // 12. AC22 — Employer only (Waived off to 0)
    const ac22WithGrp    = parseFloat(pf.AC22WithGroupIns)         || 0;
    const ac22WithoutGrp = parseFloat(pf.AC22WithoutGroupIns)      || 0;
    const ac22MinAmt     = parseFloat(pf.AC22WithGroupInsMinAmt)   || 0;
    const ac22Rate       = ac22WithGrp > 0 ? ac22WithGrp : ac22WithoutGrp;
    const ac22Calc       = Math.round(erPFWages * ac22Rate / 100 * 100) / 100;
    const AC22           = ac22Rate > 0 ? Math.max(ac22Calc, ac22MinAmt) : 0;

    return { PF: empPF, EmplrPF: emplrPF, Pension: pension, AdminCharges, AC21, AC22 };
  };

  // ─── Bonus computation ─────────────────────────────────────────────────────
  // Bonus = (wages * BonusRate / 100) or flat amount based on configuration
  const computeBonus = (wages, bonus, workingDays, paidDays, pparms) => {
    if (!bonus) return 0;
    if (pparms && pparms.IsBonusMonthly === false) return 0; // if monthly bonus is disabled
    
    const rate = parseFloat(bonus.BonusRate) || 0;
    const maxWages = parseFloat(bonus.MaximumWages) || 0;
    const maxBonus = parseFloat(bonus.MaximumBonus) || 0;
    const minBonus = parseFloat(bonus.MinimumBonus) || 0;
    const minDays = parseFloat(bonus.MinimumBonusDays) || 0;
    
    // Check whether bonus is on working days or paid days
    const daysForBonus = (pparms?.IsBonusOnWorkingDays) ? workingDays : paidDays;
    
    // Check minimum working days eligibility
    if (minDays > 0 && daysForBonus < minDays) return 0;
    
    // If IsBonusOnMinWages is enabled and minWages is specified, base on MinWages
    const minWagesParam = parseFloat(pparms?.MinWages) || 0;
    let baseWages = (pparms?.IsBonusOnMinWages && minWagesParam > 0) ? minWagesParam : wages;

    // Calculate wages for bonus (capped at MaximumWages if specified)
    const bonusWages = maxWages > 0 ? Math.min(baseWages, maxWages) : baseWages;
    let bonusAmount = bonusWages * rate / 100;
    
    // Apply maximum and minimum bonus limits
    if (maxBonus > 0) bonusAmount = Math.min(bonusAmount, maxBonus);
    if (minBonus > 0) bonusAmount = Math.max(bonusAmount, minBonus);
    
    return Math.round(bonusAmount * 100) / 100;
  };

  // ─── Gratuity computation ───────────────────────────────────────────────────
  // Gratuity = (LastDrawnBasic * 15 * YearsOfService) / 26  or Rate-based calculation
  const computeGratuity = (basicSalary, gratuity, yearsOfService = 1) => {
    if (!gratuity) return 0;
    const rate = parseFloat(gratuity.Rate) || 0;
    
    // If rate is provided, use rate-based calculation
    if (rate > 0) {
      return Math.round(basicSalary * rate / 100 * 100) / 100;
    }
    
    // Statutory gratuity calculation
    const payableDays = parseFloat(payrollParams?.GratuityPayableDays) || 15;
    const years = parseFloat(yearsOfService) || 1;
    return Math.round((basicSalary * payableDays * years / 26) * 100) / 100;
  };

  // Helper to sync standard legacy leave fields with top 3 leaves
  const syncStandardLeaveFields = useCallback((bals, curVals = {}) => {
    const plLeave = leaveMasterList.find(l => String(l.Name).toUpperCase().includes("PL") || String(l.Name).toUpperCase().includes("PRIV") || String(l.Name).toUpperCase().includes("EARN")) || leaveMasterList[0];
    const clLeave = leaveMasterList.find(l => String(l.Name).toUpperCase().includes("CL") || String(l.Name).toUpperCase().includes("CASUAL")) || leaveMasterList[1] || leaveMasterList[0];
    const mlLeave = leaveMasterList.find(l => String(l.Name).toUpperCase().includes("ML") || String(l.Name).toUpperCase().includes("MED") || String(l.Name).toUpperCase().includes("SICK")) || leaveMasterList[2] || leaveMasterList[1];

    setF(p => ({
      ...p,
      PL_Bal: plLeave ? (bals[plLeave.Id]?.Bal ?? p.PL_Bal) : p.PL_Bal,
      PL_Enjoy: plLeave ? (bals[plLeave.Id]?.TotalEnjoyed ?? p.PL_Enjoy) : p.PL_Enjoy,
      CL_Bal: clLeave ? (bals[clLeave.Id]?.Bal ?? p.CL_Bal) : p.CL_Bal,
      CL_Enjoy: clLeave ? (bals[clLeave.Id]?.TotalEnjoyed ?? p.CL_Enjoy) : p.CL_Enjoy,
      ML_Bal: mlLeave ? (bals[mlLeave.Id]?.Bal ?? p.ML_Bal) : p.ML_Bal,
      ML_Enjoy: mlLeave ? (bals[mlLeave.Id]?.TotalEnjoyed ?? p.ML_Enjoy) : p.ML_Enjoy,
      LeaveWithWages: plLeave ? (parseFloat(curVals[plLeave.Id] !== undefined ? curVals[plLeave.Id] : leaveValues[plLeave.Id]) || 0) : p.LeaveWithWages,
    }));
  }, [leaveMasterList, leaveValues]);

  // Load past records in current leave cycle and compute dynamic leave balances
  const loadAndComputeEmployeeLeaves = useCallback(async (emp, initialLeaveVals = {}) => {
    if (!emp || !leaveMasterList || leaveMasterList.length === 0) return {};
    const activeFirmId = sessionFirm?.Id || firmId || 0;
    const curMonthNum = getMonthIndex(selectedMonth) + 1;

    let pastRecords = [];
    if (activeFirmId && emp.Id) {
      const cacheKey = `${activeFirmId}_${emp.Id}_${selectedYear}`;
      if (cachedLeaveHistory.has(cacheKey)) {
        pastRecords = cachedLeaveHistory.get(cacheKey);
      } else {
        try {
          const fdCur = new FormData();
          fdCur.append("Id", 0);
          fdCur.append("F_FirmMaster", activeFirmId);
          fdCur.append("F_EmployeeMaster", emp.Id);
          fdCur.append("PayrollYear", selectedYear);
          fdCur.append("PayrollMonth", "");

          const pCur = Fn_GetReport(dispatch, () => {}, "data", 'GetPayrollTransaction/0/token', { arguList: { id: 0, formData: fdCur } }, true).catch(() => []);

          let pPrev = Promise.resolve([]);
          if (curMonthNum <= 4) {
            const fdPrev = new FormData();
            fdPrev.append("Id", 0);
            fdPrev.append("F_FirmMaster", activeFirmId);
            fdPrev.append("F_EmployeeMaster", emp.Id);
            fdPrev.append("PayrollYear", selectedYear - 1);
            fdPrev.append("PayrollMonth", "");
            pPrev = Fn_GetReport(dispatch, () => {}, "data", 'GetPayrollTransaction/0/token', { arguList: { id: 0, formData: fdPrev } }, true).catch(() => []);
          }

          const [resCur, resPrev] = await Promise.all([pCur, pPrev]);
          pastRecords = [...(Array.isArray(resPrev) ? resPrev : []), ...(Array.isArray(resCur) ? resCur : [])];
          cachedLeaveHistory.set(cacheKey, pastRecords);
        } catch (err) {
          console.error("Error fetching past payroll records:", err);
        }
      }
    }

    // Parse employee opening balances
    let openingList = [];
    if (emp.LeaveOpeningJson) {
      try {
        openingList = typeof emp.LeaveOpeningJson === "string" ? JSON.parse(emp.LeaveOpeningJson) : emp.LeaveOpeningJson;
        if (!Array.isArray(openingList)) openingList = [];
      } catch (_) { openingList = []; }
    }

    const newBals = {};
    leaveMasterList.forEach(leave => {
      const { pastMonthsList } = getLeaveCycleMonths(leave.CalenderFromMonth, leave.CalenderToMonth, curMonthNum, selectedYear);

      // Filter past records to months in the current cycle strictly prior to current month
      const cyclePastRecords = pastRecords.filter(r => {
        const rYear = parseInt(r.PayrollYear) || 0;
        let rMonthNum = 0;
        if (r.PayrollMonth) {
          const parsed = parseInt(r.PayrollMonth);
          if (!isNaN(parsed) && parsed >= 1 && parsed <= 12) rMonthNum = parsed;
          else {
            const idx = MONTH_NAMES_PY.findIndex(m => m.toLowerCase() === String(r.PayrollMonth).toLowerCase());
            if (idx !== -1) rMonthNum = idx + 1;
          }
        }
        return pastMonthsList.some(pm => pm.year === rYear && pm.month === rMonthNum);
      });

      const pastEnjoyed = cyclePastRecords.reduce((sum, r) => sum + extractLeaveDaysFromRecord(r, leave), 0);

      // Opening balance
      let opBal = 0;
      const matchedOp = openingList.find(x => String(x.LeaveMasterId) === String(leave.Id) || (x.LeaveName && String(x.LeaveName).trim().toLowerCase() === String(leave.Name).trim().toLowerCase()));
      if (matchedOp && matchedOp.OpeningBalance !== undefined && matchedOp.OpeningBalance !== "") {
        opBal = parseFloat(matchedOp.OpeningBalance) || 0;
      } else if (String(leave.Name).toUpperCase().includes("PL") || String(leave.Name).toUpperCase().includes("EARN")) {
        opBal = parseFloat(emp.LeaveBalance) || 0;
      }

      const entitled = parseFloat(leave.MaxLimit) || 0;
      const currentTaken = parseFloat(initialLeaveVals[leave.Id] !== undefined ? initialLeaveVals[leave.Id] : leaveValues[leave.Id]) || 0;
      const totalEnjoyed = pastEnjoyed + currentTaken;
      const bal = Math.max(0, Math.round(((opBal + entitled) - totalEnjoyed) * 100) / 100);

      newBals[leave.Id] = {
        OpBal: opBal,
        Entitled: entitled,
        PastEnjoyed: pastEnjoyed,
        CurrentTaken: currentTaken,
        TotalEnjoyed: totalEnjoyed,
        Bal: bal
      };
    });

    setDynamicLeaveBalances(newBals);
    syncStandardLeaveFields(newBals, initialLeaveVals);
    return newBals;
  }, [leaveMasterList, sessionFirm?.Id, firmId, selectedMonth, selectedYear, dispatch, leaveValues, syncStandardLeaveFields]);

  // Handle leave days input change from attendance section
  const handleCurrentLeaveChange = (leaveId, cleanVal) => {
    let numVal = cleanVal === "" ? 0 : parseFloat(cleanVal) || 0;
    const isMaxLeaveValidate = !!(payrollParamsRef.current?.IsMaxLeaveValidate || payrollParams?.IsMaxLeaveValidate);

    if (isMaxLeaveValidate && numVal > 0) {
      const cur = dynamicLeaveBalances[leaveId] || { OpBal: 0, Entitled: 0, PastEnjoyed: 0 };
      const leaveObj = leaveMasterList.find(l => String(l.Id) === String(leaveId));
      const leaveName = leaveObj?.Name || "Leave";
      const entitled = cur.Entitled !== undefined ? parseFloat(cur.Entitled) : (parseFloat(leaveObj?.MaxLimit) || 0);
      const opBal = parseFloat(cur.OpBal) || 0;
      const pastEnjoyed = parseFloat(cur.PastEnjoyed) || 0;
      const maxAvailable = Math.max(0, Math.round(((opBal + entitled) - pastEnjoyed) * 100) / 100);

      if (numVal > maxAvailable) {
        toast.error(`Leave '${leaveName}' (${numVal} days) exceeds available balance (${maxAvailable} days). Max Leave validation is active.`);
        numVal = maxAvailable > 0 ? maxAvailable : 0;
        cleanVal = numVal > 0 ? numVal : "";
      }
    }

    // ── Total Days cannot exceed Month Days validation ──
    const monthDays = parseFloat(f.MonthDays) || 0;
    if (monthDays > 0 && numVal > 0) {
      const wd = parseFloat(f.WorkingDays) || 0;
      const lw = parseFloat(f.LeaveWages) || 0;
      const lww = parseFloat(f.LeaveWithWages) || 0;
      const fh = parseFloat(f.FestivalHoliday) || 0;
      const wh = parseFloat(f.WeeklyHoliday) || 0;
      const otherLeaves = Object.entries(leaveValues)
        .filter(([id]) => String(id) !== String(leaveId))
        .reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
      const otherDays = wd + lw + (lww > 0 && lw === 0 ? lww : 0) + fh + wh + otherLeaves;
      const totalDays = otherDays + numVal;

      if (totalDays > monthDays) {
        const leaveObj = leaveMasterList.find(l => String(l.Id) === String(leaveId));
        const leaveName = leaveObj?.Name || "Leave";
        const maxAllowed = Math.max(0, Math.round((monthDays - otherDays) * 100) / 100);
        toast.error(
          `Total days (${Math.round(totalDays * 100) / 100}) cannot exceed Month Days (${monthDays})! Maximum allowed for '${leaveName}' is ${maxAllowed}.`
        );
        numVal = maxAllowed;
        cleanVal = numVal > 0 ? numVal : "";
      }
    }

    const nextVals = { ...leaveValues, [leaveId]: cleanVal };
    setLeaveValues(nextVals);

    setDynamicLeaveBalances(prev => {
      const cur = prev[leaveId] || { OpBal: 0, Entitled: 0, PastEnjoyed: 0 };
      const totalEnjoyed = (parseFloat(cur.PastEnjoyed) || 0) + numVal;
      const baseBal = (parseFloat(cur.OpBal) || 0) + (parseFloat(cur.Entitled) || 0);
      const netBal = Math.max(0, Math.round((baseBal - totalEnjoyed) * 100) / 100);
      const nextBals = {
        ...prev,
        [leaveId]: {
          ...cur,
          CurrentTaken: numVal,
          TotalEnjoyed: totalEnjoyed,
          Bal: netBal
        }
      };
      syncStandardLeaveFields(nextBals, nextVals);
      return nextBals;
    });
  };

  // Handle direct manual edit of top leave Bal / Enjoy
  const handleLeaveBalanceChange = (leaveId, field, value) => {
    const num = value === "" ? "" : (parseFloat(value) || 0);
    setDynamicLeaveBalances(prev => {
      const cur = prev[leaveId] || { OpBal: 0, Entitled: 0, PastEnjoyed: 0, CurrentTaken: 0, TotalEnjoyed: 0, Bal: 0 };
      const nextBals = {
        ...prev,
        [leaveId]: {
          ...cur,
          [field]: num
        }
      };
      syncStandardLeaveFields(nextBals);
      return nextBals;
    });
  };

  const handleEmployeeChange = async (id, copiedDays = null, empListOverride = null, savedRecsOverride = null) => {
    if (!id) { 
      userClearedRef.current = true;
      set('F_EmployeeMaster', ''); set('EmployeeName', ''); set('FatherName', ''); set('Department', ''); 
      set('BasicTypeName', ''); set('WorkingHoursDisplay', '');
      setRawAllowances([]);
      setRawDeductions([]);
      setSelectedRecordId(null); // Clear edit mode
      setState(p => ({ ...p, selectedEmployee: null }));
      return; 
    }
    userClearedRef.current = false;

    const isCopyDaysOn = !!(payrollParams?.IsCopyDaysNextEmployee || payrollParams?.CopyDaysToNextEmployee);

    // If currently active employee in form had days entered, preserve them before switching
    if (f.F_EmployeeMaster && (parseFloat(f.WorkingDays) > 0 || parseFloat(f.WorkingHours) > 0 || parseFloat(f.FestivalHoliday) > 0 || parseFloat(f.WeeklyHoliday) > 0)) {
      lastDaysRef.current = {
        WorkingDays: f.WorkingDays,
        WorkingHours: f.WorkingHours,
        WorkingMinutes: f.WorkingMinutes,
        LeaveWages: f.LeaveWages,
        FestivalHoliday: f.FestivalHoliday,
        WeeklyHoliday: f.WeeklyHoliday,
      };
    }

    const effectiveCopiedDays = copiedDays || (isCopyDaysOn ? lastDaysRef.current : null);

    const currentSavedRecords = savedRecsOverride || savedRecords;
    const currentEmployees = empListOverride || state.employees;

    // Check if employee already has a saved record for this month/year (unless editing)
    if (selectedRecordId === null) {
      const existingRec = currentSavedRecords.find(r => String(r.F_EmployeeMaster) === String(id));
      if (existingRec) {
        loadSavedRecord(existingRec);
        toast.info(`Loaded saved payroll record for ${existingRec.EmployeeName || 'employee'}.`);
        return;
      }
    }

    const emp = currentEmployees.find(e => String(e.Id) === String(id));
    if (!emp) return;
    let al = [];
    try { al = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : []; } catch {}
    let dl = [];
    try { dl = emp.DeductionJson ? JSON.parse(emp.DeductionJson) : []; } catch {}
    let salList = [];
    try { salList = emp.SalaryJson ? JSON.parse(emp.SalaryJson) : []; } catch {}
    const effAl = getEffectiveAllowances(al, selectedMonth, selectedYear);
    const effDl = getEffectiveDeductions(dl, selectedMonth, selectedYear);
    setRawAllowances(effAl);
    setRawDeductions(effDl);
    setSelectedRecordId(null);

    let joinList = [];
    try { joinList = emp.JoinJson ? JSON.parse(emp.JoinJson) : []; } catch {}
    const latestJoin = Array.isArray(joinList) && joinList.length > 0 ? joinList[joinList.length - 1] : null;
    const exitReason = (emp.Join_ReasonOfLeft || emp.ReasonOfLeft || latestJoin?.ReasonOfLeft || "").trim();
    const exitDateRaw = emp.Join_LeftDate || emp.LeftDate || latestJoin?.LeftDate || "";
    const exitDate = (exitDateRaw && !String(exitDateRaw).startsWith("1899") && !String(exitDateRaw).startsWith("1900")) 
      ? formatDateForSave(exitDateRaw) 
      : "";

    if (!copiedDays && isCopyDaysOn && effectiveCopiedDays && (parseFloat(effectiveCopiedDays.WorkingDays) > 0 || parseFloat(effectiveCopiedDays.WorkingHours) > 0)) {
      toast.info("Auto-filled days from previous employee", { autoClose: 2000 });
    }

    setF(p => {
      const salEntry     = getEffectiveSalary(salList, selectedMonth, selectedYear, emp);
      const is26Days     = is26DaysForEmployee(payrollParams, emp);
      const isDailyWage  = (salEntry?.F_BasicTypeMaster === 2 || String(salEntry?.F_BasicTypeMaster) === "2") || isDailyWageEmployee(emp, salEntry);
      const isHourly     = salEntry?.F_BasicTypeMaster === 3 || String(salEntry?.F_BasicTypeMaster) === "3";
      const isNoWH       = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages);
      const calculatedMonthDays = getCalculatedMonthDays(selectedMonth, selectedYear, payrollParams, emp.WeeklyHoliday, departmentList, emp);
      const defaultWorkingDays  = computeWorkingDays(calculatedMonthDays, payrollParams, emp);

      const stdWorkingHours = parseFloat(payrollParams?.WorkingHours) || 8;
      const workingDays = effectiveCopiedDays ? Math.min(calculatedMonthDays, (parseFloat(effectiveCopiedDays.WorkingDays) || 0)) : defaultWorkingDays;
      const workingHours = effectiveCopiedDays ? (parseFloat(effectiveCopiedDays.WorkingHours) || 0) : 0;
      const workingMinutes = effectiveCopiedDays ? (parseFloat(effectiveCopiedDays.WorkingMinutes) || 0) : 0;
      const leaveWages = effectiveCopiedDays ? (parseFloat(effectiveCopiedDays.LeaveWages) || 0) : 0;
      const festivalHoliday = effectiveCopiedDays ? (parseFloat(effectiveCopiedDays.FestivalHoliday) || 0) : 0;
      const weeklyHoliday = (is26Days || (isNoWH && isDailyWage)) ? 0 : (effectiveCopiedDays ? (parseFloat(effectiveCopiedDays.WeeklyHoliday) || 0) : 0);
      const overtimeHours = 0; // Overtime is employee specific, never copy across workers
      const extraPay = 0;      // ExtraPay is employee specific, never copy across workers
      const extraPay1 = 0;     // ExtraPay1 is employee specific, never copy across workers

      // Leave values are employee-specific balances; do not copy from previous worker
      const currentLeaves = leaveValues;
      const totalLeave = Object.values(currentLeaves).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDays     = Math.min(calculatedMonthDays, workingDays + leaveWages + festivalHoliday + weeklyHoliday + totalLeave);
      // monthlyBasic = monthly equivalent used for % allowance bases (e.g. 500/day → 500*26=13000)
      const monthlyBasic = computeBasic(salEntry, workingDays, payrollParams, emp);
      // wages = actual earned wages based on BasicType + paid days
      const wages        = computeWages(salEntry, paidDays, calculatedMonthDays, workingHours, workingMinutes, payrollParams, emp);
      const totalAl      = computeAllowancesTotal(effAl, calculatedMonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      const totalDed     = computeDeductionsTotal(effDl, calculatedMonthDays, paidDays, monthlyBasic, selectedMonth, selectedYear);
      
      // Get BasicType name
      const basicType = basicTypeMaster.find(bt => String(bt.Id) === String(salEntry?.F_BasicTypeMaster || emp.F_BasicTypeMaster || emp.BasicType || 1));
      const basicTypeName = basicType ? basicType.Name : "";
      
      // Get working hours display for hourly payment
      const workingHoursDisplay = getWorkingHoursDisplay(workingHours, workingMinutes, payrollParams, salEntry?.F_BasicTypeMaster || emp.F_BasicTypeMaster || emp.BasicType || 1);
      
      // Opening leave balance carried forward from Employee Master
      let initialPLBal = parseFloat(emp.LeaveBalance) || 0;
      if (emp.LeaveOpeningJson) {
        try {
          const lJson = typeof emp.LeaveOpeningJson === "string" ? JSON.parse(emp.LeaveOpeningJson) : emp.LeaveOpeningJson;
          if (Array.isArray(lJson) && lJson.length > 0) {
            const plRow = lJson.find(x => String(x.LeaveName).toUpperCase().includes("PL") || String(x.LeaveName).toUpperCase().includes("EARN") || String(x.LeaveName).toUpperCase().includes("PRIV"));
            if (plRow && plRow.OpeningBalance !== undefined && plRow.OpeningBalance !== "") {
              initialPLBal = parseFloat(plRow.OpeningBalance) || 0;
            } else if (lJson[0]?.OpeningBalance !== undefined && lJson[0]?.OpeningBalance !== "") {
              initialPLBal = parseFloat(lJson[0].OpeningBalance) || 0;
            }
          }
        } catch (_) {}
      }

      return {
        ...p,
        F_EmployeeMaster: id,
        EmployeeName: emp.Name || "",
        FatherName: emp.FatherName || "",
        Department: emp.DepartmentName || emp.Department || "",
        JoinDate: emp.JoinDate ? String(emp.JoinDate).substring(0, 10) : "",
        PFReason: (exitDate && exitReason) ? exitReason : "",
        PFLast: (exitDate && exitReason) ? exitDate : "",
        ESIReason: (exitDate && exitReason) ? exitReason : "",
        ESILast: (exitDate && exitReason) ? exitDate : "",
        LastWorking: (exitDate && exitReason) ? exitDate : "",
        MonthDays: calculatedMonthDays,
        WorkingDays: workingDays,
        WorkingHours: workingHours,
        WorkingMinutes: workingMinutes,
        LeaveWages: leaveWages,
        FestivalHoliday: festivalHoliday,
        WeeklyHoliday: weeklyHoliday,
        PaidDay: paidDays,
        OvertimeHours: overtimeHours,
        ExtraPay: extraPay,
        ExtraPay1: extraPay1,
        PL_Bal: initialPLBal,
        // Show raw rate from master: 500 for daily, 13000 for monthly — as-is (with master fallback)
        BasicSalary: parseFloat(salEntry?.BasicSalary) || parseFloat(emp.BasicSalary || emp.Sal_BasicSalary || emp.Salary || 0) || 0,
        Wages: wages,
        TotalAllowances: totalAl,
        TotalDeductions: totalDed,
        BasicTypeName: basicTypeName,
        WorkingHoursDisplay: workingHoursDisplay,
        PFApplicable: parseBoolFlag(emp.PFApplicable, false),
        ESIApplicable: parseBoolFlag(emp.ESIApplicable, false),
        PensionApplicable: parseBoolFlag(emp.PensionApplicable, true),
        EmpleeDedOverLimit: parseBoolFlag(emp.EmpleeDedOverLimit, false),
        EmplrPFOverLimit: parseBoolFlag(emp.EmplrPFOverLimit, false),
        PensionOverLimit: parseBoolFlag(emp.PensionOverLimit, false),
        HigherPension: parseBoolFlag(emp.HigherPension, false),
        FixedPFSalary: parseFloat(emp.FixedPFSalary) || 0,
        DateOfBirth: emp.DateOfBirth || "",
        IsDeferredPension: !!emp.IsDeferredPension,
        PensionAge: emp.PensionAge || "",
        ExtraPF: parseFloat(emp.ExtraPF) || 0,
        PFCalculationType: emp.PFCalculationType || "Percentage",
      };
    });
    setState(p => ({ ...p, selectedEmployee: emp }));

    // Reset current month inputs and dynamically compute cycle balances
    const initVals = {};
    leaveMasterList.forEach(l => { initVals[l.Id] = 0; });
    setLeaveValues(initVals);
    loadAndComputeEmployeeLeaves(emp, initVals);

    // Auto-focus WorkingDays and select text so user can press Enter to keep copied days or type new days
    setTimeout(() => {
      const wdInput = document.querySelector('input[name="WorkingDays"]');
      if (wdInput) {
        wdInput.focus();
        if (typeof wdInput.select === 'function') wdInput.select();
      }
    }, 120);
  };

  // Load saved record into form
  const loadSavedRecord = (record) => {
    if (!record || !record.F_EmployeeMaster) return;
    
    // Find employee and fill basic info
    const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
    
    setSelectedRecordId(record.Id); // Mark as edit mode

    if (payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked) {
      toast.info("Payroll transactions are locked in Payroll Parameters. This saved record is read-only.");
    }

    // ── Restore rawAllowances & rawDeductions ──
    let al = [];
    let dl = [];
    if (emp) {
      try { al = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : []; } catch {}
      try { dl = emp.DeductionJson ? JSON.parse(emp.DeductionJson) : []; } catch {}
    }
    let effAl = getEffectiveAllowances(al, selectedMonth, selectedYear);
    let effDl = getEffectiveDeductions(dl, selectedMonth, selectedYear);

    if (effAl.length === 0) {
      effAl = reconstructAllowancesFromRecord(record, allowanceMasterList);
    }
    if (effDl.length === 0) {
      effDl = reconstructDeductionsFromRecord(record, deductionMasterList);
    }

    setRawAllowances(effAl);
    setRawDeductions(effDl);
    
    let joinList = [];
    if (emp) {
      try { joinList = emp.JoinJson ? JSON.parse(emp.JoinJson) : []; } catch {}
    }
    const latestJoin = Array.isArray(joinList) && joinList.length > 0 ? joinList[joinList.length - 1] : null;
    const exitReason = emp?.Join_ReasonOfLeft || emp?.ReasonOfLeft || latestJoin?.ReasonOfLeft || "";
    const exitDateRaw = emp?.Join_LeftDate || emp?.LeftDate || latestJoin?.LeftDate || "";
    const exitDate = exitDateRaw ? formatDateForSave(exitDateRaw) : "";

    const isDailyWage = isDailyWageEmployee(record) || isDailyWageEmployee(emp) || (record.F_BasicTypeMaster === 2);
    const isNoWH = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages);
    const effectiveWeeklyHoliday = (isNoWH && isDailyWage) ? 0 : (parseFloat(record.WeeklyHoliday) || 0);

    // Populate form with saved record data
    lastDaysRef.current = {
      WorkingDays: parseFloat(record.WorkingDays) || 0,
      WorkingHours: parseFloat(record.WorkingHours) || 0,
      WorkingMinutes: parseFloat(record.WorkingMinutes) || 0,
      LeaveWages: parseFloat(record.LeaveWages) || 0,
      FestivalHoliday: parseFloat(record.FestivalHoliday) || 0,
      WeeklyHoliday: effectiveWeeklyHoliday,
    };

    setF(p => ({
      ...p,
      F_EmployeeMaster: record.F_EmployeeMaster,
      EmployeeName: emp?.Name || record.EmployeeName || "",
      FatherName: emp?.FatherName || record.FatherName || "",
      Department: emp?.DepartmentName || emp?.Department || record.Department || "",
      JoinDate: emp?.JoinDate ? String(emp.JoinDate).substring(0, 10) : (record.JoinDate ? String(record.JoinDate).substring(0, 10) : ""),
      BasicSalary: parseFloat(record.BasicSalary) || 0,
      HRA: parseFloat(record.HRA) || 0,
      Conveyance: parseFloat(record.Conveyance) || 0,
      CCA: parseFloat(record.CCA) || 0,
      Medical: parseFloat(record.Medical) || 0,
      Retention: parseFloat(record.Retention) || 0,
      MonthDays: parseFloat(record.MonthDays) || 31,
      WorkingDays: parseFloat(record.WorkingDays) || 0,
      WorkingHours: parseFloat(record.WorkingHours) || 0,
      WorkingMinutes: parseFloat(record.WorkingMinutes) || 0,
      LeaveWages: parseFloat(record.LeaveWages) || 0,
      FestivalHoliday: parseFloat(record.FestivalHoliday) || 0,
      WeeklyHoliday: effectiveWeeklyHoliday,
      CL: parseFloat(record.CL) || 0,
      ML: parseFloat(record.ML) || 0,
      OvertimeHours: parseFloat(record.OvertimeHours) || 0,
      Arrear: parseFloat(record.Arrear) || 0,
      AdvanceBonus: parseFloat(record.AdvanceBonus) || 0,
      ExtraPay: parseFloat(record.ExtraPay) || 0,
      ExtraPay1: parseFloat(record.ExtraPay1) || 0,
      PFRefund: parseFloat(record.PFRefund) || 0,
      Recovery: parseFloat(record.Recovery) || 0,
      Advance: parseFloat(record.Advance) || 0,
      PFReason: record.PFReason || "",
      PFLast: (record.PFLast && !String(record.PFLast).startsWith("1899") && !String(record.PFLast).startsWith("1900")) ? String(record.PFLast).substring(0, 10) : "",
      ESIReason: record.ESIReason || "",
      ESILast: (record.ESILast && !String(record.ESILast).startsWith("1899") && !String(record.ESILast).startsWith("1900")) 
        ? String(record.ESILast).substring(0, 10) 
        : ((record.LastWorking && !String(record.LastWorking).startsWith("1899") && !String(record.LastWorking).startsWith("1900")) ? String(record.LastWorking).substring(0, 10) : ""),
      LastWorking: (record.LastWorking && !String(record.LastWorking).startsWith("1899") && !String(record.LastWorking).startsWith("1900")) 
        ? String(record.LastWorking).substring(0, 10) 
        : ((record.ESILast && !String(record.ESILast).startsWith("1899") && !String(record.ESILast).startsWith("1900")) ? String(record.ESILast).substring(0, 10) : ""),
      PL_Bal: parseFloat(record.PL_Bal) || 0,
      CL_Bal: parseFloat(record.CL_Bal) || 0,
      ML_Bal: parseFloat(record.ML_Bal) || 0,
      PL_Enjoy: parseFloat(record.PL_Enjoy) || 0,
      CL_Enjoy: parseFloat(record.CL_Enjoy) || 0,
      ML_Enjoy: parseFloat(record.ML_Enjoy) || 0,
      Wages: parseFloat(record.Wages) || 0,
      OverTime: parseFloat(record.OverTime) || 0,
      EmplrPF: parseFloat(record.EmplrPF) || 0,
      Pension: parseFloat(record.Pension) || 0,
      PF: parseFloat(record.PF) || 0,
      ArrearPF: parseFloat(record.ArrearPF) || 0,
      ArrERPF: parseFloat(record.ArrERPF) || 0,
      ArrPens: parseFloat(record.ArrPens) || 0,
      EmplrESI: parseFloat(record.EmplrESI) || 0,
      ESI: parseFloat(record.ESI) || 0,
      Bonus: parseFloat(record.Bonus) || 0,
      Gratuity: parseFloat(record.Gratuity) || 0,
      AdminCharges: parseFloat(record.AdminCharges) || 0,
      AC21: parseFloat(record.AC21) || 0,
      AC22: parseFloat(record.AC22) || 0,
      PFApplicable: parseBoolFlag(emp?.PFApplicable !== undefined ? emp.PFApplicable : record.PFApplicable, false),
      ESIApplicable: parseBoolFlag(emp?.ESIApplicable !== undefined ? emp.ESIApplicable : record.ESIApplicable, false),
      PensionApplicable: parseBoolFlag(emp?.PensionApplicable !== undefined ? emp.PensionApplicable : record.PensionApplicable, true),
      EmpleeDedOverLimit: parseBoolFlag(emp?.EmpleeDedOverLimit !== undefined ? emp.EmpleeDedOverLimit : record.EmpleeDedOverLimit, false),
      EmplrPFOverLimit: parseBoolFlag(emp?.EmplrPFOverLimit !== undefined ? emp.EmplrPFOverLimit : record.EmplrPFOverLimit, false),
      PensionOverLimit: parseBoolFlag(emp?.PensionOverLimit !== undefined ? emp.PensionOverLimit : record.PensionOverLimit, false),
      HigherPension: parseBoolFlag(emp?.HigherPension !== undefined ? emp.HigherPension : record.HigherPension, false),
      FixedPFSalary: parseFloat(emp?.FixedPFSalary) || 0,
      DateOfBirth: emp?.DateOfBirth || "",
      IsDeferredPension: emp?.IsDeferredPension ?? false,
      PensionAge: emp?.PensionAge || "",
      ExtraPF: parseFloat(emp?.ExtraPF) || 0,
      PFCalculationType: emp?.PFCalculationType || "Percentage",
      PaidDay: parseFloat(record.PaidDay) || 0,
      TotalAllowances: parseFloat(record.TotalAllowances) || 0,
      TotalDeductions: parseFloat(record.TotalDeductions) || 0,
      GrossAmount: parseFloat(record.GrossAmount) || 0,
      TotalDeduction: parseFloat(record.TotalDeduction) || 0,
      NetAmount: parseFloat(record.NetAmount) || 0,
      LeaveWithWages: parseFloat(record.LeaveWithWages) || 0,
      LeaveFrom1: record.LeaveFrom1 || "",
      LeaveTo1: record.LeaveTo1 || "",
      LeaveFrom2: record.LeaveFrom2 || "",
      LeaveTo2: record.LeaveTo2 || "",
      LeaveFrom3: record.LeaveFrom3 || "",
      LeaveTo3: record.LeaveTo3 || "",
      AbsentDays: parseFloat(record.AbsentDays) || 0,
      AbsentFrom1: record.AbsentFrom1 || "",
      AbsentTo1: record.AbsentTo1 || "",
      AbsentFrom2: record.AbsentFrom2 || "",
      AbsentTo2: record.AbsentTo2 || "",
      AbsentFrom3: record.AbsentFrom3 || "",
      AbsentTo3: record.AbsentTo3 || "",
      // ── Restore BasicType name from salary master ──
      BasicTypeName: (() => {
        try {
          const salList = emp?.SalaryJson ? JSON.parse(emp.SalaryJson) : [];
          const salEntry = getEffectiveSalary(salList, selectedMonth, selectedYear);
          const bt = basicTypeMaster.find(b => b.Id === (record.F_BasicTypeMaster || salEntry?.F_BasicTypeMaster));
          return bt ? bt.Name : "";
        } catch { return ""; }
      })(),
      // ── Restore saved Allowance / Deduction name + amount fields ──
      ...(() => {
        const dynFields = {};
        for (let i = 1; i <= 10; i++) {
          dynFields[`Allowance${i}Name`]       = record[`Allowance${i}Name`]       || "";
          dynFields[`OriginalAllowance${i}`]   = parseFloat(record[`OriginalAllowance${i}`])   || 0;
          dynFields[`CalculatedAllowance${i}`] = parseFloat(record[`CalculatedAllowance${i}`]) || 0;
          dynFields[`Deduction${i}Name`]       = record[`Deduction${i}Name`]       || "";
          dynFields[`OriginalDeduction${i}`]   = parseFloat(record[`OriginalDeduction${i}`])   || 0;
          dynFields[`CalculatedDeduction${i}`] = parseFloat(record[`CalculatedDeduction${i}`]) || 0;
        }
        return dynFields;
      })(),
    }));
    
    setState(p => ({ ...p, selectedEmployee: emp || { Id: record.F_EmployeeMaster, Name: record.EmployeeName } }));
    // Restore leaveValues & dynamicLeaveBalances from saved record
    if (record.LeaveDetailJson) {
      try {
        const arr = typeof record.LeaveDetailJson === "string" ? JSON.parse(record.LeaveDetailJson) : record.LeaveDetailJson;
        if (Array.isArray(arr) && arr.length > 0) {
          const restoredVals = {};
          const restoredBals = {};
          arr.forEach(item => {
            if (item.LeaveId) {
              restoredVals[item.LeaveId] = parseFloat(item.Taken) || 0;
              restoredBals[item.LeaveId] = {
                OpBal: parseFloat(item.OpBal) || 0,
                Entitled: parseFloat(item.Entitled) || 0,
                PastEnjoyed: parseFloat(item.PastEnjoyed) || 0,
                CurrentTaken: parseFloat(item.Taken) || 0,
                TotalEnjoyed: parseFloat(item.Enjoy) || 0,
                Bal: parseFloat(item.Bal) || 0
              };
            }
          });
          setLeaveValues(restoredVals);
          setDynamicLeaveBalances(restoredBals);
        }
      } catch (_) {}
    } else {
      const restored = {};
      const leaveKeys = Object.keys(leaveValues);
      if (leaveKeys[0] !== undefined) restored[leaveKeys[0]] = parseFloat(record.CL || 0);
      if (leaveKeys[1] !== undefined) restored[leaveKeys[1]] = parseFloat(record.ML || 0);
      setLeaveValues(prev => ({ ...prev, ...restored }));
      if (emp) {
        loadAndComputeEmployeeLeaves(emp, restored);
      }
    }
    setActiveTab('entry'); // Switch to entry tab
    setTimeout(() => {
      const wdInput = document.querySelector('input[name="WorkingDays"]');
      if (wdInput) {
        wdInput.focus();
        if (typeof wdInput.select === 'function') wdInput.select();
      }
    }, 120);
  };

  // Recalculate MonthDays based on selectedMonth/Year, Sunday Include, and Employee Weekly Holiday
  useEffect(() => {
    if (selectedRecordId !== null) return; // Do not overwrite saved record's MonthDays
    if (!selectedMonth || !selectedYear) return;

    const empWeeklyHoliday = state.selectedEmployee?.WeeklyHoliday || "";
    const is26Days = is26DaysForEmployee(payrollParams, state.selectedEmployee);
    const calculatedMonthDays = getCalculatedMonthDays(selectedMonth, selectedYear, payrollParams, empWeeklyHoliday, departmentList, state.selectedEmployee);
    setF(p => ({
      ...p,
      MonthDays: calculatedMonthDays,
      WorkingDays: is26Days ? 26 : p.WorkingDays,
      WeeklyHoliday: is26Days ? 0 : 0
    }));
  }, [selectedMonth, selectedYear, payrollParams, selectedRecordId, state.selectedEmployee, departmentList]);

  // Auto-recalculate whenever any value that affects gross/allowances/PF/ESI changes
  useEffect(() => {
    setF(p => {
      // Get effective salary entry for selected month/year
      const salList2 = (() => { try { return JSON.parse(state.selectedEmployee?.SalaryJson || "[]"); } catch { return []; } })();
      const salEntry = getEffectiveSalary(salList2, selectedMonth, selectedYear, state.selectedEmployee);
      const isDailyWage = isDailyWageEmployee(state.selectedEmployee, salEntry) || p.F_BasicTypeMaster === 2 || String(p.BasicTypeName || "").toLowerCase().includes("day");
      const isNoWH = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages);
      const is26Days = is26DaysForEmployee(payrollParams, state.selectedEmployee);
      const effectiveWH = (is26Days || (isNoWH && isDailyWage)) ? 0 : (parseFloat(p.WeeklyHoliday) || 0);

      const workingDays = computeWorkingDays(p.MonthDays, payrollParams, state.selectedEmployee);
      const totalLeaveAuto = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDays  = Math.min(p.MonthDays, p.WorkingDays + p.LeaveWages + p.FestivalHoliday + effectiveWH + totalLeaveAuto);
      const workingHoursDisplay = getWorkingHoursDisplay(p.WorkingHours, p.WorkingMinutes, payrollParams, salEntry?.F_BasicTypeMaster || state.selectedEmployee?.F_BasicTypeMaster || state.selectedEmployee?.BasicType || 1);
      // monthlyBasic = monthly equivalent for % allowance base (daily 500 → 500*26=13000)
      const monthlyBasic = computeBasic(salEntry, workingDays, payrollParams, state.selectedEmployee);
      // wages = actual earned wages (honors direct wages entry if parameter or department enables it and user entered value)
      const isDirectWages = isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList);
      const wages = (isDirectWages && parseFloat(p.Wages) > 0)
        ? parseFloat(p.Wages)
        : computeWages(salEntry, paidDays, p.MonthDays, p.WorkingHours, p.WorkingMinutes, payrollParams, state.selectedEmployee);

      const allowanceBaseSalary = (payrollParams?.IsAllowanceOnArrear && parseFloat(p.Arrear) > 0)
        ? (monthlyBasic + (parseFloat(p.Arrear) || 0))
        : monthlyBasic;

      const totalAl   = computeAllowancesTotal(rawAllowances, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const totalDed  = computeDeductionsTotal(rawDeductions, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const pfAlAmt   = computePFAllowancesAmount(rawAllowances, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const esiAlAmt  = computeESIAllowancesAmount(rawAllowances, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const bonus = computeBonus(wages, bonusRate, p.WorkingDays, paidDays, payrollParams);
      const gratuity = computeGratuity(monthlyBasic, gratuityRate, 1);
      const otAmount = computeOvertime(salEntry, p.OvertimeHours, p.MonthDays, payrollParams, rawAllowances, selectedMonth, selectedYear, allowanceBaseSalary);
      
      // ── PF Base: wages + each component if its flag is ON + allowances (via pfAlAmt inside computePF)
      const pfBase = wages
        + (payrollParams?.IsPFOnOT      ? otAmount  : 0)
        + (payrollParams?.IsPFOnExtraPay  ? (parseFloat(p.ExtraPay) || 0)  : 0)
        + (payrollParams?.IsPFOnExtraPay1 ? (parseFloat(p.ExtraPay1) || 0) : 0);
      // ── ESI Base: wages + each component if its flag is ON + allowances (esiAlAmt)
      const esiBase = wages
        + esiAlAmt
        + (payrollParams?.IsESIOnOT      ? otAmount  : 0)
        + (payrollParams?.IsESIOnArrear  ? (parseFloat(p.Arrear) || 0)  : 0)
        + (payrollParams?.IsESIOnExtraPay  ? (parseFloat(p.ExtraPay) || 0)  : 0)
        + (payrollParams?.IsESIOnExtraPay1 ? (parseFloat(p.ExtraPay1) || 0) : 0);
      const gross = wages + (parseFloat(p.ExtraPay) || 0) + (parseFloat(p.ExtraPay1) || 0) + (parseFloat(p.AdvanceBonus) || 0) + (parseFloat(p.Arrear) || 0) + otAmount + totalAl + bonus;
      // Apply PF only if employee PFApplicable; pfAlAmt (allowances with IsPFApply) added inside computePF
      const { PF, EmplrPF, Pension, AdminCharges, AC21, AC22 } = p.PFApplicable
        ? computePF(pfBase, pfRate, pfAlAmt, p.PensionApplicable, payrollParams, {
            emp: state.selectedEmployee || p,
            paidDays,
            monthDays: p.MonthDays,
            totalAllowances: totalAl,
            gross,
            selectedMonth,
            selectedYear,
            isEeOverLimit: p.EmpleeDedOverLimit,
            isErOverLimit: p.EmplrPFOverLimit,
            isPenOverLimit: p.PensionOverLimit,
            isHigherPen: p.HigherPension,
          })
        : { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };
      // Apply ESI only if employee ESIApplicable
      const { ESI, EmplrESI } = p.ESIApplicable
        ? computeESI(esiBase, esiRate, payrollParams, {
            emp: state.selectedEmployee || p,
            paidDays,
            monthDays: p.MonthDays,
            otAmount,
            totalGross: gross,
            selectedMonth,
            selectedYear,
            isESIApplicable: p.ESIApplicable,
          })
        : { ESI: 0, EmplrESI: 0 };

      // Deductions: PF + ESI + Advance + Recovery + TDS + ArrearPF + Other Deductions
      const advanceVal = parseFloat(p.Advance) || 0;
      const arrearPFVal = parseFloat(p.ArrearPF) || 0;
      const deduct = PF + ESI + advanceVal + (parseFloat(p.Recovery) || 0) + (parseFloat(p.TDS) || 0) + arrearPFVal + totalDed;
      
      let net = gross - deduct + (parseFloat(p.PFRefund) || 0);
      if (payrollParams?.IsNetPayRounded) {
        net = Math.round(net);
      } else {
        net = Math.round(net * 100) / 100;
      }

      return { 
        ...p, 
        WeeklyHoliday: effectiveWH,
        Wages: wages,
        OverTime: otAmount,
        PaidDay: paidDays,
        WorkingHoursDisplay: workingHoursDisplay,
        TotalAllowances: totalAl,
        TotalDeductions: totalDed,
        GrossAmount: Math.round(gross * 100) / 100, 
        PF, EmplrPF, Pension, ESI, EmplrESI, 
        AdminCharges, AC21, AC22,
        Bonus: bonus, Gratuity: gratuity,
        TotalDeduction: Math.round(deduct * 100) / 100, 
        NetAmount: net 
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rawAllowances, rawDeductions, pfRate, esiRate, bonusRate, gratuityRate, payrollParams, state.selectedEmployee,
    selectedMonth, selectedYear,
    f.BasicSalary, f.MonthDays,
    f.WorkingDays, f.WorkingHours, f.WorkingMinutes, f.LeaveWages, f.FestivalHoliday, f.WeeklyHoliday,
    f.ExtraPay, f.ExtraPay1, f.AdvanceBonus, f.Arrear, f.OvertimeHours,
    f.Advance, f.Recovery, f.TDS, f.ArrearPF, f.ArrERPF, f.ArrPens, f.PFRefund,
    f.PFApplicable, f.ESIApplicable, f.PensionApplicable,
    f.EmplrPFOverLimit, f.EmpleeDedOverLimit, f.PensionOverLimit, f.HigherPension,
    leaveValues,
  ]);

  const calculate = () => {
    setF(p => {
      // Resolve effective salary entry for selected month/year
      const calSalList = (() => { try { return JSON.parse(state.selectedEmployee?.SalaryJson || "[]"); } catch { return []; } })();
      const calSalEntry = getEffectiveSalary(calSalList, selectedMonth, selectedYear, state.selectedEmployee);
      const isDailyWage = isDailyWageEmployee(state.selectedEmployee, calSalEntry) || p.F_BasicTypeMaster === 2 || String(p.BasicTypeName || "").toLowerCase().includes("day");
      const isNoWH = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages);
      const effectiveWH = (isNoWH && isDailyWage) ? 0 : (parseFloat(p.WeeklyHoliday) || 0);

      const workingDays = computeWorkingDays(p.MonthDays, payrollParams, state.selectedEmployee);
      const totalLeave = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDays  = Math.min(p.MonthDays, p.WorkingDays + p.LeaveWages + p.FestivalHoliday + effectiveWH + totalLeave);
      const workingHoursDisplay = getWorkingHoursDisplay(p.WorkingHours, p.WorkingMinutes, payrollParams, calSalEntry?.F_BasicTypeMaster || state.selectedEmployee?.F_BasicTypeMaster || state.selectedEmployee?.BasicType || 1);
      // monthlyBasic = monthly equivalent for % allowance base (daily 500 → 500*26=13000)
      const monthlyBasic = computeBasic(calSalEntry, workingDays, payrollParams, state.selectedEmployee);
      // wages = actual earned wages
      const isDirectWages = isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList);
      const calWages = (isDirectWages && parseFloat(p.Wages) > 0)
        ? parseFloat(p.Wages)
        : computeWages(calSalEntry, paidDays, p.MonthDays, p.WorkingHours, p.WorkingMinutes, payrollParams, state.selectedEmployee);

      const allowanceBaseSalary = (payrollParams?.IsAllowanceOnArrear && parseFloat(p.Arrear) > 0)
        ? (monthlyBasic + (parseFloat(p.Arrear) || 0))
        : monthlyBasic;

      const totalAl   = computeAllowancesTotal(rawAllowances, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const totalDed  = computeDeductionsTotal(rawDeductions, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const pfAlAmt   = computePFAllowancesAmount(rawAllowances, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const esiAlAmt  = computeESIAllowancesAmount(rawAllowances, p.MonthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
      const bonus = computeBonus(calWages, bonusRate, p.WorkingDays, paidDays, payrollParams);
      const gratuity = computeGratuity(monthlyBasic, gratuityRate, 1);
      const otAmount = computeOvertime(calSalEntry, p.OvertimeHours, p.MonthDays, payrollParams, rawAllowances, selectedMonth, selectedYear, allowanceBaseSalary);
      // ── PF Base: earned wages + each component if flag ON (allowances added inside computePF)
      const pfBase = calWages
        + (payrollParams?.IsPFOnOT       ? otAmount   : 0)
        + (payrollParams?.IsPFOnExtraPay  ? (parseFloat(p.ExtraPay) || 0)  : 0)
        + (payrollParams?.IsPFOnExtraPay1 ? (parseFloat(p.ExtraPay1) || 0) : 0);
      // ── ESI Base: earned wages + each component if flag ON + allowances
      const esiBase = calWages
        + esiAlAmt
        + (payrollParams?.IsESIOnOT       ? otAmount   : 0)
        + (payrollParams?.IsESIOnArrear   ? (parseFloat(p.Arrear) || 0)   : 0)
        + (payrollParams?.IsESIOnExtraPay  ? (parseFloat(p.ExtraPay) || 0)  : 0)
        + (payrollParams?.IsESIOnExtraPay1 ? (parseFloat(p.ExtraPay1) || 0) : 0);
      // Gross = earned wages + all additions (without leaveWages double counting)
      const gross = calWages + (parseFloat(p.ExtraPay) || 0) + (parseFloat(p.ExtraPay1) || 0) + (parseFloat(p.AdvanceBonus) || 0) + (parseFloat(p.Arrear) || 0) + otAmount + totalAl + bonus;
      // Apply PF only if employee PFApplicable; pfAlAmt (allowances with IsPFApply) added inside computePF
      const { PF, EmplrPF, Pension, AdminCharges, AC21, AC22 } = p.PFApplicable
        ? computePF(pfBase, pfRate, pfAlAmt, p.PensionApplicable, payrollParams, {
            emp: state.selectedEmployee || p,
            paidDays,
            monthDays: p.MonthDays,
            totalAllowances: totalAl,
            gross,
            selectedMonth,
            selectedYear,
            isEeOverLimit: p.EmpleeDedOverLimit,
            isErOverLimit: p.EmplrPFOverLimit,
            isPenOverLimit: p.PensionOverLimit,
            isHigherPen: p.HigherPension,
          })
        : { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };
      // Apply ESI only if employee ESIApplicable
      const { ESI, EmplrESI } = p.ESIApplicable
        ? computeESI(esiBase, esiRate, payrollParams, {
            emp: state.selectedEmployee || p,
            paidDays,
            monthDays: p.MonthDays,
            otAmount,
            totalGross: gross,
            selectedMonth,
            selectedYear,
            isESIApplicable: p.ESIApplicable,
          })
        : { ESI: 0, EmplrESI: 0 };

      // ── Arrear PF, ArrERPF & ArrPens ──
      let finalArrearPF = parseFloat(p.ArrearPF) || 0;
      let finalArrERPF  = parseFloat(p.ArrERPF) || 0;
      let finalArrPens  = parseFloat(p.ArrPens) || 0;

      if (p.PFApplicable && parseFloat(p.Arrear) > 0) {
        if (!finalArrearPF && !finalArrERPF && !finalArrPens) {
          const arrResult = computeArrearPF(p.Arrear, Pension, pfRate, p.PensionApplicable, payrollParams, {
            emp: state.selectedEmployee || p,
            selectedMonth,
            selectedYear,
            isHigherPen: p.HigherPension || p.PensionOverLimit,
          });
          finalArrearPF = arrResult.ArrearPF;
          finalArrPens  = arrResult.ArrPens;
          finalArrERPF  = arrResult.ArrERPF;
        }
      } else if (!p.PFApplicable || parseFloat(p.Arrear) === 0) {
        finalArrearPF = 0;
        finalArrERPF  = 0;
        finalArrPens  = 0;
      }

      // Deductions: PF + ESI + Advance + Recovery + TDS + ArrearPF + Other Deductions
      const advanceVal = parseFloat(p.Advance) || 0;
      const deduct = PF + ESI + advanceVal + (parseFloat(p.Recovery) || 0) + (parseFloat(p.TDS) || 0) + finalArrearPF + totalDed;

      let net = gross - deduct + (parseFloat(p.PFRefund) || 0);
      if (payrollParams?.IsNetPayRounded) {
        net = Math.round(net);
      } else {
        net = Math.round(net * 100) / 100;
      }

      return { 
        ...p, 
        WeeklyHoliday: effectiveWH,
        Wages: calWages,
        PaidDay: paidDays,
        WorkingHoursDisplay: workingHoursDisplay,
        TotalAllowances: totalAl,
        TotalDeductions: totalDed,
        OverTime: otAmount,
        GrossAmount: Math.round(gross * 100) / 100, 
        PF, EmplrPF, Pension, ESI, EmplrESI, 
        ArrearPF: finalArrearPF,
        ArrERPF: finalArrERPF,
        ArrPens: finalArrPens,
        AdminCharges, AC21, AC22,
        Bonus: bonus, Gratuity: gratuity,
        TotalDeduction: Math.round(deduct * 100) / 100, 
        NetAmount: net 
      };
    });
  };

  // Helper function to format date as YYYY-MM-DD (SQL Server date format, date only no time)
  const formatDateForSave = (dateValue) => {
    if (!dateValue) return "";
    const str = String(dateValue).trim();
    if (str.startsWith("1899") || str.startsWith("1900")) return "";
    // If it's already a string in YYYY-MM-DD format, validate year > 1950
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const yr = parseInt(str.split("-")[0], 10);
      return yr > 1950 ? str : "";
    }
    // If it's a date object or ISO string, extract just the date part
    const dateObj = new Date(dateValue);
    if (isNaN(dateObj.getTime()) || dateObj.getFullYear() <= 1950) return "";
    return dateObj.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  const handleSave = async () => {
    const activeFirmId = sessionFirm?.Id || firmId || 0;
    if (!activeFirmId) { toast.error("Please select a firm first"); return; }
    const isLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
    if (selectedRecordId !== null && isLocked) {
      toast.warn("Payroll transactions are locked in Payroll Parameters. Saved records cannot be modified.");
      return;
    }

    // Safety check: ensure employee belongs to currently loaded employees for active firm
    const empExists = state.employees.some(e => String(e.Id) === String(f.F_EmployeeMaster));
    if (!empExists) {
      toast.error("Selected employee does not belong to the active firm! Please re-select employee.");
      return;
    }

    // Check if employee already has a saved record for this month/year (unless editing)
    if (selectedRecordId === null) {
      const exists = savedRecords.some(r => String(r.F_EmployeeMaster) === String(f.F_EmployeeMaster));
      if (exists) {
        toast.error("Payroll record for this employee already exists in the selected month!");
        return;
      }
    }

    if (payrollParams?.IsMaxLeaveValidate || payrollParamsRef.current?.IsMaxLeaveValidate) {
      for (const l of leaveMasterList) {
        const taken = parseFloat(leaveValues[l.Id]) || 0;
        if (taken > 0) {
          const b = dynamicLeaveBalances[l.Id] || {};
          const opBal = parseFloat(b.OpBal) || 0;
          const entitled = b.Entitled !== undefined ? parseFloat(b.Entitled) : (parseFloat(l.MaxLimit) || 0);
          const pastEnjoyed = parseFloat(b.PastEnjoyed) || 0;
          const maxAvailable = Math.max(0, Math.round(((opBal + entitled) - pastEnjoyed) * 100) / 100);
          if (taken > maxAvailable) {
            toast.error(`Leave '${l.Name}' taken (${taken} days) exceeds available balance (${maxAvailable} days). Max Leave validation is active.`);
            return;
          }
        }
      }
    }

    // ── Validation: Total Days cannot exceed Month Days ──
    const monthDaysVal = parseFloat(f.MonthDays) || 0;
    const totalInputDays = (parseFloat(f.WorkingDays) || 0)
      + (parseFloat(f.LeaveWages) || 0)
      + (parseFloat(f.FestivalHoliday) || 0)
      + (parseFloat(f.WeeklyHoliday) || 0)
      + (parseFloat(f.LeaveWithWages) > 0 && !parseFloat(f.LeaveWages) ? parseFloat(f.LeaveWithWages) : 0)
      + Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);

    if (monthDaysVal > 0 && totalInputDays > monthDaysVal) {
      toast.error(`Cannot save: Total days (${Math.round(totalInputDays * 100) / 100}) cannot exceed Month Days (${monthDaysVal})! Please adjust Working Days or Leaves.`);
      return;
    }

    setState(p => ({ ...p, isLoading: true }));
    try {
      const fd = new FormData();
      fd.append("Id", selectedRecordId || 0);
      // Append each field separately (exclude display-only fields)
      fd.append("F_FirmMaster", activeFirmId);
      fd.append("PayrollYear", selectedYear || 0);
      fd.append("PayrollMonth", selectedMonth || "");
      fd.append("F_EmployeeMaster", f.F_EmployeeMaster || 0);
      fd.append("BasicSalary", f.BasicSalary || 0);
      fd.append("HRA", f.HRA || 0);
      fd.append("Conveyance", f.Conveyance || 0);
      fd.append("CCA", f.CCA || 0);
      fd.append("Medical", f.Medical || 0);
      fd.append("Retention", f.Retention || 0);
      fd.append("MonthDays", f.MonthDays || 31);
      fd.append("WorkingDays", f.WorkingDays || 0);
      fd.append("WorkingHours", f.WorkingHours || 0);
      fd.append("WorkingMinutes", f.WorkingMinutes || 0);
      fd.append("LeaveWages", f.LeaveWages || 0);
      fd.append("FestivalHoliday", f.FestivalHoliday || 0);
      fd.append("WeeklyHoliday", f.WeeklyHoliday || 0);
      // Dynamic leaves JSON serialization
      const leaveDetailArr = leaveMasterList.map(l => {
        const b = dynamicLeaveBalances[l.Id] || {};
        const taken = parseFloat(leaveValues[l.Id]) || 0;
        return {
          LeaveId: l.Id,
          LeaveName: l.Name,
          Taken: taken,
          Bal: b.Bal !== undefined ? parseFloat(b.Bal) : 0,
          Enjoy: b.TotalEnjoyed !== undefined ? parseFloat(b.TotalEnjoyed) : taken,
          OpBal: b.OpBal !== undefined ? parseFloat(b.OpBal) : 0,
          Entitled: b.Entitled !== undefined ? parseFloat(b.Entitled) : (parseFloat(l.MaxLimit) || 0),
          PastEnjoyed: b.PastEnjoyed !== undefined ? parseFloat(b.PastEnjoyed) : 0
        };
      });
      fd.append("LeaveDetailJson", JSON.stringify(leaveDetailArr));

      // Map dynamic leaves to legacy CL, ML, PL columns
      const plLeave = leaveMasterList.find(l => String(l.Name).toUpperCase().includes("PL") || String(l.Name).toUpperCase().includes("PRIV") || String(l.Name).toUpperCase().includes("EARN")) || leaveMasterList[0];
      const clLeave = leaveMasterList.find(l => String(l.Name).toUpperCase().includes("CL") || String(l.Name).toUpperCase().includes("CASUAL")) || leaveMasterList[1] || leaveMasterList[0];
      const mlLeave = leaveMasterList.find(l => String(l.Name).toUpperCase().includes("ML") || String(l.Name).toUpperCase().includes("MED") || String(l.Name).toUpperCase().includes("SICK")) || leaveMasterList[2] || leaveMasterList[1];

      fd.append("CL", clLeave ? (parseFloat(leaveValues[clLeave.Id]) || 0) : 0);
      fd.append("ML", mlLeave ? (parseFloat(leaveValues[mlLeave.Id]) || 0) : 0);
      fd.append("LeaveWithWages", plLeave ? (parseFloat(leaveValues[plLeave.Id]) || 0) : (f.LeaveWithWages || 0));

      fd.append("OvertimeHours", f.OvertimeHours || 0);
      fd.append("Arrear", f.Arrear || 0);
      fd.append("AdvanceBonus", f.AdvanceBonus || 0);
      fd.append("ExtraPay", f.ExtraPay || 0);
      fd.append("ExtraPay1", f.ExtraPay1 || 0);
      fd.append("PFRefund", f.PFRefund || 0);
      fd.append("Recovery", f.Recovery || 0);
      fd.append("Advance", f.Advance || 0);
      fd.append("TDS", f.TDS || 0);
      fd.append("PFReason", f.PFReason || "");
      if (f.PFLast) fd.append("PFLast", formatDateForSave(f.PFLast));
      if (f.ESIReason) fd.append("ESIReason", f.ESIReason);
      if (f.ESILast) {
        fd.append("ESILast", formatDateForSave(f.ESILast));
        fd.append("LastWorking", formatDateForSave(f.ESILast));
      }
      fd.append("PL_Bal", plLeave ? (dynamicLeaveBalances[plLeave.Id]?.Bal ?? f.PL_Bal ?? 0) : (f.PL_Bal || 0));
      fd.append("CL_Bal", clLeave ? (dynamicLeaveBalances[clLeave.Id]?.Bal ?? f.CL_Bal ?? 0) : (f.CL_Bal || 0));
      fd.append("ML_Bal", mlLeave ? (dynamicLeaveBalances[mlLeave.Id]?.Bal ?? f.ML_Bal ?? 0) : (f.ML_Bal || 0));
      fd.append("PL_Enjoy", plLeave ? (dynamicLeaveBalances[plLeave.Id]?.TotalEnjoyed ?? f.PL_Enjoy ?? 0) : (f.PL_Enjoy || 0));
      fd.append("CL_Enjoy", clLeave ? (dynamicLeaveBalances[clLeave.Id]?.TotalEnjoyed ?? f.CL_Enjoy ?? 0) : (f.CL_Enjoy || 0));
      fd.append("ML_Enjoy", mlLeave ? (dynamicLeaveBalances[mlLeave.Id]?.TotalEnjoyed ?? f.ML_Enjoy ?? 0) : (f.ML_Enjoy || 0));
      fd.append("Wages", f.Wages || 0);
      fd.append("OverTime", f.OverTime || 0);
      fd.append("EmplrPF", f.EmplrPF || 0);
      fd.append("Pension", f.Pension || 0);
      fd.append("PF", f.PF || 0);
      fd.append("ArrearPF", f.ArrearPF || 0);
      fd.append("ArrERPF", f.ArrERPF || 0);
      fd.append("ArrPens", f.ArrPens || 0);
      fd.append("EmplrESI", f.EmplrESI || 0);
      fd.append("ESI", f.ESI || 0);
      fd.append("Bonus", f.Bonus || 0);
      fd.append("Gratuity", f.Gratuity || 0);
      fd.append("AdminCharges", f.AdminCharges || 0);
      fd.append("AC21", f.AC21 || 0);
      fd.append("AC22", f.AC22 || 0);
      fd.append("PFApplicable", f.PFApplicable ? true : false);
      fd.append("ESIApplicable", f.ESIApplicable ? true : false);
      fd.append("PensionApplicable", f.PensionApplicable ? true : false);
      fd.append("EmpleeDedOverLimit", f.EmpleeDedOverLimit ? true : false);
      fd.append("EmplrPFOverLimit", f.EmplrPFOverLimit ? true : false);
      fd.append("PensionOverLimit", f.PensionOverLimit ? true : false);
      fd.append("HigherPension", f.HigherPension ? true : false);
      fd.append("PaidDay", f.PaidDay || 0);
      fd.append("TotalAllowances", f.TotalAllowances || 0);
      fd.append("TotalDeductions", f.TotalDeductions || 0);
      fd.append("GrossAmount", f.GrossAmount || 0);
      fd.append("TotalDeduction", f.TotalDeduction || 0);
      fd.append("NetAmount", f.NetAmount || 0);
      fd.append("LeaveWithWages", f.LeaveWithWages || 0);
      fd.append("IsBasicEditable", payrollParams?.IsBasicEditable ? true : false);
      // Only append date fields if they have values
      if (f.LeaveFrom1) fd.append("LeaveFrom1", formatDateForSave(f.LeaveFrom1));
      if (f.LeaveTo1) fd.append("LeaveTo1", formatDateForSave(f.LeaveTo1));
      if (f.LeaveFrom2) fd.append("LeaveFrom2", formatDateForSave(f.LeaveFrom2));
      if (f.LeaveTo2) fd.append("LeaveTo2", formatDateForSave(f.LeaveTo2));
      if (f.LeaveFrom3) fd.append("LeaveFrom3", formatDateForSave(f.LeaveFrom3));
      if (f.LeaveTo3) fd.append("LeaveTo3", formatDateForSave(f.LeaveTo3));
      fd.append("AbsentDays", f.AbsentDays || 0);
      // Only append absent date fields if they have values
      if (f.AbsentFrom1) fd.append("AbsentFrom1", formatDateForSave(f.AbsentFrom1));
      if (f.AbsentTo1) fd.append("AbsentTo1", formatDateForSave(f.AbsentTo1));
      if (f.AbsentFrom2) fd.append("AbsentFrom2", formatDateForSave(f.AbsentFrom2));
      if (f.AbsentTo2) fd.append("AbsentTo2", formatDateForSave(f.AbsentTo2));
      if (f.AbsentFrom3) fd.append("AbsentFrom3", formatDateForSave(f.AbsentFrom3));
      if (f.AbsentTo3) fd.append("AbsentTo3", formatDateForSave(f.AbsentTo3));

      // --- Add Original and Calculated values for Basic and Allowances ---
      const totalLeavedays = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
      const paidDaysForCalc = Math.min(
        parseFloat(f.MonthDays) || 0,
        (parseFloat(f.WorkingDays) || 0) +
        (parseFloat(f.LeaveWages) || 0) +
        (parseFloat(f.FestivalHoliday) || 0) +
        (parseFloat(f.WeeklyHoliday) || 0) +
        totalLeavedays
      );

      fd.append("Original_BasicSalary", f.BasicSalary || 0);
      fd.append("Calculated_BasicSalary", f.Wages || 0);

      const effectiveAllowances = getEffectiveAllowances(rawAllowances, selectedMonth, selectedYear);
      for (let i = 0; i < 10; i++) {
        const a = effectiveAllowances[i];
        if (a) {
          const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow_${a.F_AllowanceMaster}`;
          const computed = computeSingleAllowance(a, f.MonthDays, paidDaysForCalc, f.BasicSalary);
          const original = parseFloat(a.Amount) || 0;
          fd.append(`Allowance${i+1}Name`, name);
          fd.append(`OriginalAllowance${i+1}`, original);
          fd.append(`CalculatedAllowance${i+1}`, computed);
        } else {
          fd.append(`Allowance${i+1}Name`, "");
          fd.append(`OriginalAllowance${i+1}`, 0);
          fd.append(`CalculatedAllowance${i+1}`, 0);
        }
      }

      const effectiveDeductions = getEffectiveDeductions(rawDeductions, selectedMonth, selectedYear);
      for (let i = 0; i < 10; i++) {
        const d = effectiveDeductions[i];
        if (d) {
          const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct_${d.F_DeductionMaster}`;
          const computed = computeSingleDeduction(d, f.MonthDays, paidDaysForCalc, f.BasicSalary);
          const original = parseFloat(d.Amount) || 0;
          fd.append(`Deduction${i+1}Name`, name);
          fd.append(`OriginalDeduction${i+1}`, original);
          fd.append(`CalculatedDeduction${i+1}`, computed);
        } else {
          fd.append(`Deduction${i+1}Name`, "");
          fd.append(`OriginalDeduction${i+1}`, 0);
          fd.append(`CalculatedDeduction${i+1}`, 0);
        }
      }
      // ------------------------------------------------------------------

      const savedEmpId = f.F_EmployeeMaster;
      const isNewEntry = selectedRecordId === null;
      const prevDays = {
        WorkingDays: f.WorkingDays,
        WorkingHours: f.WorkingHours,
        WorkingMinutes: f.WorkingMinutes,
        LeaveWages: f.LeaveWages,
        FestivalHoliday: f.FestivalHoliday,
        WeeklyHoliday: f.WeeklyHoliday,
        OvertimeHours: f.OvertimeHours,
        ExtraPay: f.ExtraPay,
        ExtraPay1: f.ExtraPay1,
        leaveValues: { ...leaveValues }
      };

      lastDaysRef.current = prevDays;

      fd.append("UserId", 0);
      const res = await Fn_AddEditData(dispatch, () => {}, { arguList: { id: selectedRecordId || 0, formData: fd } }, 'PayrollTransaction/0/token', true, "memberid", navigate, "#");
      if (res && res?.id > 0) { 
        toast.success(isNewEntry ? "Payroll saved!" : "Payroll updated!", { autoClose: 3000 });
        
        // Invalidate caches and refresh employees in background
        cachedEmployeeData.delete(activeFirmId);
        cachedLeaveHistory.clear();
        loadEmployees(activeFirmId);

        const refreshedRecords = await logPayrollSession();
        const isAutoPayrollOn = !!(payrollParams?.IsAutoPayroll || payrollParams?.CreatePayrollAutomatically);
        const isCopyDaysOn = !!(payrollParams?.IsCopyDaysNextEmployee || payrollParams?.CopyDaysToNextEmployee);

        if (isAutoPayrollOn && isNewEntry) {
          const sortedActive = sortEmployeesByParams(state.employees, payrollParams)
            .filter(e => !isNotInUse(e))
            .filter(e => !isMultiESI || !filterEsiNo || (e.ESINo || "").trim() === filterEsiNo)
            .filter(e => !isMultiPF  || !filterPfNo  || (e.PFNo  || "").trim() === filterPfNo);

          const currentSavedList = (refreshedRecords && Array.isArray(refreshedRecords) && refreshedRecords.length > 0)
            ? refreshedRecords
            : [...savedRecords, { F_EmployeeMaster: savedEmpId }];

          const pending = sortedActive.filter(e => !currentSavedList.some(r => String(r.F_EmployeeMaster) === String(e.Id)));

          if (pending.length > 0) {
            const currentIdx = sortedActive.findIndex(e => String(e.Id) === String(savedEmpId));
            let nextEmp = null;
            if (currentIdx >= 0) {
              nextEmp = sortedActive.slice(currentIdx + 1).find(e => pending.some(p => String(p.Id) === String(e.Id)));
            }
            if (!nextEmp) {
              nextEmp = pending[0];
            }

            if (nextEmp) {
              const daysToCopy = isCopyDaysOn ? prevDays : null;
              await handleEmployeeChange(nextEmp.Id, daysToCopy, state.employees, currentSavedList);
              toast.info(`Next employee auto-selected: ${nextEmp.Name}`, { autoClose: 2500 });
              setActiveTab('entry');
              setTimeout(() => {
                const wdInput = document.querySelector('input[name="WorkingDays"]');
                if (wdInput) {
                  wdInput.focus();
                  if (typeof wdInput.select === 'function') wdInput.select();
                }
              }, 100);
              return;
            }
          } else {
            resetForm();
            toast.success("All employees' payroll for this month has been created!", { autoClose: 3500 });
            return;
          }
        }

        resetForm(); // Clear form after save to prepare for next entry
      }
      else { toast.error("Save failed."); }
    } catch { toast.error("Error saving payroll."); }
    finally { setState(p => ({ ...p, isLoading: false })); }
  };

  // ─── Bulk Update & Recalculate All Saved Records ───
  const handleRecalculateAll = async () => {
    const isLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
    if (isLocked) {
      toast.warn("Payroll transactions are locked in Payroll Parameters. Recalculating or modifying saved records is not allowed.");
      return;
    }
    const activeFirmId = sessionFirm?.Id || firmId || 0;
    if (!activeFirmId || !selectedMonth || !selectedYear || savedRecords.length === 0) {
      toast.error("No saved records to recalculate.");
      return;
    }

    setIsRecalculatingAll(true);
    setRecalcProgress({ current: 0, total: savedRecords.length, empName: "" });

    try {
      // 1. Fetch fresh master datasets
      const [freshEmpList, freshParamsRes, freshPfRes, freshEsiRes, freshBonusRes, freshGratuityRes, freshAllowMaster, freshDeductMaster] = await Promise.all([
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EmployeeDataDynamic/0/${activeFirmId}`).catch(() => state.employees),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${activeFirmId}`).catch(() => []),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PfParamFetch/TBL.F_FirmMaster/${activeFirmId}`).catch(() => []),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/EsiParamFetch/TBL.F_FirmMaster/${activeFirmId}`).catch(() => []),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/BonusParamFetch/TBL.F_FirmMaster/${activeFirmId}`).catch(() => []),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/GratuityParamFetch/TBL.F_FirmMaster/${activeFirmId}`).catch(() => []),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/AllowanceMaster/Id/0`).catch(() => allowanceMasterList),
        Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/DeductionMaster/Id/0`).catch(() => deductionMasterList)
      ]);

      const freshPayrollParams = (freshParamsRes && Array.isArray(freshParamsRes) && freshParamsRes.length > 0) ? freshParamsRes[0] : payrollParams;
      const sortedPf = (freshPfRes || []).sort((a, b) => new Date(a.PFDate) - new Date(b.PFDate));
      const freshPfRate = sortedPf.length > 0 ? sortedPf[sortedPf.length - 1] : pfRate;
      const sortedEsi = (freshEsiRes || []).sort((a, b) => new Date(a.ESIDate) - new Date(b.ESIDate));
      const freshEsiRate = sortedEsi.length > 0 ? sortedEsi[sortedEsi.length - 1] : esiRate;
      const sortedBonus = (freshBonusRes || []).sort((a, b) => new Date(a.BonusDate) - new Date(b.BonusDate));
      const freshBonusRate = sortedBonus.length > 0 ? sortedBonus[sortedBonus.length - 1] : bonusRate;
      const sortedGratuity = (freshGratuityRes || []).sort((a, b) => new Date(a.GratuityDate) - new Date(b.GratuityDate));
      const freshGratuityRate = sortedGratuity.length > 0 ? sortedGratuity[sortedGratuity.length - 1] : gratuityRate;
      const freshAllowList = filterActiveMasters(freshAllowMaster || allowanceMasterList);
      const freshDeductList = filterActiveMasters(freshDeductMaster || deductionMasterList);
      const currentEmployees = freshEmpList || state.employees;

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < savedRecords.length; i++) {
        const record = savedRecords[i];
        const emp = currentEmployees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
        const empDisplayName = emp?.Name || record.EmployeeName || `Record #${i + 1}`;

        setRecalcProgress({
          current: i + 1,
          total: savedRecords.length,
          empName: empDisplayName
        });

        // Parse employee latest salary, allowances, deductions
        let al = [];
        let dl = [];
        let salList = [];
        if (emp) {
          try { al = emp.AllowanceJson ? JSON.parse(emp.AllowanceJson) : []; } catch {}
          try { dl = emp.DeductionJson ? JSON.parse(emp.DeductionJson) : []; } catch {}
          try { salList = emp.SalaryJson ? JSON.parse(emp.SalaryJson) : []; } catch {}
        }
        let effAl = getEffectiveAllowances(al, selectedMonth, selectedYear);
        let effDl = getEffectiveDeductions(dl, selectedMonth, selectedYear);

        if (effAl.length === 0) {
          effAl = reconstructAllowancesFromRecord(record, freshAllowList);
        }
        if (effDl.length === 0) {
          effDl = reconstructDeductionsFromRecord(record, freshDeductList);
        }

        const salEntry = getEffectiveSalary(salList, selectedMonth, selectedYear, emp);
        const monthDays = parseFloat(record.MonthDays) || getCalculatedMonthDays(selectedMonth, selectedYear, freshPayrollParams, emp?.WeeklyHoliday, departmentList, emp);
        const workingDays = parseFloat(record.WorkingDays) || 0;
        const workingHours = parseFloat(record.WorkingHours) || 0;
        const workingMinutes = parseFloat(record.WorkingMinutes) || 0;
        const leaveWages = parseFloat(record.LeaveWages) || 0;
        const festivalHoliday = parseFloat(record.FestivalHoliday) || 0;
        const isDailyWage = isDailyWageEmployee(record, salEntry) || isDailyWageEmployee(emp, salEntry) || (record.F_BasicTypeMaster === 2);
        const isNoWH = !!(freshPayrollParams?.IsNoWeeklyHolidayDailyWages || freshPayrollParams?.NoWeeklyHolidayForDailyWages);
        const weeklyHoliday = (isNoWH && isDailyWage) ? 0 : (parseFloat(record.WeeklyHoliday) || 0);
        const cl = parseFloat(record.CL) || 0;
        const ml = parseFloat(record.ML) || 0;
        const paidDays = Math.min(monthDays, workingDays + leaveWages + festivalHoliday + weeklyHoliday + cl + ml);

        const monthlyBasic = computeBasic(salEntry, workingDays, freshPayrollParams, emp);
        const wages = computeWages(salEntry, paidDays, monthDays, workingHours, workingMinutes, freshPayrollParams, emp);
        const arrear = parseFloat(record.Arrear) || 0;
        const allowanceBaseSalary = (freshPayrollParams?.IsAllowanceOnArrear && arrear > 0)
          ? (monthlyBasic + arrear)
          : monthlyBasic;

        const otHours = parseFloat(record.OvertimeHours) || 0;
        const totalAl = computeAllowancesTotal(effAl, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
        const totalDed = computeDeductionsTotal(effDl, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
        const otAmount = computeOvertime(salEntry, otHours, monthDays, freshPayrollParams, effAl, selectedMonth, selectedYear, allowanceBaseSalary);
        const pfAlAmt = computePFAllowancesAmount(effAl, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
        const esiAlAmt = computeESIAllowancesAmount(effAl, monthDays, paidDays, allowanceBaseSalary, selectedMonth, selectedYear);
        const bonus = computeBonus(wages, freshBonusRate, workingDays, paidDays, freshPayrollParams);
        const gratuity = computeGratuity(monthlyBasic, freshGratuityRate, 1);
        const advanceBonus = parseFloat(record.AdvanceBonus) || 0;
        const extraPay = parseFloat(record.ExtraPay) || 0;
        const extraPay1 = parseFloat(record.ExtraPay1) || 0;
        const pfRefund = parseFloat(record.PFRefund) || 0;
        const recovery = parseFloat(record.Recovery) || 0;
        const advance = parseFloat(record.Advance) || 0;
        const tds = parseFloat(record.TDS) || 0;
        const arrearPF = parseFloat(record.ArrearPF) || 0;

        const pfBase = wages
          + (freshPayrollParams?.IsPFOnOT ? otAmount : 0)
          + (freshPayrollParams?.IsPFOnExtraPay ? extraPay : 0)
          + (freshPayrollParams?.IsPFOnExtraPay1 ? extraPay1 : 0);

        const esiBase = wages
          + esiAlAmt
          + (freshPayrollParams?.IsESIOnOT ? otAmount : 0)
          + (freshPayrollParams?.IsESIOnArrear ? arrear : 0)
          + (freshPayrollParams?.IsESIOnExtraPay ? extraPay : 0)
          + (freshPayrollParams?.IsESIOnExtraPay1 ? extraPay1 : 0);

        const gross = wages + extraPay + extraPay1 + advanceBonus + arrear + otAmount + totalAl + bonus;

        const pfApplicable = emp ? (emp.PFApplicable || false) : (record.PFApplicable || false);
        const esiApplicable = emp ? (emp.ESIApplicable || false) : (record.ESIApplicable || false);
        const pensionApplicable = emp ? (emp.PensionApplicable !== undefined ? !!emp.PensionApplicable : true) : (record.PensionApplicable !== undefined ? !!record.PensionApplicable : true);

        const activeEmp = emp || record;
        const { PF, EmplrPF, Pension, AdminCharges, AC21, AC22 } = pfApplicable
          ? computePF(pfBase, freshPfRate, pfAlAmt, pensionApplicable, freshPayrollParams, {
              emp: activeEmp,
              paidDays,
              monthDays,
              totalAllowances: totalAl,
              gross,
              selectedMonth,
              selectedYear,
              isEeOverLimit: emp?.EmpleeDedOverLimit !== undefined ? !!emp.EmpleeDedOverLimit : !!record.EmpleeDedOverLimit,
              isErOverLimit: emp?.EmplrPFOverLimit !== undefined ? !!emp.EmplrPFOverLimit : !!record.EmplrPFOverLimit,
              isPenOverLimit: emp?.PensionOverLimit !== undefined ? !!emp.PensionOverLimit : !!record.PensionOverLimit,
              isHigherPen: emp?.HigherPension !== undefined ? !!emp.HigherPension : !!record.HigherPension,
            })
          : { PF: 0, EmplrPF: 0, Pension: 0, AdminCharges: 0, AC21: 0, AC22: 0 };

        // ── Arrear PF, Employer PF & Pension in Bulk Calculate ──
        let calculatedArrearPF = parseFloat(record.ArrearPF) || 0;
        let calculatedArrERPF  = parseFloat(record.ArrERPF) || 0;
        let calculatedArrPens  = parseFloat(record.ArrPens) || 0;

        if (pfApplicable && arrear > 0) {
          const arrResult = computeArrearPF(arrear, Pension, freshPfRate, pensionApplicable, freshPayrollParams, {
            emp: activeEmp,
            selectedMonth,
            selectedYear,
            isHigherPen: activeEmp?.HigherPension || activeEmp?.PensionOverLimit,
          });
          calculatedArrearPF = calculatedArrearPF > 0 ? calculatedArrearPF : arrResult.ArrearPF;
          calculatedArrPens  = (record.ArrPens !== undefined && record.ArrPens !== null && record.ArrPens !== "" && parseFloat(record.ArrPens) > 0)
            ? parseFloat(record.ArrPens)
            : arrResult.ArrPens;
          calculatedArrERPF  = (record.ArrERPF !== undefined && record.ArrERPF !== null && record.ArrERPF !== "" && parseFloat(record.ArrERPF) > 0)
            ? parseFloat(record.ArrERPF)
            : arrResult.ArrERPF;
        } else if (!pfApplicable || arrear === 0) {
          calculatedArrearPF = 0;
          calculatedArrERPF  = 0;
          calculatedArrPens  = 0;
        }

        const { ESI, EmplrESI } = esiApplicable
          ? computeESI(esiBase, freshEsiRate, freshPayrollParams, {
              emp: activeEmp || record,
              paidDays,
              monthDays,
              otAmount,
              totalGross: gross,
              selectedMonth,
              selectedYear,
              isESIApplicable: esiApplicable,
            })
          : { ESI: 0, EmplrESI: 0 };

        const deduct = PF + ESI + advance + recovery + tds + calculatedArrearPF + totalDed;
        let netAmount = gross - deduct + pfRefund;
        if (freshPayrollParams?.IsNetPayRounded) {
          netAmount = Math.round(netAmount);
        } else {
          netAmount = Math.round(netAmount * 100) / 100;
        }

        const fd = new FormData();
        fd.append("Id", record.Id);
        fd.append("F_FirmMaster", activeFirmId);
        fd.append("PayrollYear", selectedYear);
        fd.append("PayrollMonth", selectedMonth);
        fd.append("F_EmployeeMaster", record.F_EmployeeMaster);
        fd.append("BasicSalary", parseFloat(salEntry?.BasicSalary) || 0);
        fd.append("HRA", parseFloat(record.HRA) || 0);
        fd.append("Conveyance", parseFloat(record.Conveyance) || 0);
        fd.append("CCA", parseFloat(record.CCA) || 0);
        fd.append("Medical", parseFloat(record.Medical) || 0);
        fd.append("Retention", parseFloat(record.Retention) || 0);
        fd.append("MonthDays", monthDays);
        fd.append("WorkingDays", workingDays);
        fd.append("WorkingHours", workingHours);
        fd.append("WorkingMinutes", workingMinutes);
        fd.append("LeaveWages", leaveWages);
        fd.append("FestivalHoliday", festivalHoliday);
        fd.append("WeeklyHoliday", weeklyHoliday);
        fd.append("CL", cl);
        fd.append("ML", ml);
        fd.append("OvertimeHours", otHours);
        fd.append("Arrear", arrear);
        fd.append("AdvanceBonus", advanceBonus);
        fd.append("ExtraPay", extraPay);
        fd.append("ExtraPay1", extraPay1);
        fd.append("PFRefund", pfRefund);
        fd.append("Recovery", recovery);
        fd.append("Advance", advance);
        fd.append("TDS", tds);
        fd.append("PFReason", record.PFReason || "");
        if (record.PFLast) fd.append("PFLast", formatDateForSave(record.PFLast));
        fd.append("ESIReason", record.ESIReason || "");
        if (record.ESILast || record.LastWorking) {
          fd.append("ESILast", formatDateForSave(record.ESILast || record.LastWorking));
          fd.append("LastWorking", formatDateForSave(record.LastWorking || record.ESILast));
        }
        fd.append("PL_Bal", parseFloat(record.PL_Bal) || 0);
        fd.append("CL_Bal", parseFloat(record.CL_Bal) || 0);
        fd.append("ML_Bal", parseFloat(record.ML_Bal) || 0);
        fd.append("PL_Enjoy", parseFloat(record.PL_Enjoy) || 0);
        fd.append("CL_Enjoy", parseFloat(record.CL_Enjoy) || 0);
        fd.append("ML_Enjoy", parseFloat(record.ML_Enjoy) || 0);
        fd.append("Wages", wages);
        fd.append("OverTime", otAmount);
        fd.append("EmplrPF", EmplrPF);
        fd.append("Pension", Pension);
        fd.append("PF", PF);
        fd.append("ArrearPF", calculatedArrearPF);
        fd.append("ArrERPF", calculatedArrERPF);
        fd.append("ArrPens", calculatedArrPens);
        fd.append("EmplrESI", EmplrESI);
        fd.append("ESI", ESI);
        fd.append("Bonus", bonus);
        fd.append("Gratuity", gratuity);
        fd.append("AdminCharges", AdminCharges);
        fd.append("AC21", AC21);
        fd.append("AC22", AC22);
        fd.append("PFApplicable", pfApplicable);
        fd.append("ESIApplicable", esiApplicable);
        fd.append("PaidDay", paidDays);
        fd.append("TotalAllowances", totalAl);
        fd.append("TotalDeductions", totalDed);
        fd.append("GrossAmount", gross);
        fd.append("TotalDeduction", deduct);
        fd.append("NetAmount", netAmount);
        fd.append("LeaveWithWages", parseFloat(record.LeaveWithWages) || 0);
        fd.append("IsBasicEditable", freshPayrollParams?.IsBasicEditable ? true : false);
        if (record.LeaveFrom1) fd.append("LeaveFrom1", formatDateForSave(record.LeaveFrom1));
        if (record.LeaveTo1) fd.append("LeaveTo1", formatDateForSave(record.LeaveTo1));
        if (record.LeaveFrom2) fd.append("LeaveFrom2", formatDateForSave(record.LeaveFrom2));
        if (record.LeaveTo2) fd.append("LeaveTo2", formatDateForSave(record.LeaveTo2));
        if (record.LeaveFrom3) fd.append("LeaveFrom3", formatDateForSave(record.LeaveFrom3));
        if (record.LeaveTo3) fd.append("LeaveTo3", formatDateForSave(record.LeaveTo3));
        fd.append("AbsentDays", parseFloat(record.AbsentDays) || 0);
        if (record.AbsentFrom1) fd.append("AbsentFrom1", formatDateForSave(record.AbsentFrom1));
        if (record.AbsentTo1) fd.append("AbsentTo1", formatDateForSave(record.AbsentTo1));
        if (record.AbsentFrom2) fd.append("AbsentFrom2", formatDateForSave(record.AbsentFrom2));
        if (record.AbsentTo2) fd.append("AbsentTo2", formatDateForSave(record.AbsentTo2));
        if (record.AbsentFrom3) fd.append("AbsentFrom3", formatDateForSave(record.AbsentFrom3));
        if (record.AbsentTo3) fd.append("AbsentTo3", formatDateForSave(record.AbsentTo3));

        fd.append("Original_BasicSalary", parseFloat(salEntry?.BasicSalary) || 0);
        fd.append("Calculated_BasicSalary", wages);

        for (let j = 0; j < 10; j++) {
          const a = effAl[j];
          if (a) {
            const name = freshAllowList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow_${a.F_AllowanceMaster}`;
            const computed = computeSingleAllowance(a, monthDays, paidDays, parseFloat(salEntry?.BasicSalary) || 0);
            const original = parseFloat(a.Amount) || 0;
            fd.append(`Allowance${j + 1}Name`, name);
            fd.append(`OriginalAllowance${j + 1}`, original);
            fd.append(`CalculatedAllowance${j + 1}`, computed);
          } else {
            fd.append(`Allowance${j + 1}Name`, "");
            fd.append(`OriginalAllowance${j + 1}`, 0);
            fd.append(`CalculatedAllowance${j + 1}`, 0);
          }
        }

        for (let j = 0; j < 10; j++) {
          const d = effDl[j];
          if (d) {
            const name = freshDeductList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct_${d.F_DeductionMaster}`;
            const computed = computeSingleDeduction(d, monthDays, paidDays, parseFloat(salEntry?.BasicSalary) || 0);
            const original = parseFloat(d.Amount) || 0;
            fd.append(`Deduction${j + 1}Name`, name);
            fd.append(`OriginalDeduction${j + 1}`, original);
            fd.append(`CalculatedDeduction${j + 1}`, computed);
          } else {
            fd.append(`Deduction${j + 1}Name`, "");
            fd.append(`OriginalDeduction${j + 1}`, 0);
            fd.append(`CalculatedDeduction${j + 1}`, 0);
          }
        }

        fd.append("UserId", 0);
        const res = await Fn_AddEditData(dispatch, () => {}, { arguList: { id: record.Id, formData: fd } }, 'PayrollTransaction/0/token', true, "memberid", navigate, "#");
        if (res && res?.id > 0) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      // 3. Post-completion sync
      await logPayrollSession();
      await loadEmployees(activeFirmId);
      if (selectedRecordId) {
        const refreshedRecs = await logPayrollSession();
        const currentActiveRec = refreshedRecs.find(r => r.Id === selectedRecordId);
        if (currentActiveRec) {
          loadSavedRecord(currentActiveRec);
        }
      }

      if (errorCount === 0) {
        toast.success(`Successfully updated and recalculated all ${successCount} payroll records!`, { autoClose: 4000 });
      } else {
        toast.warning(`Recalculated ${successCount} records (${errorCount} errors).`, { autoClose: 4000 });
      }
    } catch (err) {
      console.error("Error during bulk recalculation:", err);
      toast.error("An error occurred during recalculation.");
    } finally {
      setIsRecalculatingAll(false);
      setShowRecalculateModal(false);
    }
  };

  // ─── Delete Confirmation Modal State ───
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    targetId: null,
    empName: "",
    empCode: "",
    month: "",
    year: "",
    netAmount: "0.00",
    grossAmount: "0.00",
    isClearingUnsaved: false,
  });

  // Open Delete Confirmation Modal ("Are you sure?")
  const handleDelete = (idToDelete = null) => {
    let targetId = idToDelete || selectedRecordId;

    // If targetId is not set, check if current employee has a saved record in database
    if (!targetId && f.F_EmployeeMaster) {
      const existing = savedRecords.find(r => String(r.F_EmployeeMaster) === String(f.F_EmployeeMaster));
      if (existing) {
        targetId = existing.Id;
      }
    }

    const isLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
    if (targetId && targetId > 0 && isLocked) {
      toast.warn("Payroll transactions are locked in Payroll Parameters. Saved records cannot be deleted.");
      return;
    }

    const rec = targetId ? savedRecords.find(r => r.Id === targetId) : null;
    const emp = rec
      ? state.employees.find(e => String(e.Id) === String(rec.F_EmployeeMaster))
      : (f.F_EmployeeMaster ? state.employees.find(e => String(e.Id) === String(f.F_EmployeeMaster)) : null);
    const empName = f.EmployeeName || emp?.Name || rec?.EmployeeName || "Selected Employee";
    const empCode = emp?.CardNo || emp?.EmployeeCode || rec?.CardNo || f.CardNo || "";

    // If no database transaction exists for this employee
    if (!targetId || targetId <= 0) {
      if (f.F_EmployeeMaster || f.EmployeeName) {
        setDeleteModal({
          show: true,
          targetId: null,
          empName,
          empCode,
          month: selectedMonth,
          year: selectedYear,
          netAmount: to2Dec(f.NetAmount),
          grossAmount: to2Dec(f.GrossAmount),
          isClearingUnsaved: true,
        });
        return;
      }
      toast.warning("Please select an employee or saved payroll record to delete.");
      return;
    }

    setDeleteModal({
      show: true,
      targetId,
      empName,
      empCode,
      month: selectedMonth,
      year: selectedYear,
      netAmount: to2Dec(rec?.NetAmount || f.NetAmount),
      grossAmount: to2Dec(rec?.GrossAmount || f.GrossAmount),
      isClearingUnsaved: false,
    });
  };

  // Execute the confirmed deletion
  const executeDelete = async () => {
    const isAutoPayrollOn = !!(payrollParams?.IsAutoPayroll || payrollParams?.CreatePayrollAutomatically);

    if (deleteModal.isClearingUnsaved) {
      setDeleteModal(prev => ({ ...prev, show: false }));
      userClearedRef.current = false;
      resetForm();
      if (!isAutoPayrollOn) {
        focusEmployeeDropdown(200);
      }
      toast.info("Payroll entry cleared.");
      return;
    }

    const targetId = deleteModal.targetId;
    const empName = deleteModal.empName;

    const isLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
    if (targetId && isLocked) {
      setDeleteModal(prev => ({ ...prev, show: false }));
      toast.warn("Payroll transactions are locked in Payroll Parameters. Saved records cannot be deleted.");
      return;
    }

    setDeleteModal(prev => ({ ...prev, show: false }));
    setState(p => ({ ...p, isLoading: true }));
    try {
      const authUser = (() => {
        try {
          return JSON.parse(localStorage.getItem("userDetails") || "{}");
        } catch {
          return {};
        }
      })();
      const authToken = authUser?.token || API_WEB_URLS.Token;
      const userId = authUser?.Id ? String(authUser.Id) : "0";

      const res = await fetch(`${API_WEB_URLS.BASE}PayrollTransaction/${userId}/token/${targetId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const json = await res.json();
      if (res.ok && json.success) {
        toast.success(`Payroll record for ${empName} deleted successfully!`);
        const activeFirmId = sessionFirm?.Id || firmId || 0;
        if (activeFirmId) {
          cachedEmployeeData.delete(activeFirmId);
          cachedLeaveHistory.clear();
        }
        userClearedRef.current = false;
        resetForm(); // Sets selectedRecordId(null), clears fields, setActiveTab('entry') -> ADD MODE
        const refreshedRecords = await logPayrollSession(); // Reloads saved records from database

        if (!isAutoPayrollOn) {
          // Manual entry: clean Add mode, Entry tab, and focus directly into Employee dropdown
          setActiveTab('entry');
          focusEmployeeDropdown(250);
        } else {
          // Auto payroll mode: pick next pending employee from fresh list
          const currentSavedList = (refreshedRecords && Array.isArray(refreshedRecords))
            ? refreshedRecords
            : savedRecords.filter(r => r.Id !== targetId);

          const sortedActive = sortEmployeesByParams(state.employees, payrollParams)
            .filter(e => !isNotInUse(e))
            .filter(e => isEmployeeEligibleForPeriod(e, selectedMonth, selectedYear, currentSavedList))
            .filter(e => !isMultiESI || !filterEsiNo || (e.ESINo || "").trim() === filterEsiNo)
            .filter(e => !isMultiPF  || !filterPfNo  || (e.PFNo  || "").trim() === filterPfNo);

          const pending = sortedActive.filter(e => !currentSavedList.some(r => String(r.F_EmployeeMaster) === String(e.Id)));

          if (pending.length > 0) {
            const nextEmp = pending[0];
            await handleEmployeeChange(nextEmp.Id, null, state.employees, currentSavedList);
            toast.info(`Next pending employee auto-selected: ${nextEmp.Name}`, { autoClose: 2500 });
            setActiveTab('entry');
            setTimeout(() => {
              const wdInput = document.querySelector('input[name="WorkingDays"]');
              if (wdInput) {
                wdInput.focus();
                if (typeof wdInput.select === 'function') wdInput.select();
              }
            }, 100);
          } else {
            setActiveTab('entry');
            focusEmployeeDropdown(250);
          }
        }
      } else {
        toast.error(json.message || "Failed to delete payroll transaction.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Network error while deleting payroll transaction.");
    } finally {
      setState(p => ({ ...p, isLoading: false }));
    }
  };

  // ─── Load master delete security password from PayrollParameter for current firm ───
  const loadSecurityPassword = useCallback(async () => {
    const fid = sessionFirm?.Id || firmId || 0;
    if (!fid) return;
    try {
      const res = await Fn_FillListData(
        dispatch,
        () => {},
        "Data",
        `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${fid}`
      );
      if (res && Array.isArray(res) && res.length > 0) {
        const rawPass = res[0]?.DeletePassword;
        if (rawPass) {
          const dec = await decryptPassword(rawPass);
          if (dec && dec.trim() !== "") {
            setDeleteSecurityPassword(dec.trim());
            return;
          }
        }
      }
      setDeleteSecurityPassword("015889");
    } catch (e) {
      console.error("Error loading delete security password from PayrollParameter:", e);
      setDeleteSecurityPassword("015889");
    }
  }, [dispatch, sessionFirm?.Id, firmId]);

  // ─── Prompt Delete All Modal with Security Password Verification ───
  const promptDeleteAll = () => {
    const isLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
    if (isLocked) {
      toast.warn("Payroll transactions are locked in Payroll Parameters. Deleting saved records is not allowed.");
      return;
    }
    loadSecurityPassword();
    const searchLower = (panelSearch || "").trim().toLowerCase();
    const items = searchLower
      ? savedRecords.filter(record => {
          const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
          return (
            (emp?.Name || "").toLowerCase().includes(searchLower) ||
            (emp?.FatherName || "").toLowerCase().includes(searchLower) ||
            (emp?.EmployeeCode || "").toLowerCase().includes(searchLower) ||
            (emp?.CardNo || "").toLowerCase().includes(searchLower)
          );
        })
      : savedRecords;

    if (items.length === 0) {
      toast.warning("No saved payroll records available to delete.");
      return;
    }

    setDeleteAllTarget({
      items: items,
      totalCount: items.length,
    });
    setSecurityPassword("");
    setPasswordError("");
    setDeleteReason("");
    setShowPassword(false);
    setShowDeleteAllModal(true);
  };

  // ─── Execute Confirmed Secure Bulk Deletion ───
  const handleConfirmDeleteAll = async () => {
    const isLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
    if (isLocked) {
      toast.warn("Payroll transactions are locked in Payroll Parameters. Deleting saved records is not allowed.");
      setShowDeleteAllModal(false);
      return;
    }
    const entered = (securityPassword || "").trim();
    const authorized = (deleteSecurityPassword || "").trim() || "015889";

    if (entered !== authorized) {
      setPasswordError("Invalid security password! Please enter the authorized delete password configured in Payroll Parameter.");
      if (passwordInputRef.current) passwordInputRef.current.focus();
      return;
    }

    setPasswordError("");
    setIsDeletingAll(true);
    const targetItems = deleteAllTarget.items;
    const total = targetItems.length;
    setDeleteAllProgress({ current: 0, total, percentage: 0 });

    const authUser = (() => {
      try {
        return JSON.parse(localStorage.getItem("userDetails") || "{}");
      } catch {
        return {};
      }
    })();
    const authToken = authUser?.token || API_WEB_URLS.Token;
    const userId = authUser?.Id ? String(authUser.Id) : "0";

    const successfullyDeletedIds = [];
    const failedList = [];

    try {
      const chunkSize = 5;
      for (let i = 0; i < targetItems.length; i += chunkSize) {
        const chunk = targetItems.slice(i, i + chunkSize);
        await Promise.all(
          chunk.map(async (record) => {
            const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
            const empName = emp?.Name || record.EmployeeName || `ID ${record.Id}`;
            try {
              const res = await fetch(`${API_WEB_URLS.BASE}PayrollTransaction/${userId}/token/${record.Id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${authToken}`,
                  "Content-Type": "application/json",
                },
              });

              if (res.ok) {
                const data = await res.json().catch(() => ({}));
                if (data.success !== false) {
                  successfullyDeletedIds.push(record.Id);
                } else {
                  failedList.push({
                    record,
                    empName,
                    reason: data.message || "Failed to delete record",
                  });
                }
              } else {
                const errData = await res.json().catch(() => ({}));
                const msg = errData?.message || errData?.Message || `HTTP ${res.status}`;
                failedList.push({
                  record,
                  empName,
                  reason: msg,
                });
              }
            } catch (err) {
              failedList.push({
                record,
                empName,
                reason: err.message || "Network error",
              });
            }
          })
        );

        const currentCount = Math.min(i + chunkSize, total);
        const pct = Math.round((currentCount / total) * 100);
        setDeleteAllProgress({ current: currentCount, total, percentage: pct });
      }

      setShowDeleteAllModal(false);

      if (successfullyDeletedIds.length > 0) {
        resetForm();
        userClearedRef.current = false;
        setActiveTab('entry');
        const isAutoPayrollOn = !!(payrollParams?.IsAutoPayroll || payrollParams?.CreatePayrollAutomatically);
        if (!isAutoPayrollOn) {
          focusEmployeeDropdown(250);
        }
      }

      cachedLeaveHistory.clear();
      await logPayrollSession();

      if (successfullyDeletedIds.length > 0 && failedList.length === 0) {
        toast.success(`Successfully deleted all ${successfullyDeletedIds.length} payroll records!`);
      } else if (successfullyDeletedIds.length > 0 && failedList.length > 0) {
        toast.warning(`Deleted ${successfullyDeletedIds.length} records. ${failedList.length} records failed.`);
        setDeleteAllResults({
          successCount: successfullyDeletedIds.length,
          failedCount: failedList.length,
          failedItems: failedList,
        });
        setShowDeleteAllResultModal(true);
      } else if (failedList.length > 0) {
        toast.error(`Failed to delete payroll records.`);
        setDeleteAllResults({
          successCount: 0,
          failedCount: failedList.length,
          failedItems: failedList,
        });
        setShowDeleteAllResultModal(true);
      }
    } catch (err) {
      console.error("Bulk payroll delete error:", err);
      toast.error("Error during bulk deletion process.");
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Get min and max dates for the current payroll month/year (restricts date picker to current month only)
  const getDateRangeForMonth = () => {
    const monthNum = getMonthIndex(selectedMonth);
    const year = parseInt(selectedYear, 10) || new Date().getFullYear();
    
    const firstDay = new Date(year, monthNum, 1);
    const lastDay = new Date(year, monthNum + 1, 0);
    
    const minDate = firstDay.toISOString().split('T')[0]; // YYYY-MM-DD
    const maxDate = lastDay.toISOString().split('T')[0]; // YYYY-MM-DD
    
    return { minDate, maxDate };
  };

  // ─── Focus employee dropdown on page load or after loading ───────────────
  useEffect(() => {
    if (!state.isLoading && empSelectRef.current) {
      empSelectRef.current.focus();
    }
  }, [state.isLoading]);

  // ─── Escape closes save modal ────────────────────────────────────────────
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') setShowSaveModal(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, []);

  // ─── Enter-key field navigation (desktop-software style) ────────────────
  // Collects all [data-nav-order] elements currently in DOM, moves focus to next.
  // When the last field is reached → switches to Data Display tab.
  const handleEnterNav = React.useCallback((currentOrder) => {
    const all = Array.from(document.querySelectorAll('[data-nav-order]'))
      .map(el => ({ el, order: parseFloat(el.getAttribute('data-nav-order')) }))
      .filter(x => !isNaN(x.order))
      .sort((a, b) => a.order - b.order);
    const idx = all.findIndex(x => x.order === currentOrder);
    if (idx >= 0 && idx < all.length - 1) {
      const next = all[idx + 1];
      next.el.focus();
      if (typeof next.el.select === 'function') next.el.select();
    } else {
      // Last navigable field → go to Data Display tab
      setActiveTab('display');
      setTimeout(() => {
        const btn = document.querySelector('[data-display-enter]');
        if (btn) btn.focus();
      }, 80);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTransactionLocked = !!(payrollParams?.IsTransactionLocked || payrollParams?.TransactionLocked);
  const isRecordLocked = isTransactionLocked && selectedRecordId !== null && Number(selectedRecordId) > 0;
  const isAutoPayrollOn = !!(payrollParams?.IsAutoPayroll || payrollParams?.CreatePayrollAutomatically);

  // ─── Add Button Action: Select Next Pending Employee (Visible when IsAutoPayroll is OFF) ───
  const handleAddNextEmployee = useCallback(() => {
    userClearedRef.current = false;
    if (!state.employees || state.employees.length === 0) {
      toast.warn("No employees loaded for current firm.");
      return;
    }

    const sortedActive = sortEmployeesByParams(state.employees, payrollParams)
      .filter(e => isEmployeeEligibleForPeriod(e, selectedMonth, selectedYear, savedRecords))
      .filter(e => !isMultiESI || !filterEsiNo || (e.ESINo || "").trim() === filterEsiNo)
      .filter(e => !isMultiPF  || !filterPfNo  || (e.PFNo  || "").trim() === filterPfNo);

    if (sortedActive.length === 0) {
      toast.warn("No active eligible employees found for this month.");
      return;
    }

    // Pending employees (not yet in savedRecords)
    const pending = sortedActive.filter(e => !savedRecords.some(r => String(r.F_EmployeeMaster) === String(e.Id)));

    if (pending.length === 0) {
      toast.info(`All ${sortedActive.length} employees already have payroll records for ${selectedMonth} ${selectedYear}.`);
      return;
    }

    let nextEmp = null;
    const currentEmpId = f.F_EmployeeMaster;
    if (currentEmpId) {
      const currentIdx = sortedActive.findIndex(e => String(e.Id) === String(currentEmpId));
      if (currentIdx >= 0) {
        nextEmp = sortedActive.slice(currentIdx + 1).find(e => pending.some(p => String(p.Id) === String(e.Id)));
      }
    }

    if (!nextEmp) {
      nextEmp = pending[0];
    }

    if (nextEmp) {
      const isCopyDaysOn = !!(payrollParams?.IsCopyDaysNextEmployee || payrollParams?.CopyDaysToNextEmployee);
      const daysToCopy = isCopyDaysOn && (parseFloat(f.WorkingDays) > 0 || parseFloat(f.MonthDays) > 0) ? {
        WorkingDays: f.WorkingDays,
        WorkingHours: f.WorkingHours,
        WorkingMinutes: f.WorkingMinutes,
        FestivalHoliday: f.FestivalHoliday,
        WeeklyHoliday: f.WeeklyHoliday,
        LeaveWages: f.LeaveWages
      } : null;

      handleEmployeeChange(nextEmp.Id, daysToCopy, state.employees, savedRecords);
      toast.info(`Next employee selected: ${nextEmp.Name}`);
      setActiveTab('entry');
      setTimeout(() => {
        const wdInput = document.querySelector('input[name="WorkingDays"]');
        if (wdInput) {
          wdInput.focus();
          wdInput.select();
        }
      }, 150);
    }
  }, [state.employees, payrollParams, selectedMonth, selectedYear, savedRecords, isMultiESI, filterEsiNo, isMultiPF, filterPfNo, f, handleEmployeeChange]);

  // ─── Page Up Action: Select Previous Saved Payroll Record ───
  const handlePageUp = useCallback(() => {
    if (!savedRecords || savedRecords.length === 0) {
      toast.info("No saved payroll records for this month.");
      return;
    }
    userClearedRef.current = false;
    const curIdx = savedRecords.findIndex(r => r.Id === selectedRecordId || String(r.F_EmployeeMaster) === String(f.F_EmployeeMaster));
    let targetRec = null;
    if (curIdx > 0) {
      targetRec = savedRecords[curIdx - 1];
    } else if (curIdx === 0) {
      toast.info("Already at the first saved payroll record.");
      return;
    } else {
      targetRec = savedRecords[savedRecords.length - 1];
    }
    if (targetRec) {
      loadSavedRecord(targetRec);
      const emp = state.employees.find(e => String(e.Id) === String(targetRec.F_EmployeeMaster));
      toast.info(`[PgUp / ↑] Loaded: ${emp?.Name || targetRec.EmployeeName || "Saved Record"} (${savedRecords.indexOf(targetRec) + 1}/${savedRecords.length})`);
    }
  }, [savedRecords, selectedRecordId, f.F_EmployeeMaster, loadSavedRecord, state.employees]);

  // ─── Page Down Action: Select Next Saved Payroll Record ───
  const handlePageDown = useCallback(() => {
    if (!savedRecords || savedRecords.length === 0) {
      toast.info("No saved payroll records for this month.");
      return;
    }
    userClearedRef.current = false;
    const curIdx = savedRecords.findIndex(r => r.Id === selectedRecordId || String(r.F_EmployeeMaster) === String(f.F_EmployeeMaster));
    let targetRec = null;
    if (curIdx >= 0 && curIdx < savedRecords.length - 1) {
      targetRec = savedRecords[curIdx + 1];
    } else if (curIdx === savedRecords.length - 1) {
      toast.info("Already at the last saved payroll record.");
      return;
    } else {
      targetRec = savedRecords[0];
    }
    if (targetRec) {
      loadSavedRecord(targetRec);
      const emp = state.employees.find(e => String(e.Id) === String(targetRec.F_EmployeeMaster));
      toast.info(`[PgDn / ↓] Loaded: ${emp?.Name || targetRec.EmployeeName || "Saved Record"} (${savedRecords.indexOf(targetRec) + 1}/${savedRecords.length})`);
    }
  }, [savedRecords, selectedRecordId, f.F_EmployeeMaster, loadSavedRecord, state.employees]);

  // ─── Keyboard Hotkeys for PageUp / PageDown / ArrowUp / ArrowDown ───
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept when dialogs/modals are open
      if (showSaveModal || deleteModal.show || showDeleteAllModal || showRecalculateModal) return;

      // Check if an open dropdown/select menu is actively open (e.g. react-select menu or custom dropdown)
      const isMenuOpen = !!document.querySelector(
        '.react-select__menu, [class*="-menu"], [role="listbox"], .dropdown-menu.show'
      );

      // Check active element
      const activeEl = document.activeElement;
      const isInsideSelectControl = activeEl && (
        activeEl.tagName === 'SELECT' ||
        activeEl.getAttribute('role') === 'combobox' ||
        activeEl.getAttribute('aria-autocomplete') === 'list' ||
        !!activeEl.closest('.react-select-container, [class*="-container"], [class*="-control"], [class*="-menu"], .dropdown-menu')
      );

      // If an option menu is open or user is navigating inside a dropdown/select control, allow standard menu navigation
      const isNavigatingDropdownMenu = (isMenuOpen || isInsideSelectControl) && !e.altKey && !e.ctrlKey;

      const isUp = e.key === "PageUp" || ((e.key === "ArrowUp" || e.code === "ArrowUp") && !isNavigatingDropdownMenu);
      const isDown = e.key === "PageDown" || ((e.key === "ArrowDown" || e.code === "ArrowDown") && !isNavigatingDropdownMenu);

      if (isUp) {
        e.preventDefault();
        handlePageUp();
      } else if (isDown) {
        e.preventDefault();
        handlePageDown();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showSaveModal, deleteModal.show, showDeleteAllModal, showRecalculateModal, handlePageUp, handlePageDown]);

  
  const isDailyWage = (
    f.F_BasicTypeMaster === 2 ||
    String(f.BasicTypeName || "").toLowerCase().includes("day") ||
    isDailyWageEmployee(state.selectedEmployee)
  );
  const isNoWeeklyHolidayForDaily = !!(payrollParams?.IsNoWeeklyHolidayDailyWages || payrollParams?.NoWeeklyHolidayForDailyWages) && isDailyWage;
  const isBasicEditable = !!(payrollParams?.IsBasicEditable || payrollParams?.BasicEditable);

  return (
    <RecordLockContext.Provider value={isRecordLocked}>
      <div style={S.page} className="payroll-form">
      {/* ─── Focus highlight styles ─── */}
      <style>{`
        .payroll-form input:focus,
        .payroll-form select:focus {
          background: #fff !important;
          border: 1.5px solid #7c69ef !important;
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(124,105,239,0.18) !important;
          border-radius: 6px !important;
        }
        .react-select__option--is-focused,
        [class*="-option"][class*="-is-focused"] {
          background-color: #7c69ef !important;
          color: #ffffff !important;
          font-weight: 600 !important;
        }
        .react-select__option--is-selected,
        [class*="-option"][class*="-is-selected"] {
          background-color: #5b47d6 !important;
          color: #ffffff !important;
          font-weight: 700 !important;
        }
        @keyframes py-spin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes py-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        @keyframes py-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes py-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <PageTitle activeMenu="Payroll" motherMenu="Transaction" pageContent="Payroll" />

      {/* ─── Full-Page Loader Overlay: only show blocking backdrop on cold start when no employees loaded ─── */}
      {state.isLoading && state.employees.length === 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(3px)',
          zIndex: 9998,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          animation: 'py-fade-in 0.2s ease',
        }}>
          {/* Spinner */}
          <div style={{
            width: 48, height: 48,
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #7c69ef',
            borderRadius: '50%',
            animation: 'py-spin 0.75s linear infinite',
            marginBottom: 14,
            boxShadow: '0 4px 16px rgba(124,105,239,0.18)',
          }} />
          <div style={{
            fontSize: 13.5, fontWeight: 700, color: '#7c69ef',
            letterSpacing: '0.3px',
            animation: 'py-pulse 1.4s ease-in-out infinite',
          }}>Loading Payroll...</div>
          <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4 }}>Please wait</div>
        </div>
      )}

      {/* ─── Top Progress Line when warm reload / refreshing in background ─── */}
      {state.isLoading && state.employees.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 3,
          background: 'linear-gradient(90deg, #7c69ef, #60a5fa, #7c69ef)',
          backgroundSize: '200% 100%',
          animation: 'py-shimmer 1.5s infinite linear',
          zIndex: 9999
        }} />
      )}

      {/* ─── Session Banner (Firm / Month / Year) ─── */}
      {isSessionReady ? (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", color: "#fff",
          padding: "8px 16px", fontSize: "12.5px", fontWeight: 500,
          marginBottom: "10px", borderRadius: "8px", boxShadow: "0 4px 12px rgba(15,23,42,0.15)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ background: "rgba(255,255,255,0.12)", padding: "2px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, letterSpacing: "0.4px" }}>FIRM</span>
            <span style={{ color: "#f8fafc", fontWeight: 600 }}>{sessionFirm.Name}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ background: "rgba(124,105,239,0.25)", color: "#c7d2fe", padding: "2px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, border: "1px solid rgba(124,105,239,0.4)", letterSpacing: "0.4px" }}>PERIOD</span>
              <span style={{ color: "#f8fafc", fontWeight: 600 }}>{(() => {
                const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                const monthNum = monthNames.indexOf(selectedMonth) + 1;
                const monthStr = String(monthNum).padStart(2, '0');
                return `${monthStr}-${selectedYear}`;
              })()}</span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-light ms-1"
              onClick={() => {
                const fid = sessionFirm?.Id || firmId || 0;
                cachedFirmMasters.clear();
                cachedEmployeeData.clear();
                cachedLeaveHistory.clear();
                lastDaysRef.current = null;
                loadAllPayrollData(fid);
                toast.info("Refreshed latest employee & parameters from server.");
              }}
              style={{ fontWeight: 600, fontSize: "11.5px", padding: "3px 10px", borderRadius: "6px" }}
              title="Sync latest master & employee data from server"
            >
              <i className="fas fa-sync-alt me-1"></i> Refresh Data
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light ms-2"
              onClick={() => navigate("/PayrollImportExport")}
              style={{ fontWeight: 600, fontSize: "11.5px", padding: "3px 12px", borderRadius: "6px", color: "#0f172a" }}
            >
              <i className="fas fa-file-excel me-1" style={{ color: "#16a34a" }}></i> Import / Export
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          background: "#FFF8E1", border: "1px solid #FFD54F",
          padding: "10px 16px", fontSize: "13px", color: "#856404",
          marginBottom: "8px", borderRadius: "8px", textAlign: "center",
        }}>
          ⚠ Payroll form is <strong>disabled</strong>. Please go to <strong>Home</strong> and select Firm, Month &amp; Year first.
        </div>
      )}

      {/* ─── Transaction Locked Banner ─── */}
      {isSessionReady && isTransactionLocked && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "#fff3cd",
          border: "1px solid #ffeeba",
          color: "#856404",
          padding: "8px 16px",
          fontSize: "12px",
          fontWeight: 600,
          marginBottom: "10px",
          borderRadius: "8px"
        }}>
          <span style={{ fontSize: "14px" }}>🔒</span>
          <span>
            <strong>TRANSACTIONS LOCKED:</strong> Payroll transactions are locked in Payroll Parameters. Saved records cannot be modified or deleted.
          </span>
        </div>
      )}

      {/* ─── Main Card (disabled when session not ready) ─── */}
      <div style={{ ...S.card, opacity: isSessionReady ? 1 : 0.45, pointerEvents: isSessionReady ? "auto" : "none", userSelect: isSessionReady ? "auto" : "none" }}>
        {/* ─── TABS ─── */}
        <div style={{ display: "flex", padding: "0 10px", paddingTop: "4px", background: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
          <div style={activeTab === "entry" ? S.tabActive : S.tab} onClick={() => setActiveTab("entry")}>Data Entry</div>
          <div style={activeTab === "display" ? S.tabActive : S.tab} onClick={() => setActiveTab("display")}>Data Display</div>
          <div style={activeTab === "leave" ? S.tabActive : S.tab} onClick={() => setActiveTab("leave")}>Leave Date</div>
        </div>

        <div style={S.cardBody}>

          {/* ╔══════════════════════════════════════════════════════════════╗ */}
          {/* ║  TAB 1 — DATA ENTRY                                        ║ */}
          {/* ╚══════════════════════════════════════════════════════════════╝ */}
          {activeTab === "entry" && (
            <div>
              {/* ─── Top Section: Employee Info ─── */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  {/* ── Multi ESI / PF Filter dropdowns (show only when params ON) ── */}
                  {isMultiESI && esiNoList.length > 0 && (
                    <LF label="ESI No Filter:" lw={100}>
                      <select
                        style={{ ...S.select, minWidth: 160 }}
                        value={filterEsiNo}
                        onChange={e => setFilterEsiNo(e.target.value)}
                      >
                        <option value="">-- All ESI Nos --</option>
                        {esiNoList.map(no => (
                          <option key={no} value={no}>{no}</option>
                        ))}
                      </select>
                    </LF>
                  )}
                  {isMultiPF && pfNoList.length > 0 && (
                    <LF label="PF No Filter:" lw={100}>
                      <select
                        style={{ ...S.select, minWidth: 160 }}
                        value={filterPfNo}
                        onChange={e => setFilterPfNo(e.target.value)}
                      >
                        <option value="">-- All PF Nos --</option>
                        {pfNoList.map(no => (
                          <option key={no} value={no}>{no}</option>
                        ))}
                      </select>
                    </LF>
                  )}

                  {/* ── Employee Searchable Select with Criteria Radios ── */}
                  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "4px", gap: "6px" }}>
                    <label style={{ ...S.label, width: 100, minWidth: 100, textAlign: "right", paddingRight: "4px", marginTop: "24px" }}>Emp. Name:</label>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Search Criteria Radio Buttons */}
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "4px",
                        padding: "3px 10px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        width: "fit-content"
                      }}>
                        {[
                          { id: "Name", label: "Name" },
                          { id: "UAN", label: "UAN" },
                          { id: "PFNo", label: "PF No" },
                          { id: "ESINo", label: "ESINo" },
                          { id: "Card", label: "Card" },
                          { id: "Father", label: "Father" },
                        ].map(opt => (
                          <label
                            key={opt.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                              fontSize: "11.5px",
                              fontWeight: searchBy === opt.id ? 700 : 500,
                              color: searchBy === opt.id ? "#7c69ef" : "#475569",
                              margin: 0,
                              userSelect: "none"
                            }}
                          >
                            <input
                              type="radio"
                              name="employeeSearchCriteria"
                              value={opt.id}
                              checked={searchBy === opt.id}
                              onChange={() => {
                                setSearchBy(opt.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  if (empSelectRef.current) empSelectRef.current.focus();
                                }
                              }}
                              style={{ cursor: "pointer", accentColor: "#7c69ef" }}
                            />
                            <span>{opt.label}</span>
                          </label>
                        ))}
                      </div>

                      {/* Searchable Select */}
                      <div style={{ position: "relative" }}>
                        {(() => {
                          const filteredEmployees = sortEmployeesByParams(state.employees, payrollParams)
                            .filter(e => isEmployeeEligibleForPeriod(e, selectedMonth, selectedYear, savedRecords) || String(e.Id) === String(f.F_EmployeeMaster))
                            .filter(e => !savedRecords.some(r => String(r.F_EmployeeMaster) === String(e.Id)) || String(e.Id) === String(f.F_EmployeeMaster))
                            .filter(e => !isMultiESI || !filterEsiNo || (e.ESINo || "").trim() === filterEsiNo)
                            .filter(e => !isMultiPF  || !filterPfNo  || (e.PFNo  || "").trim() === filterPfNo);

                          const employeeOptions = filteredEmployees.map(e => ({
                            value: e.Id,
                            label: getEmployeeOptionLabel(e, searchBy),
                            employee: e
                          }));

                          const selectedOption = employeeOptions.find(opt => String(opt.value) === String(f.F_EmployeeMaster)) || (
                            f.F_EmployeeMaster ? { value: f.F_EmployeeMaster, label: f.EmployeeName || "Selected Employee" } : null
                          );

                          return (
                            <Select
                              ref={empSelectRef}
                              className="react-select-container"
                              classNamePrefix="react-select"
                              isDisabled={state.isLoading}
                              isLoading={state.isLoading}
                              isClearable
                              isSearchable
                              openMenuOnFocus
                              placeholder={state.isLoading ? "Loading employees..." : `Search by ${searchBy}, Name, UAN, PF, ESI, Card, Father...`}
                              options={employeeOptions}
                              value={selectedOption}
                              onChange={(selected) => {
                                const val = selected ? selected.value : "";
                                handleEmployeeChange(val);
                                if (val) {
                                  setTimeout(() => {
                                    const wdInput = document.querySelector('input[name="WorkingDays"]');
                                    if (wdInput) {
                                      wdInput.focus();
                                      if (typeof wdInput.select === 'function') wdInput.select();
                                    }
                                  }, 120);
                                }
                              }}
                              filterOption={filterEmployeeOption}
                              styles={customSelectStyles}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  // Check if the dropdown menu is open
                                  const isMenuOpen = !!document.querySelector('.react-select__menu, [class*="-menu"]');
                                  if (isMenuOpen) {
                                    // Allow react-select default Enter handler to select the currently focused option
                                    return;
                                  }
                                  // If menu is closed or already selected an employee, Enter moves to WorkingDays
                                  if (f.F_EmployeeMaster) {
                                    e.preventDefault();
                                    handleEnterNav(1);
                                  }
                                }
                              }}
                            />
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <LF label="Employee :" lw={100}>
                    <input style={{ ...S.inputRO, width: 200 }} value={f.EmployeeName} readOnly />
                    <label style={{ ...S.label, width: 50, textAlign: "right", paddingRight: 4 }}>Father</label>
                    <input style={{ ...S.inputRO, flex: 1 }} value={f.FatherName} readOnly />
                  </LF>
                  <LF label="Department" lw={100}>
                    <input style={{ ...S.inputRO, width: 200 }} value={f.Department} readOnly />
                    <label style={{ ...S.label, width: 70, textAlign: "right", paddingRight: 4 }}>Join Date</label>
                    <input style={{ ...S.inputRO, width: 110 }} value={f.JoinDate} readOnly />
                  </LF>
                </div>

                {/* Dynamic Leave Balance Box */}
                <div style={{ border: "1px solid #E9ECEF", padding: "6px 10px", background: "#F8FAFF", minWidth: 200, maxWidth: 360, borderRadius: "8px", overflowX: "auto" }}>
                  <table style={{ fontSize: "11px", fontWeight: 600 }}>
                    <thead><tr>
                      <td></td>
                      {leaveMasterList.length > 0 ? (
                        leaveMasterList.map(leave => (
                          <td key={leave.Id} style={{ padding: "0 6px", textAlign: "center", color: "#1c7ed6", whiteSpace: "nowrap" }}>
                            {leave.Name}
                          </td>
                        ))
                      ) : (
                        <td style={{ padding: "0 8px", textAlign: "center", color: "#aaa" }}>No Leaves</td>
                      )}
                    </tr></thead>
                    <tbody>
                      <tr>
                        <td style={{ fontWeight: 700, paddingRight: 6 }}>Bal :</td>
                        {leaveMasterList.length > 0 ? (
                          leaveMasterList.map(leave => (
                            <td key={leave.Id} style={{ padding: "1px 3px" }}>
                              <input
                                style={{ ...(isRecordLocked ? S.inputRO : S.input), width: 44, textAlign: "center", fontWeight: 700, color: isRecordLocked ? "#64748b" : "#2b8a3e" }}
                                value={dynamicLeaveBalances[leave.Id]?.Bal ?? 0}
                                onChange={e => !isRecordLocked && handleLeaveBalanceChange(leave.Id, 'Bal', e.target.value)}
                                readOnly={isRecordLocked}
                                disabled={isRecordLocked}
                                title={`Opening: ${dynamicLeaveBalances[leave.Id]?.OpBal || 0} + Entitled: ${dynamicLeaveBalances[leave.Id]?.Entitled || 0} - Total Enjoyed: ${dynamicLeaveBalances[leave.Id]?.TotalEnjoyed || 0}`}
                              />
                            </td>
                          ))
                        ) : (
                          <td><input style={{ ...S.inputRO, width: 44, textAlign: "center" }} value={0} readOnly disabled /></td>
                        )}
                      </tr>
                      <tr>
                        <td style={{ fontWeight: 700, paddingRight: 6 }}>Enjoy</td>
                        {leaveMasterList.length > 0 ? (
                          leaveMasterList.map(leave => (
                            <td key={leave.Id} style={{ padding: "1px 3px" }}>
                              <input
                                style={{ ...(isRecordLocked ? S.inputRO : S.input), width: 44, textAlign: "center", color: isRecordLocked ? "#64748b" : "#c92a2a" }}
                                value={dynamicLeaveBalances[leave.Id]?.TotalEnjoyed ?? 0}
                                onChange={e => !isRecordLocked && handleLeaveBalanceChange(leave.Id, 'TotalEnjoyed', e.target.value)}
                                readOnly={isRecordLocked}
                                disabled={isRecordLocked}
                                title={`Past: ${dynamicLeaveBalances[leave.Id]?.PastEnjoyed || 0} + Current: ${dynamicLeaveBalances[leave.Id]?.CurrentTaken || 0}`}
                              />
                            </td>
                          ))
                        ) : (
                          <td><input style={{ ...S.inputRO, width: 44, textAlign: "center" }} value={0} readOnly disabled /></td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ─── Salary Component Strip (Master Defined Rates & Calculated Amounts) ─── */}
              {(() => {
                const effectiveAl = getEffectiveAllowances(rawAllowances, selectedMonth, selectedYear);
                const effectiveDl = getEffectiveDeductions(rawDeductions, selectedMonth, selectedYear);

                const totalLeaveAuto = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                const currentPaidDays = Math.min(
                  parseFloat(f.MonthDays) || 0,
                  (parseFloat(f.WorkingDays) || 0) +
                  (parseFloat(f.LeaveWages) || 0) +
                  (parseFloat(f.FestivalHoliday) || 0) +
                  (parseFloat(f.WeeklyHoliday) || 0) +
                  totalLeaveAuto
                );

                const isDirectWages = isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList);
                const isWagesEditable = (isDirectWages || isBasicEditable) && !isRecordLocked;
                const isAllowDeductEditable = !!payrollParams?.IsAllowanceDeductionEditable && !isRecordLocked;

                return (
                  <div style={{
                    ...S.sectionLine,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    marginTop: "8px",
                    marginBottom: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px"
                  }}>
                    {/* Row 1: Master / Defined Rates */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#475569", background: "#e2e8f0", padding: "2px 8px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Master / Rate:
                      </span>

                      <label style={{ ...S.label, fontWeight: 600 }}>Basic:</label>
                      <I 
                        name="BasicSalary"
                        value={f.BasicSalary} 
                        onChange={e => {
                          if (!isBasicEditable) {
                            toast.warning("Basic Salary is not editable as per Payroll Parameters.");
                            return;
                          }
                          setNum('BasicSalary', e.target.value);
                        }} 
                        w={85} 
                        readOnly={!isBasicEditable}
                        disabled={!isBasicEditable}
                        style={{
                          ...(!isBasicEditable ? S.inputRO : S.input),
                          width: 85,
                          backgroundColor: !isBasicEditable ? "#f1f5f9" : "#ffffff",
                          cursor: !isBasicEditable ? "not-allowed" : "text",
                          color: !isBasicEditable ? "#64748b" : "#0f172a"
                        }}
                        title={!isBasicEditable ? "Basic Salary is locked (enable 'Basic editable' in Payroll Parameters to edit)" : "Basic Salary Rate"}
                        data-nav-order={isBasicEditable ? 2 : undefined} 
                        onKeyDown={e => { if (isBasicEditable && e.key === 'Enter') { e.preventDefault(); handleEnterNav(2); } }}
                      />
                      {f.BasicTypeName && (
                        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>({f.BasicTypeName})</span>
                      )}

                      {effectiveAl.map((a, idx) => {
                        const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow ${a.F_AllowanceMaster}`;
                        const orderVal = Number((2.01 + idx * 0.01).toFixed(2));
                        return (
                          <React.Fragment key={a.F_AllowanceMaster}>
                            <label style={{ ...S.label, color: "#16a34a", fontWeight: 600 }}>{name}:</label>
                            <I
                              value={a.Amount ?? ""}
                              onChange={e => {
                                const val = parseFloat(e.target.value);
                                const newAmt = isNaN(val) ? "" : Math.max(0, val);
                                setRawAllowances(prev => prev.map(item => item.F_AllowanceMaster === a.F_AllowanceMaster ? { ...item, Amount: newAmt } : item));
                              }}
                              w={75}
                              readOnly={!isAllowDeductEditable}
                              style={{
                                color: isAllowDeductEditable ? "#16a34a" : "#64748b",
                                fontWeight: 600,
                                background: isAllowDeductEditable ? "#ffffff" : "#f1f5f9",
                                cursor: isAllowDeductEditable ? "text" : "not-allowed"
                              }}
                              data-nav-order={isAllowDeductEditable ? orderVal : undefined}
                              onKeyDown={ev => { if (isAllowDeductEditable && ev.key === 'Enter') { ev.preventDefault(); handleEnterNav(orderVal); } }}
                              title={isAllowDeductEditable ? `Edit ${name}` : `${name} (Read-Only: Enable "ALLOWANCE DEDUCTION EDITABLE" in Payroll Parameter)`}
                            />
                          </React.Fragment>
                        );
                      })}

                      {effectiveDl.map((d, idx) => {
                        const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct ${d.F_DeductionMaster}`;
                        const orderVal = Number((3.01 + idx * 0.01).toFixed(2));
                        return (
                          <React.Fragment key={d.F_DeductionMaster}>
                            <label style={{ ...S.label, color: "#dc2626", fontWeight: 600 }}>{name}(-):</label>
                            <I
                              value={d.Amount ?? ""}
                              onChange={e => {
                                const val = parseFloat(e.target.value);
                                const newAmt = isNaN(val) ? "" : Math.max(0, val);
                                setRawDeductions(prev => prev.map(item => item.F_DeductionMaster === d.F_DeductionMaster ? { ...item, Amount: newAmt } : item));
                              }}
                              w={75}
                              readOnly={!isAllowDeductEditable}
                              style={{
                                color: isAllowDeductEditable ? "#dc2626" : "#64748b",
                                fontWeight: 600,
                                background: isAllowDeductEditable ? "#ffffff" : "#f1f5f9",
                                cursor: isAllowDeductEditable ? "text" : "not-allowed"
                              }}
                              data-nav-order={isAllowDeductEditable ? orderVal : undefined}
                              onKeyDown={ev => { if (isAllowDeductEditable && ev.key === 'Enter') { ev.preventDefault(); handleEnterNav(orderVal); } }}
                              title={isAllowDeductEditable ? `Edit ${name}` : `${name} (Read-Only: Enable "ALLOWANCE DEDUCTION EDITABLE" in Payroll Parameter)`}
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {/* Row 2: Calculated / Earned for Current Paid Days */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", borderTop: "1px dashed #cbd5e1", paddingTop: "6px" }}>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#1d4ed8", background: "#dbeafe", padding: "2px 8px", borderRadius: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Calculated:
                      </span>

                      <label style={{ ...S.label, color: "#2563eb", fontWeight: 600 }}>Earned Wages:</label>
                      <input
                        style={{
                          ...(isWagesEditable ? S.input : S.inputRO),
                          width: 85,
                          color: "#2563eb",
                          fontWeight: 700,
                          background: isWagesEditable ? "#ffffff" : "#eff6ff",
                          border: isWagesEditable ? "1px solid #2563eb" : "1px solid #bfdbfe"
                        }}
                        value={f.Wages}
                        readOnly={!isWagesEditable || isRecordLocked}
                        disabled={!isWagesEditable || isRecordLocked}
                        onChange={e => setNum('Wages', e.target.value)}
                        data-nav-order={isWagesEditable ? 3 : undefined}
                        name="Wages"
                        onFocus={e => {
                          if (typeof e.target.select === "function") {
                            e.target.select();
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === "-" || e.key === "e" || e.key === "E") {
                            e.preventDefault();
                          } else if (isWagesEditable && e.key === 'Enter') {
                            e.preventDefault();
                            handleEnterNav(3);
                          }
                        }}
                        title={isDirectWages ? "Direct Wages Entry Enabled" : "Earned Basic Wages for Paid Days"}
                      />

                      {effectiveAl.map((a) => {
                        const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow ${a.F_AllowanceMaster}`;
                        const computedAmt = computeSingleAllowance(a, f.MonthDays, currentPaidDays, f.BasicSalary);
                        return (
                          <React.Fragment key={`calc_al_${a.F_AllowanceMaster}`}>
                            <label style={{ ...S.label, color: "#15803d", fontWeight: 500 }}>{name}:</label>
                            <input
                              style={{ ...S.inputRO, width: 75, color: "#15803d", fontWeight: 700, background: "#f0fdf4", border: "1px solid #bbf7d0" }}
                              value={to2Dec(computedAmt)}
                              readOnly
                              title={`Calculated ${name} for Paid Days (${currentPaidDays})`}
                            />
                          </React.Fragment>
                        );
                      })}

                      {effectiveDl.map((d) => {
                        const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct ${d.F_DeductionMaster}`;
                        const computedAmt = computeSingleDeduction(d, f.MonthDays, currentPaidDays, f.BasicSalary);
                        return (
                          <React.Fragment key={`calc_dl_${d.F_DeductionMaster}`}>
                            <label style={{ ...S.label, color: "#b91c1c", fontWeight: 500 }}>{name}(-):</label>
                            <input
                              style={{ ...S.inputRO, width: 75, color: "#b91c1c", fontWeight: 700, background: "#fef2f2", border: "1px solid #fecaca" }}
                              value={to2Dec(computedAmt)}
                              readOnly
                              title={`Calculated ${name} for Paid Days (${currentPaidDays})`}
                            />
                          </React.Fragment>
                        );
                      })}

                      <div style={{ marginLeft: "auto", display: "flex", gap: "10px", alignItems: "center" }}>
                        <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>Total Allow:</span>
                        <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 700 }}>₹{to2Dec(f.TotalAllowances)}</span>
                        <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>Total Deduct:</span>
                        <span style={{ fontSize: "12px", color: "#dc2626", fontWeight: 700 }}>₹{to2Dec(f.TotalDeductions)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ─── Month Days ─── */}
              <div style={{ display: "flex", gap: "6px", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ ...S.label, width: 100, textAlign: "right" }}>Monthday:</label>
                <I
                  name="MonthDays"
                  value={f.MonthDays}
                  onChange={e => setNum('MonthDays', e.target.value)}
                  onBlur={() => {
                    const md = parseFloat(f.MonthDays) || 0;
                    const total = (parseFloat(f.WorkingDays) || 0)
                      + (parseFloat(f.LeaveWages) || 0)
                      + (parseFloat(f.FestivalHoliday) || 0)
                      + (parseFloat(f.WeeklyHoliday) || 0)
                      + (parseFloat(f.LeaveWithWages) > 0 && !parseFloat(f.LeaveWages) ? parseFloat(f.LeaveWithWages) : 0)
                      + Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                    if (md > 0 && total > md) {
                      toast.warning(`Total days (${Math.round(total * 100) / 100}) exceeded Month Days (${md}). Adjusted Working Days to fit.`);
                      const otherDays = total - (parseFloat(f.WorkingDays) || 0);
                      const allowedWD = Math.max(0, Math.round((md - otherDays) * 100) / 100);
                      setNum('WorkingDays', allowedWD);
                    }
                  }}
                  w={60}
                />
              </div>

              {/* ─── Main 3-Column Section ─── */}
              <div style={{ display: "flex", gap: "8px" }}>
                {/* Column 1 — Left */}
                <div style={{ minWidth: 220 }}>
                  {/* Working Days row with conditional Hours/Minutes & Matching Badge */}
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
                    <LF label="Working Days:" lw={120}>
                      <I 
                        value={f.WorkingDays} 
                        onChange={e => setNum('WorkingDays', e.target.value)} 
                        w={60} 
                        name="WorkingDays" 
                        data-nav-order="4" 
                        onKeyDown={e => { 
                          if (e.key === 'Enter') { 
                            e.preventDefault(); 
                            handleEnterNav(4); 
                          } 
                        }} 
                      />
                    </LF>
                    {!!payrollParams?.IsHourlyPayment && (
                      <>
                        <LF label="Hours:" lw={48}>
                          <I value={f.WorkingHours} onChange={e => setNum('WorkingHours', e.target.value)} w={75} step="0.01" data-nav-order="5" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(5); } }} />
                        </LF>
                        <LF label="Minutes:" lw={55}>
                          <I value={f.WorkingMinutes} onChange={e => setNum('WorkingMinutes', e.target.value)} w={55} data-nav-order="6" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(6); } }} />
                        </LF>
                        {/* Working Hours extra indicator */}
                        {(() => {
                          const stdWH = parseFloat(payrollParams?.WorkingHours) || 8;
                          const wDays = parseFloat(f.WorkingDays) || 0;
                          const extraHours = (parseFloat(f.WorkingHours) || 0) + ((parseFloat(f.WorkingMinutes) || 0) / 60);
                          const totalHours = Math.round(((wDays * stdWH) + extraHours) * 100) / 100;
                          
                          if (extraHours > 0) {
                            return (
                              <span
                                className="badge bg-warning text-dark d-inline-flex align-items-center gap-1"
                                style={{ fontSize: 11, padding: "4px 8px", cursor: "help" }}
                                title={`${wDays} days (${wDays * stdWH}h) + ${f.WorkingHours || 0}h ${f.WorkingMinutes || 0}m extra = ${totalHours} total hrs`}
                              >
                                ⏱️ +{extraHours.toFixed(1)}h extra (Total: {totalHours}h)
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </>
                    )}
                  </div>
                  <LF label="Leave Wages:" lw={120}><I value={f.LeaveWages} onChange={e => setNum('LeaveWages', e.target.value)} w={60} name="LeaveWages" data-nav-order="7" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(7); } }} /></LF>
                  <LF label="Festival Holiday:" lw={120}><I value={f.FestivalHoliday} onChange={e => setNum('FestivalHoliday', e.target.value)} w={60} name="FestivalHoliday" data-nav-order="8" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(8); } }} /></LF>
                  <LF label="Weekly Holiday:" lw={120}>
                    <I
                      value={isNoWeeklyHolidayForDaily ? 0 : f.WeeklyHoliday}
                      onChange={e => {
                        if (isNoWeeklyHolidayForDaily) {
                          toast.warning("Weekly Holiday is not applicable for Daily Wage employees as per Payroll Parameters.");
                          return;
                        }
                        setNum('WeeklyHoliday', e.target.value);
                      }}
                      w={60}
                      name="WeeklyHoliday"
                      data-nav-order="9"
                      readOnly={isNoWeeklyHolidayForDaily}
                      disabled={isNoWeeklyHolidayForDaily}
                      title={isNoWeeklyHolidayForDaily ? "Weekly Holiday is not applicable for Daily Wage employees as per Payroll Parameters" : ""}
                      style={isNoWeeklyHolidayForDaily ? { backgroundColor: "#f1f5f9", cursor: "not-allowed", color: "#64748b" } : undefined}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(9); } }}
                    />
                  </LF>
                  {/* Dynamic leaves from LeaveMaster */}
                  {leaveMasterList.length > 0 ? (
                    leaveMasterList.map((leave, idx) => (
                      <LF key={leave.Id} label={`${leave.Name}:`} lw={120}>
                        <I
                          value={leaveValues[leave.Id] ?? ""}
                          onChange={e => {
                            const val = parseFloat(e.target.value);
                            const cleanVal = isNaN(val) ? "" : Math.max(0, val);
                            handleCurrentLeaveChange(leave.Id, cleanVal);
                          }}
                          w={60}
                          name={`Leave_${leave.Id}`}
                          data-nav-order={10 + idx}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(10 + idx); } }}
                        />
                      </LF>
                    ))
                  ) : (
                    // Fallback if no leaves defined
                    <LF label="CL:" lw={120}><span style={{ fontSize: 11, color: '#aaa' }}>No leaves defined</span></LF>
                  )}
                  <LF label="Paid Days:" lw={120}>
                    {(() => {
                      const md = parseFloat(f.MonthDays) || 0;
                      const tot = (parseFloat(f.WorkingDays) || 0) +
                        (parseFloat(f.LeaveWages) || 0) +
                        (parseFloat(f.FestivalHoliday) || 0) +
                        (parseFloat(f.WeeklyHoliday) || 0) +
                        (parseFloat(f.LeaveWithWages) > 0 && !parseFloat(f.LeaveWages) ? parseFloat(f.LeaveWithWages) : 0) +
                        Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                      const isOver = md > 0 && tot > md;
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <input style={{
                            ...S.inputRO,
                            width: 60,
                            fontWeight: 700,
                            color: isOver ? "#dc2626" : "#4C6EF5",
                            textAlign: "center",
                            background: isOver ? "#fef2f2" : "#f8fafc",
                            borderColor: isOver ? "#fca5a5" : "#cbd5e1"
                          }}
                            value={Math.min(md, tot)}
                            readOnly />
                          {isOver && (
                            <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 700 }} title="Total days cannot exceed Month Days">
                              ⚠️ Total &gt; Month Days
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </LF>
                  {(payrollParams?.IsOvertime || payrollParams?.OverTime) && (
                    <LF label="Overtime(Hrs.):" lw={120}><I value={f.OvertimeHours} onChange={e => setNum('OvertimeHours', e.target.value)} w={80} step="0.01" name="OvertimeHours" data-nav-order="12" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(12); } }} /></LF>
                  )}
                </div>

                {/* Column 2 — Middle */}
                <div style={{ minWidth: 200 }}>
                  {payrollParams?.IsArrearApplicable && (
                    <LF label="Arrear :" lw={90}>
                      <I
                        value={f.Arrear}
                        onChange={e => setNum('Arrear', e.target.value)}
                        onBlur={e => handleArrearBlur(e.target.value)}
                        w={80}
                        name="Arrear"
                        data-nav-order="13"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleArrearBlur(e.target.value);
                            handleEnterNav(13);
                          }
                        }}
                      />
                    </LF>
                  )}
                  <div style={{ height: 27 }} />
                  {payrollParams?.IsRecovery && (
                    <LF label="Recovery:" lw={90}><I value={f.Recovery} onChange={e => setNum('Recovery', e.target.value)} w={80} name="Recovery" data-nav-order="14" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(14); } }} /></LF>
                  )}
                  {payrollParams?.IsAdvance && (
                    <LF label="Advance:" lw={90}><I value={f.Advance} onChange={e => setNum('Advance', e.target.value)} w={80} name="Advance" data-nav-order="15" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(15); } }} /></LF>
                  )}
                  {payrollParams?.IsTDS && (
                    <LF label="TDS:" lw={90}><I value={f.TDS} onChange={e => setNum('TDS', e.target.value)} w={80} name="TDS" data-nav-order="16" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(16); } }} /></LF>
                  )}
                </div>

                {/* Column 3 — Right */}
                <div style={{ minWidth: 220 }}>
                  {payrollParams?.IsAdvanceBonus && (
                    <LF label="Advance Bonus:" lw={110}><I value={f.AdvanceBonus} onChange={e => setNum('AdvanceBonus', e.target.value)} w={80} name="AdvanceBonus" data-nav-order="17" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(17); } }} /></LF>
                  )}
                  {payrollParams?.IsExtraPay && (
                    <LF label={payrollParams?.ExtraPayCaption || "Extra Pay"} lw={110}><I value={f.ExtraPay} onChange={e => setNum('ExtraPay', e.target.value)} w={80} name="ExtraPay" data-nav-order="18" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(18); } }} /></LF>
                  )}
                  {payrollParams?.IsPFAdvRefund && (
                    <LF label="PF Refund:" lw={110}><I value={f.PFRefund} onChange={e => setNum('PFRefund', e.target.value)} w={80} name="PFRefund" data-nav-order="19" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(19); } }} /></LF>
                  )}
                  {payrollParams?.IsExtraPay1 && (
                    <LF label={payrollParams?.ExtraPay1Caption || "Extra Pay1"} lw={110}><I value={f.ExtraPay1} onChange={e => setNum('ExtraPay1', e.target.value)} w={80} name="ExtraPay1" data-nav-order="20" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleEnterNav(20); } }} /></LF>
                  )}
                </div>
              </div>

              {/* ─── ESI & PF Left / Exit Reason & Dates ─── */}
              <div style={{ marginTop: "10px", paddingTop: "8px", borderTop: "1px solid #E9ECEF" }}>
                {f.ESIApplicable && (parseFloat(f.WorkingDays || 0) === 0 || parseFloat(f.PaidDay || 0) === 0) && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", marginBottom: "8px",
                    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", fontSize: "12px", color: "#b45309", fontWeight: 600
                  }}>
                    <span>⚠️ 0 Working Days with ESI Active — Please select ESI Left Reason & Last Working Date if employee exited.</span>
                  </div>
                )}
                {/* Row 1: ESI Reason & Last Working (or Leave Date button) */}
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "6px", flexWrap: "wrap" }}>
                  <label style={{ ...S.label, width: 90, textAlign: "right", color: f.ESIReason ? "#e11d48" : (f.ESIApplicable && (parseFloat(f.WorkingDays || 0) === 0 || parseFloat(f.PaidDay || 0) === 0) ? "#d97706" : "#475569"), fontWeight: 600 }}>
                    ESI Reason:
                  </label>
                  <select 
                    style={{ ...S.select, width: 220, borderColor: f.ESIReason ? "#e11d48" : (f.ESIApplicable && (parseFloat(f.WorkingDays || 0) === 0 || parseFloat(f.PaidDay || 0) === 0) ? "#f59e0b" : "#cbd5e1"), background: f.ESIReason ? "#fff1f2" : "#ffffff" }} 
                    value={f.ESIReason || ""} 
                    onChange={e => set('ESIReason', e.target.value)}
                    disabled={isRecordLocked}
                  >
                    <option value="">-- Active / Working --</option>
                    {reasonOfLeftList.map(r => (
                      <option key={r.Id} value={r.Name}>{r.Name}</option>
                    ))}
                  </select>
                  <div style={{ width: 10 }} />
                  <label style={{ ...S.label, color: f.ESILast ? "#e11d48" : (f.ESIApplicable && (parseFloat(f.WorkingDays || 0) === 0 || parseFloat(f.PaidDay || 0) === 0) ? "#d97706" : "#475569"), fontWeight: 600 }}>
                    Last Working:
                  </label>
                  <input 
                    type="date" 
                    style={{ ...S.input, width: 135, borderColor: f.ESILast ? "#e11d48" : (f.ESIApplicable && (parseFloat(f.WorkingDays || 0) === 0 || parseFloat(f.PaidDay || 0) === 0) ? "#f59e0b" : "#cbd5e1"), background: f.ESILast ? "#fff1f2" : "#ffffff" }} 
                    value={f.ESILast ? String(f.ESILast).split('T')[0] : (f.LastWorking ? String(f.LastWorking).split('T')[0] : "")} 
                    onChange={e => {
                      set('ESILast', e.target.value);
                      set('LastWorking', e.target.value);
                    }} 
                    disabled={isRecordLocked}
                    readOnly={isRecordLocked}
                  />

                  <div style={{ marginLeft: "auto" }}>
                    <button type="button" style={{ ...S.btn, background: "rgba(47,158,68,0.1)", color: "#2F9E44", border: "1px solid rgba(47,158,68,0.25)", fontWeight: 600, padding: "5px 24px" }} onClick={() => setActiveTab("leave")}>Leave Date</button>
                  </div>
                </div>

                {/* Row 2: PF Reason & PF Last */}
                <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ ...S.label, width: 90, textAlign: "right", color: f.PFReason ? "#e11d48" : "#475569", fontWeight: 600 }}>
                    PF Reason:
                  </label>
                  <select 
                    style={{ ...S.select, width: 220, borderColor: f.PFReason ? "#e11d48" : "#cbd5e1", background: f.PFReason ? "#fff1f2" : "#ffffff" }} 
                    value={f.PFReason || ""} 
                    onChange={e => set('PFReason', e.target.value)}
                    disabled={isRecordLocked}
                  >
                    <option value="">-- Active / Working --</option>
                    {reasonOfLeftList.map(r => (
                      <option key={r.Id} value={r.Name}>{r.Name}</option>
                    ))}
                  </select>
                  <div style={{ width: 10 }} />
                  <label style={{ ...S.label, color: f.PFLast ? "#e11d48" : "#475569", fontWeight: 600 }}>
                    PF Last:
                  </label>
                  <input 
                    type="date" 
                    style={{ ...S.input, width: 135, borderColor: f.PFLast ? "#e11d48" : "#cbd5e1", background: f.PFLast ? "#fff1f2" : "#ffffff" }} 
                    value={f.PFLast ? String(f.PFLast).split('T')[0] : ""} 
                    onChange={e => set('PFLast', e.target.value)} 
                    disabled={isRecordLocked}
                    readOnly={isRecordLocked}
                  />
                </div>
              </div>

              {/* ─── Bottom Buttons ─── */}
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start", marginTop: "12px", paddingTop: "8px", borderTop: "1px solid #e2e8f0", flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  style={{ ...S.btn, background: isRecordLocked ? "#94a3b8" : "#7c69ef", color: "#ffffff", border: "none", boxShadow: isRecordLocked ? "none" : "0 2px 6px rgba(124,105,239,0.3)", cursor: isRecordLocked ? "not-allowed" : "pointer" }}
                  onClick={() => { calculate(); handleSave(); }}
                  disabled={state.isLoading || !f.F_EmployeeMaster || isRecordLocked}
                  title={isRecordLocked ? "Transaction Locked: Saved record cannot be edited" : ""}
                >
                  {state.isLoading ? (selectedRecordId === null ? "Saving..." : "Updating...") : (isRecordLocked ? "Locked 🔒" : (selectedRecordId === null ? "Save" : "Update"))}
                </button>
                {!isAutoPayrollOn && (
                  <button
                    type="button"
                    style={{
                      ...S.btn,
                      background: "#10b981",
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={handleAddNextEmployee}
                    disabled={state.isLoading}
                    title="Select next pending employee in dropdown (Auto Payroll is OFF)"
                  >
                    <i className="fas fa-user-plus me-1" />
                    Add
                  </button>
                )}
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#f8fafc",
                    color: savedRecords.length > 0 ? "#334155" : "#94a3b8",
                    border: "1px solid #cbd5e1",
                    cursor: savedRecords.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={handlePageUp}
                  disabled={state.isLoading || savedRecords.length === 0}
                  title="Previous Saved Payroll (Shortcut: ↑ Up Arrow or PageUp)"
                >
                  <i className="fas fa-arrow-up me-1" />
                  PgUp (↑)
                </button>
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#f8fafc",
                    color: savedRecords.length > 0 ? "#334155" : "#94a3b8",
                    border: "1px solid #cbd5e1",
                    cursor: savedRecords.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={handlePageDown}
                  disabled={state.isLoading || savedRecords.length === 0}
                  title="Next Saved Payroll (Shortcut: ↓ Down Arrow or PageDown)"
                >
                  PgDn (↓)
                  <i className="fas fa-arrow-down ms-1" />
                </button>
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#fff",
                    color: isRecordLocked ? "#94a3b8" : "#ef4444",
                    border: isRecordLocked ? "1px solid #cbd5e1" : "1px solid #fca5a5",
                    cursor: (f.F_EmployeeMaster && !isRecordLocked) ? "pointer" : "not-allowed",
                    boxShadow: isRecordLocked ? "none" : "0 1px 3px rgba(239, 68, 68, 0.15)",
                  }}
                  onClick={() => handleDelete()}
                  disabled={state.isLoading || !f.F_EmployeeMaster || isRecordLocked}
                  title={isRecordLocked ? "Transaction Locked: Saved record cannot be deleted" : "Delete this payroll record"}
                >
                  Delete
                </button>
                {savedRecords.length > 0 && (
                  <button
                    type="button"
                    style={{
                      ...S.btn,
                      background: "#fff",
                      color: isTransactionLocked ? "#94a3b8" : "#dc2626",
                      border: isTransactionLocked ? "1.5px solid #cbd5e1" : "1.5px solid #fca5a5",
                      cursor: isTransactionLocked ? "not-allowed" : "pointer",
                      boxShadow: isTransactionLocked ? "none" : "0 1px 3px rgba(220, 38, 38, 0.15)",
                      fontWeight: 600,
                    }}
                    onClick={promptDeleteAll}
                    disabled={state.isLoading || isDeletingAll || isTransactionLocked}
                    title={isTransactionLocked ? "Transaction Locked: Deleting records is not allowed" : "Delete all created payroll records for current month with password security"}
                  >
                    <i className="fas fa-trash-alt me-1" />
                    Delete All ({savedRecords.length})
                  </button>
                )}
                <button type="button" style={{ ...S.btn }} onClick={handleCancel} title="Clear employee and empty the form">Cancel</button>
                <div style={{ flex: 1 }} />
                <button type="button" style={{ ...S.btn, background: "#f8fafc" }} onClick={() => navigate(-1)}>Quit</button>
              </div>
            </div>
          )}

          {/* ╔══════════════════════════════════════════════════════════════╗ */}
          {/* ║  TAB 2 — DATA DISPLAY                                      ║ */}
          {/* ╚══════════════════════════════════════════════════════════════╝ */}
          {activeTab === "display" && (
            <div>
              {/* Employee row */}
              <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "4px" }}>
                <label style={{ ...S.label, width: 80, textAlign: "right" }}>Employee</label>
                <input style={{ ...S.inputRO, width: 220 }} value={f.EmployeeName || ""} readOnly />
                <label style={{ ...S.label, width: 90, textAlign: "right" }}>Father Name</label>
                <input style={{ ...S.inputRO, flex: 1 }} value={f.FatherName || ""} readOnly />
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ ...S.label, width: 80, textAlign: "right" }}>Department</label>
                <input style={{ ...S.inputRO, width: 360 }} value={f.Department || ""} readOnly />
              </div>

              {/* Paid Day */}
              <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <label style={{ ...S.label, marginRight: 10 }}>Paid Day :</label>
                <input type="number" style={{ ...S.inputRO, width: 80, textAlign: "right", fontWeight: 700, color: "#7c69ef" }} 
                  value={f.PaidDay || 0} readOnly />
              </div>

              {/* 2-Column: Left = deductions, Right = components */}
              <div style={{ display: "flex", gap: "30px" }}>
                {/* Left Column */}
                <div style={{ minWidth: 220 }}>
                  <LF label="Wages :" lw={90}>
                    <I
                      value={f.Wages}
                      onChange={e => {
                        const isDirectWages = isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList);
                        const isWagesEditable = isDirectWages || isBasicEditable;
                        if (!isWagesEditable) {
                          toast.warning("Wages is not editable as per Payroll Parameters.");
                          return;
                        }
                        setNum('Wages', e.target.value);
                      }}
                      w={100}
                      readOnly={!(isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList) || isBasicEditable)}
                      disabled={!(isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList) || isBasicEditable)}
                      style={{
                        ...(!(isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList) || isBasicEditable) ? S.inputRO : S.input),
                        width: 100,
                        backgroundColor: !(isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList) || isBasicEditable) ? "#eff6ff" : "#ffffff",
                        cursor: !(isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList) || isBasicEditable) ? "not-allowed" : "text",
                        color: "#2563eb",
                        fontWeight: 700
                      }}
                      title={isDirectWagesForEmployee(payrollParams, state.selectedEmployee, departmentList) ? "Direct Wages Entry Enabled" : isBasicEditable ? "Basic Editable Enabled" : "Earned Basic Wages for Paid Days"}
                    />
                  </LF>
                  {(payrollParams?.IsOvertime || payrollParams?.OverTime) && (
                    <LF label="Over Time :" lw={90}>
                      <input 
                        style={{ ...S.inputRO, width: 100 }} 
                        value={to2Dec(f.OverTime)} 
                        readOnly 
                        title={`OT: ${f.OvertimeHours || 0} hrs${(payrollParams?.IsOTOn26Days || payrollParams?.OvertimeOn26Days) ? " | Divisor: 26 Days" : ` | Divisor: ${f.MonthDays || 26} Days`}${(payrollParams?.IsDoubleOT || payrollParams?.DoubleOvertimeAmount) ? " | Double OT (2x)" : ""}`} 
                      />
                    </LF>
                  )}
                  {payrollParams?.IsPF && (
                    <>
                      <LF label="Emplr Pf :" lw={90}>
                        <input
                          style={{ ...S.inputRO, width: 100, ...(payrollParams?.IsFullPFEmployer ? { color: "#166534", fontWeight: 700 } : {}) }}
                          value={to2Dec(f.EmplrPF)}
                          readOnly
                          title={
                            payrollParams?.IsFullPFEmployer
                              ? "Full PF by Employer (Employee PF absorbed into Employer PF)"
                              : `Employer PF (A/C 1): ₹${to2Dec(f.EmplrPF)} | Total Employer Share: ₹${to2Dec((parseFloat(f.EmplrPF) || 0) + (parseFloat(f.Pension) || 0))}${f.EmplrPFOverLimit ? " | Overlimit ON (Full Wages - Pension)" : " | Capped at Statutory Ceiling"}`
                          }
                        />
                      </LF>
                      <LF label="Pension :" lw={90}>
                        <input
                          style={{ ...S.inputRO, width: 100 }}
                          value={to2Dec(f.Pension)}
                          readOnly
                          title={`EPS A/C 10 (Pension): ₹${to2Dec(f.Pension)} | Total Employer Share (EPF+EPS): ₹${to2Dec((parseFloat(f.EmplrPF) || 0) + (parseFloat(f.Pension) || 0))}${
                            f.PensionOverLimit || f.HigherPension ? " | Overlimit / Higher Pension ON" : " | Capped at Statutory Ceiling"
                          }${f.DateOfBirth ? (() => {
                            const [y] = String(f.DateOfBirth).split("-");
                            const age = (new Date()).getFullYear() - parseInt(y || "1970", 10);
                            return age >= 58 ? " | Age >= 58 (Member Pension Diverted to EPF)" : ` | Age: ${age}`;
                          })() : ""}`}
                        />
                      </LF>
                      <LF label="PF :" lw={90}>
                        <input
                          style={{ ...S.inputRO, width: 100, ...(payrollParams?.IsFullPFEmployer ? { color: "#166534", fontWeight: 700 } : {}) }}
                          value={to2Dec(f.PF)}
                          readOnly
                          title={
                            payrollParams?.IsFullPFEmployer
                              ? "Full PF by Employer Enabled (Employee PF Deduction = 0)"
                              : `Employee PF (A/C 1): ₹${to2Dec(f.PF)}${f.EmpleeDedOverLimit ? " | Overlimit ON (Full Earned Wages)" : " | Capped at Statutory Limit (₹15,000 pro-rata)"}${parseFloat(f.ExtraPF) > 0 ? ` | Extra PF/VPF: ₹${f.ExtraPF}` : ""}${f.PFCalculationType === "FixedAmount" ? ` | Fixed PF: ₹${f.FixedPFSalary}` : ""}`
                          }
                        />
                      </LF>
                      <LF label="ArrearPF :" lw={90}><I value={f.ArrearPF} onChange={e => setNum('ArrearPF', e.target.value)} w={100} /></LF>
                      <LF label="Arr ERPF :" lw={90}><I value={f.ArrERPF} onChange={e => setNum('ArrERPF', e.target.value)} w={100} /></LF>
                      <LF label="Arr Pens. :" lw={90}><I value={f.ArrPens} onChange={e => setNum('ArrPens', e.target.value)} w={100} /></LF>
                    </>
                  )}
                  {(payrollParams?.IsESI || !payrollParams) && (
                    <>
                      <LF label="Emplr Esi :" lw={90}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <input
                            style={{ ...S.inputRO, width: 100, ...(payrollParams?.IsFullESIEmployer ? { color: "#166534", fontWeight: 700 } : {}) }}
                            value={to2Dec(f.EmplrESI)}
                            readOnly
                            title={(() => {
                              const paidD = parseFloat(f.PaidDay) || 0;
                              const grossVal = parseFloat(f.GrossAmount || f.Wages || 0);
                              const avgD = paidD > 0 ? grossVal / paidD : 0;
                              if (payrollParams?.IsFullESIEmployer) {
                                return `Full ESI by Employer (4.00%): ₹${to2Dec(f.EmplrESI)} (Employee ESI absorbed into Employer ESI)`;
                              }
                              if (avgD > 0 && avgD <= 176) {
                                return `Employer ESI (3.25%): ₹${to2Dec(f.EmplrESI)} | Avg daily wage ₹${to2Dec(avgD)} <= ₹176 (Employee Exempt from 0.75%)`;
                              }
                              return `Employer ESI (3.25%): ₹${to2Dec(f.EmplrESI)}`;
                            })()}
                          />
                          <label
                            style={{
                              fontSize: "11px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                              margin: 0,
                              cursor: "pointer",
                              userSelect: "none",
                              color: f.ESIApplicable ? "#16a34a" : "#94a3b8",
                              fontWeight: 600,
                            }}
                            title="Toggle ESI Applicability for this employee"
                          >
                            <input
                              type="checkbox"
                              checked={!!f.ESIApplicable}
                              onChange={e => set('ESIApplicable', e.target.checked)}
                              style={{ cursor: "pointer", width: 13, height: 13 }}
                            />
                            {f.ESIApplicable ? "ESI On" : "No ESI"}
                          </label>
                        </div>
                      </LF>
                      <LF label="ESI :" lw={90}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <input
                            style={{
                              ...S.inputRO,
                              width: 100,
                              ...(payrollParams?.IsFullESIEmployer ? { color: "#166534", fontWeight: 700 } : {}),
                              ...((parseFloat(f.PaidDay) > 0 && (parseFloat(f.GrossAmount || f.Wages || 0) / parseFloat(f.PaidDay)) <= 176) ? { color: "#b45309", background: "#fffbeb", borderColor: "#fde68a" } : {})
                            }}
                            value={to2Dec(f.ESI)}
                            readOnly
                            title={(() => {
                              const paidD = parseFloat(f.PaidDay) || 0;
                              const grossVal = parseFloat(f.GrossAmount || f.Wages || 0);
                              const avgD = paidD > 0 ? grossVal / paidD : 0;
                              const isPwd = !!(state.selectedEmployee?.IsHandicap || f.IsHandicap);
                              const ceilingLimit = isPwd ? 25000 : 21000;
                              if (payrollParams?.IsFullESIEmployer) {
                                return "Full ESI by Employer Enabled (Employee ESI = ₹0, Employer pays full 4.00%)";
                              }
                              if (paidD > 0 && avgD <= 176) {
                                return `Avg daily wage ₹${to2Dec(avgD)} <= ₹176: Employee Exempt under Sec 42(1) (0% deduction, Employer 3.25%)`;
                              }
                              if (grossVal > ceilingLimit && (parseFloat(f.ESI) || 0) > 0) {
                                return `Rule 50 Active: Gross ₹${to2Dec(grossVal)} crossed limit mid-cycle; covered till 6-month cycle ends`;
                              }
                              if (grossVal > ceilingLimit && (parseFloat(f.ESI) || 0) === 0) {
                                return `Excluded Employee: Wage exceeds statutory limit of ₹${ceilingLimit.toLocaleString()}`;
                              }
                              return `Employee ESI (0.75%): ₹${to2Dec(f.ESI)}${isPwd ? " | PWD Employee (Limit ₹25,000)" : ""}`;
                            })()}
                          />
                          {f.ESIApplicable && (
                            (() => {
                              const paidD = parseFloat(f.PaidDay) || 0;
                              const grossVal = parseFloat(f.GrossAmount || f.Wages || 0);
                              const avgD = paidD > 0 ? grossVal / paidD : 0;
                              const isPwd = !!(state.selectedEmployee?.IsHandicap || f.IsHandicap);
                              const ceiling = isPwd ? 25000 : 21000;
                              if (payrollParams?.IsFullESIEmployer) {
                                return (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      background: "#dcfce7",
                                      color: "#166534",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap"
                                    }}
                                    title="Employer pays full 4% ESI. Employee share is ₹0."
                                  >
                                    Full ESI (ER)
                                  </span>
                                );
                              }
                              if (paidD > 0 && avgD <= 176) {
                                return (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      background: "#fef3c7",
                                      color: "#92400e",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap"
                                    }}
                                    title={`Average Daily Wage: ₹${to2Dec(avgD)} <= ₹176 per day. Employee contribution is exempt under ESI Act Section 42(1).`}
                                  >
                                    ≤ ₹176 Exempt
                                  </span>
                                );
                              }
                              if (grossVal > ceiling && (parseFloat(f.ESI) || 0) > 0) {
                                return (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      background: "#eff6ff",
                                      color: "#1d4ed8",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      border: "1px solid #bfdbfe"
                                    }}
                                    title="ESIC Rule 50: Employee joined or started contribution period under limit, covered on full wage till cycle end."
                                  >
                                    Rule 50 Lock
                                  </span>
                                );
                              }
                              if (grossVal > ceiling && (parseFloat(f.ESI) || 0) === 0) {
                                return (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      background: "#f1f5f9",
                                      color: "#64748b",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap"
                                    }}
                                    title={`Wage ₹${to2Dec(grossVal)} exceeds statutory limit ₹${ceiling.toLocaleString()}. Excluded Employee under ESI Act.`}
                                  >
                                    &gt; ₹{ceiling >= 1000 ? ceiling / 1000 + 'k' : ceiling} Excluded
                                  </span>
                                );
                              }
                              return null;
                            })()
                          )}
                        </div>
                      </LF>
                    </>
                  )}
                  {payrollParams?.IsBonusParameter && (
                    <LF label="Bonus :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.Bonus)} readOnly /></LF>
                  )}
                  {payrollParams?.IsGratuity && (
                    <LF label="Gratuity :" lw={90}><input style={{ ...S.inputRO, width: 100 }} value={to2Dec(f.Gratuity)} readOnly /></LF>
                  )}
                </div>

                {/* Right Column */}
                {(() => {
                  const totalLeave = Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                  const paidDays =
                    (parseFloat(f.WorkingDays) || 0) +
                    (parseFloat(f.LeaveWages) || 0) +
                    (parseFloat(f.FestivalHoliday) || 0) +
                    (parseFloat(f.WeeklyHoliday) || 0) +
                    totalLeave;
                  
                  const effectiveAl = getEffectiveAllowances(rawAllowances, selectedMonth, selectedYear);
                  const effectiveDl = getEffectiveDeductions(rawDeductions, selectedMonth, selectedYear);

                  const hasDynAl = Array.from({ length: 10 }).some((_, i) => !!f[`Allowance${i + 1}Name`]);
                  const hasDynDl = Array.from({ length: 10 }).some((_, i) => !!f[`Deduction${i + 1}Name`]);

                  return (
                    <div style={{ minWidth: 220 }}>
                      {effectiveAl.length > 0 ? (
                        effectiveAl.map(a => {
                          const name = allowanceMasterList.find(m => String(m.Id) === String(a.F_AllowanceMaster))?.Name || `Allow ${a.F_AllowanceMaster}`;
                          const computed = computeSingleAllowance(a, f.MonthDays, paidDays, f.BasicSalary);
                          return (
                            <LF key={a.F_AllowanceMaster} label={name} lw={100}>
                              <input style={{ ...S.inputRO, width: 100 }} value={to2Dec(computed)} readOnly />
                            </LF>
                          );
                        })
                      ) : (
                        hasDynAl && Array.from({ length: 10 }).map((_, i) => {
                          const name = f[`Allowance${i + 1}Name`];
                          const computed = parseFloat(f[`CalculatedAllowance${i + 1}`]) || 0;
                          if (!name) return null;
                          return (
                            <LF key={`dyn_al_${i}`} label={name} lw={100}>
                              <input style={{ ...S.inputRO, width: 100 }} value={to2Dec(computed)} readOnly />
                            </LF>
                          );
                        })
                      )}

                      {(effectiveDl.length > 0 || hasDynDl) && (
                        <div style={{ borderTop: "1px dashed #cbd5e1", marginTop: 4, paddingTop: 4 }}>
                          {effectiveDl.length > 0 ? (
                            effectiveDl.map(d => {
                              const name = deductionMasterList.find(m => String(m.Id) === String(d.F_DeductionMaster))?.Name || `Deduct ${d.F_DeductionMaster}`;
                              const computed = computeSingleDeduction(d, f.MonthDays, paidDays, f.BasicSalary);
                              return (
                                <LF key={d.F_DeductionMaster} label={`${name}(-)`} lw={100}>
                                  <input style={{ ...S.inputRO, width: 100, color: "#ef4444" }} value={to2Dec(computed)} readOnly />
                                </LF>
                              );
                            })
                          ) : (
                            Array.from({ length: 10 }).map((_, i) => {
                              const name = f[`Deduction${i + 1}Name`];
                              const computed = parseFloat(f[`CalculatedDeduction${i + 1}`]) || 0;
                              if (!name) return null;
                              return (
                                <LF key={`dyn_dl_${i}`} label={`${name}(-)`} lw={100}>
                                  <input style={{ ...S.inputRO, width: 100, color: "#ef4444" }} value={to2Dec(computed)} readOnly />
                                </LF>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ─── Summary Row ─── */}
              <div style={{ display: "flex", alignItems: "center", gap: "15px", marginTop: "14px", paddingTop: "8px", borderTop: "1px solid #e2e8f0", justifyContent: "center", flexWrap: "wrap" }}>
                <span style={S.summaryLabel}>Total Allowances:</span><span style={S.summaryVal}>{to2Dec(f.TotalAllowances)}</span>
                <span style={S.summaryLabel}>Total Deductions:</span><span style={S.summaryVal}>{to2Dec(f.TotalDeductions)}</span>
                <span style={S.summaryLabel}>Gross Amount:</span><span style={S.summaryVal}>{to2Dec(f.GrossAmount)}</span>
                <span style={S.summaryLabel}>Total Deduction :</span><span style={S.summaryVal}>{to2Dec(f.TotalDeduction)}</span>
              </div>
              <div style={{ textAlign: "center", marginTop: "8px" }}>
                <span style={{ ...S.summaryLabel, fontSize: "14px" }}>NET AMOUNT:</span>
                <span style={{ ...S.summaryVal, fontSize: "14px", fontWeight: 700, background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#7c69ef", marginLeft: 10 }}>{to2Dec(f.NetAmount)}</span>
              </div>

              {/* ─── Bottom Buttons ─── */}
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start", marginTop: "12px", paddingTop: "8px", borderTop: "1px solid #e2e8f0", flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  data-display-enter
                  style={{ ...S.btn, background: isRecordLocked ? "#94a3b8" : "#7c69ef", color: "#ffffff", border: "none", boxShadow: isRecordLocked ? "none" : "0 2px 6px rgba(124,105,239,0.3)", cursor: isRecordLocked ? "not-allowed" : "pointer" }}
                  onClick={() => {
                    if (!isRecordLocked) {
                      const md = parseFloat(f.MonthDays) || 0;
                      const tot = (parseFloat(f.WorkingDays) || 0) +
                        (parseFloat(f.LeaveWages) || 0) +
                        (parseFloat(f.FestivalHoliday) || 0) +
                        (parseFloat(f.WeeklyHoliday) || 0) +
                        (parseFloat(f.LeaveWithWages) > 0 && !parseFloat(f.LeaveWages) ? parseFloat(f.LeaveWithWages) : 0) +
                        Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                      if (md > 0 && tot > md) {
                        toast.error(`Total days (${Math.round(tot * 100) / 100}) cannot exceed Month Days (${md})! Please adjust days.`);
                        return;
                      }
                      calculate();
                      setShowSaveModal(true);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!isRecordLocked) {
                        const md = parseFloat(f.MonthDays) || 0;
                        const tot = (parseFloat(f.WorkingDays) || 0) +
                          (parseFloat(f.LeaveWages) || 0) +
                          (parseFloat(f.FestivalHoliday) || 0) +
                          (parseFloat(f.WeeklyHoliday) || 0) +
                          (parseFloat(f.LeaveWithWages) > 0 && !parseFloat(f.LeaveWages) ? parseFloat(f.LeaveWithWages) : 0) +
                          Object.values(leaveValues).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                        if (md > 0 && tot > md) {
                          toast.error(`Total days (${Math.round(tot * 100) / 100}) cannot exceed Month Days (${md})! Please adjust days.`);
                          return;
                        }
                        calculate();
                        setShowSaveModal(true);
                      }
                    }
                  }}
                  disabled={state.isLoading || !f.F_EmployeeMaster || isRecordLocked}
                  title={isRecordLocked ? "Transaction Locked: Saved record cannot be edited" : ""}
                >
                  {state.isLoading ? (selectedRecordId === null ? "Saving..." : "Updating...") : (isRecordLocked ? "Locked 🔒" : (selectedRecordId === null ? "Save" : "Update"))}
                </button>
                {!isAutoPayrollOn && (
                  <button
                    type="button"
                    style={{
                      ...S.btn,
                      background: "#10b981",
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={handleAddNextEmployee}
                    disabled={state.isLoading}
                    title="Select next pending employee in dropdown (Auto Payroll is OFF)"
                  >
                    <i className="fas fa-user-plus me-1" />
                    Add
                  </button>
                )}
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#f8fafc",
                    color: savedRecords.length > 0 ? "#334155" : "#94a3b8",
                    border: "1px solid #cbd5e1",
                    cursor: savedRecords.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={handlePageUp}
                  disabled={state.isLoading || savedRecords.length === 0}
                  title="Previous Saved Payroll (Shortcut: ↑ Up Arrow or PageUp)"
                >
                  <i className="fas fa-arrow-up me-1" />
                  PgUp (↑)
                </button>
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#f8fafc",
                    color: savedRecords.length > 0 ? "#334155" : "#94a3b8",
                    border: "1px solid #cbd5e1",
                    cursor: savedRecords.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={handlePageDown}
                  disabled={state.isLoading || savedRecords.length === 0}
                  title="Next Saved Payroll (Shortcut: ↓ Down Arrow or PageDown)"
                >
                  PgDn (↓)
                  <i className="fas fa-arrow-down ms-1" />
                </button>
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#fff",
                    color: isRecordLocked ? "#94a3b8" : "#ef4444",
                    border: isRecordLocked ? "1px solid #cbd5e1" : "1px solid #fca5a5",
                    cursor: (f.F_EmployeeMaster && !isRecordLocked) ? "pointer" : "not-allowed",
                    boxShadow: isRecordLocked ? "none" : "0 1px 3px rgba(239, 68, 68, 0.15)",
                  }}
                  onClick={() => handleDelete()}
                  disabled={state.isLoading || !f.F_EmployeeMaster || isRecordLocked}
                  title={isRecordLocked ? "Transaction Locked: Saved record cannot be deleted" : "Delete this payroll record"}
                >
                  Delete
                </button>
                {savedRecords.length > 0 && (
                  <button
                    type="button"
                    style={{
                      ...S.btn,
                      background: "#fff",
                      color: isTransactionLocked ? "#94a3b8" : "#dc2626",
                      border: isTransactionLocked ? "1.5px solid #cbd5e1" : "1.5px solid #fca5a5",
                      cursor: isTransactionLocked ? "not-allowed" : "pointer",
                      boxShadow: isTransactionLocked ? "none" : "0 1px 3px rgba(220, 38, 38, 0.15)",
                      fontWeight: 600,
                    }}
                    onClick={promptDeleteAll}
                    disabled={state.isLoading || isDeletingAll || isTransactionLocked}
                    title={isTransactionLocked ? "Transaction Locked: Deleting records is not allowed" : "Delete all created payroll records for current month with password security"}
                  >
                    <i className="fas fa-trash-alt me-1" />
                    Delete All ({savedRecords.length})
                  </button>
                )}
                <button type="button" style={{ ...S.btn }} onClick={handleCancel} title="Clear employee and empty the form">Cancel</button>
                <div style={{ flex: 1 }} />
                <button type="button" style={{ ...S.btn, background: "#f8fafc" }} onClick={() => navigate(-1)}>Quit</button>
              </div>
            </div>
          )}

          {/* ╔══════════════════════════════════════════════════════════════╗ */}
          {/* ║  TAB 3 — LEAVE DATE                                        ║ */}
          {/* ╚══════════════════════════════════════════════════════════════╝ */}
          {activeTab === "leave" && (
            <div>
              {/* Leave With Wages */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px", marginTop: "6px" }}>
                <div style={{ border: "1px solid #bbf7d0", padding: "3px 12px", background: "#f0fdf4", fontSize: "12px", fontWeight: 600, borderRadius: "6px", color: "#16a34a" }}>Leave with Wages</div>
                <I value={f.LeaveWithWages} onChange={e => setNum('LeaveWithWages', e.target.value)} w={60} />
              </div>

              {/* Leave From / To - Date only (YYYY-MM-DD format) - Restricted to current payroll month */}
              <div style={{ display: "flex", gap: "30px", marginBottom: "20px" }}>
                <div>
                  <LF label="Leave From 1:" lw={100}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.LeaveFrom1} onChange={e => !isRecordLocked && set('LeaveFrom1', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave From 2:" lw={100}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.LeaveFrom2} onChange={e => !isRecordLocked && set('LeaveFrom2', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave From 3:" lw={100}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.LeaveFrom3} onChange={e => !isRecordLocked && set('LeaveFrom3', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                </div>
                <div>
                  <LF label="Leave To 1:" lw={80}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.LeaveTo1} onChange={e => !isRecordLocked && set('LeaveTo1', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave To 2:" lw={80}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.LeaveTo2} onChange={e => !isRecordLocked && set('LeaveTo2', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Leave To 3:" lw={80}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.LeaveTo3} onChange={e => !isRecordLocked && set('LeaveTo3', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                </div>
              </div>

              {/* Absent Days */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                <div style={{ border: "1px solid #fca5a5", padding: "3px 12px", background: "#fef2f2", fontSize: "12px", fontWeight: 600, borderRadius: "6px", color: "#ef4444" }}>Absent Days :</div>
                <I value={f.AbsentDays} onChange={e => setNum('AbsentDays', e.target.value)} w={60} />
              </div>

              {/* Absent From / To - Date only (YYYY-MM-DD format) - Restricted to current payroll month */}
              <div style={{ display: "flex", gap: "30px" }}>
                <div>
                  <LF label="Absent From 1:" lw={110}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.AbsentFrom1} onChange={e => !isRecordLocked && set('AbsentFrom1', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent From 2:" lw={110}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.AbsentFrom2} onChange={e => !isRecordLocked && set('AbsentFrom2', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent From 3:" lw={110}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.AbsentFrom3} onChange={e => !isRecordLocked && set('AbsentFrom3', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                </div>
                <div>
                  <LF label="Absent To 1:" lw={80}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.AbsentTo1} onChange={e => !isRecordLocked && set('AbsentTo1', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent To 2:" lw={80}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.AbsentTo2} onChange={e => !isRecordLocked && set('AbsentTo2', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                  <LF label="Absent To 3:" lw={80}><input type="date" style={isRecordLocked ? S.inputRO : S.input} value={f.AbsentTo3} onChange={e => !isRecordLocked && set('AbsentTo3', e.target.value)} disabled={isRecordLocked} readOnly={isRecordLocked} {...getDateRangeForMonth()} /></LF>
                </div>
              </div>

              {/* ─── Bottom Buttons ─── */}
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-start", marginTop: "12px", paddingTop: "8px", borderTop: "1px solid #e2e8f0", flexWrap: "wrap", alignItems: "center" }}>
                <button
                  type="button"
                  style={{ ...S.btn, background: isRecordLocked ? "#94a3b8" : "#7c69ef", color: "#ffffff", border: "none", boxShadow: isRecordLocked ? "none" : "0 2px 6px rgba(124,105,239,0.3)", cursor: isRecordLocked ? "not-allowed" : "pointer" }}
                  onClick={() => { calculate(); handleSave(); }}
                  disabled={state.isLoading || !f.F_EmployeeMaster || isRecordLocked}
                  title={isRecordLocked ? "Transaction Locked: Saved record cannot be edited" : ""}
                >
                  {state.isLoading ? (selectedRecordId === null ? "Saving..." : "Updating...") : (isRecordLocked ? "Locked 🔒" : (selectedRecordId === null ? "Save" : "Update"))}
                </button>
                {!isAutoPayrollOn && (
                  <button
                    type="button"
                    style={{
                      ...S.btn,
                      background: "#10b981",
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                    onClick={handleAddNextEmployee}
                    disabled={state.isLoading}
                    title="Select next pending employee in dropdown (Auto Payroll is OFF)"
                  >
                    <i className="fas fa-user-plus me-1" />
                    Add
                  </button>
                )}
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#f8fafc",
                    color: savedRecords.length > 0 ? "#334155" : "#94a3b8",
                    border: "1px solid #cbd5e1",
                    cursor: savedRecords.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={handlePageUp}
                  disabled={state.isLoading || savedRecords.length === 0}
                  title="Previous Saved Payroll (Shortcut: ↑ Up Arrow or PageUp)"
                >
                  <i className="fas fa-arrow-up me-1" />
                  PgUp (↑)
                </button>
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#f8fafc",
                    color: savedRecords.length > 0 ? "#334155" : "#94a3b8",
                    border: "1px solid #cbd5e1",
                    cursor: savedRecords.length > 0 ? "pointer" : "not-allowed",
                    fontWeight: 600,
                  }}
                  onClick={handlePageDown}
                  disabled={state.isLoading || savedRecords.length === 0}
                  title="Next Saved Payroll (Shortcut: ↓ Down Arrow or PageDown)"
                >
                  PgDn (↓)
                  <i className="fas fa-arrow-down ms-1" />
                </button>
                <button
                  type="button"
                  style={{
                    ...S.btn,
                    background: "#fff",
                    color: isRecordLocked ? "#94a3b8" : "#ef4444",
                    border: isRecordLocked ? "1px solid #cbd5e1" : "1px solid #fca5a5",
                    cursor: (f.F_EmployeeMaster && !isRecordLocked) ? "pointer" : "not-allowed",
                    boxShadow: isRecordLocked ? "none" : "0 1px 3px rgba(239, 68, 68, 0.15)",
                  }}
                  onClick={() => handleDelete()}
                  disabled={state.isLoading || !f.F_EmployeeMaster || isRecordLocked}
                  title={isRecordLocked ? "Transaction Locked: Saved record cannot be deleted" : "Delete this payroll record"}
                >
                  Delete
                </button>
                {savedRecords.length > 0 && (
                  <button
                    type="button"
                    style={{
                      ...S.btn,
                      background: "#fff",
                      color: isTransactionLocked ? "#94a3b8" : "#dc2626",
                      border: isTransactionLocked ? "1.5px solid #cbd5e1" : "1.5px solid #fca5a5",
                      cursor: isTransactionLocked ? "not-allowed" : "pointer",
                      boxShadow: isTransactionLocked ? "none" : "0 1px 3px rgba(220, 38, 38, 0.15)",
                      fontWeight: 600,
                    }}
                    onClick={promptDeleteAll}
                    disabled={state.isLoading || isDeletingAll || isTransactionLocked}
                    title={isTransactionLocked ? "Transaction Locked: Deleting records is not allowed" : "Delete all created payroll records for current month with password security"}
                  >
                    <i className="fas fa-trash-alt me-1" />
                    Delete All ({savedRecords.length})
                  </button>
                )}
                <button type="button" style={{ ...S.btn }} onClick={handleCancel} title="Clear employee and empty the form">Cancel</button>
                <div style={{ flex: 1 }} />
                <button type="button" style={{ ...S.btn, background: "#f8fafc" }} onClick={() => navigate(-1)}>Quit</button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ─── Save Confirmation Modal ─────────────────────────────────────── */}
      {showSaveModal && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#ffffff', border: '1px solid #e2e8f0',
            borderRadius: '10px', width: 360, overflow: 'hidden',
            fontFamily: "inherit", fontSize: 12.5,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}>
            <div style={{
              background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", color: "#fff",
              padding: "10px 16px", fontWeight: 600, fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <span>Confirm Payroll Save</span>
              <span onClick={() => setShowSaveModal(false)} style={{ cursor: "pointer", fontWeight: "bold", fontSize: 14, opacity: 0.8 }} title="Close">✕</span>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: "#64748b" }}>Employee:</span> <strong style={{ color: "#1e293b" }}>{f.EmployeeName || '—'}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: "#64748b" }}>Month / Year:</span> <strong style={{ color: "#1e293b" }}>{selectedMonth} {selectedYear}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: "#64748b" }}>Paid Days:</span> <strong style={{ color: "#1e293b" }}>{f.PaidDay}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: "#64748b" }}>Gross Amount:</span> <strong style={{ color: "#1e293b" }}>₹ {(f.GrossAmount || 0).toFixed(2)}</strong></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px dashed #e2e8f0" }}>
                <span style={{ fontWeight: 600, color: "#334155" }}>NET AMOUNT:</span>
                <span style={{ fontWeight: 700, fontSize: 14, color: "#7c69ef" }}>₹ {(f.NetAmount || 0).toFixed(2)}</span>
              </div>
            </div>
            <div style={{ padding: "10px 16px", background: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                style={{ fontSize: 12, fontWeight: 600, padding: "5px 16px", border: "1px solid #cbd5e1", background: "#ffffff", color: "#64748b", borderRadius: 4, cursor: "pointer" }}
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                autoFocus
                type="button"
                style={{ fontSize: 12, fontWeight: 600, padding: "5px 20px", border: "none", background: "#7c69ef", color: "#ffffff", borderRadius: 4, cursor: "pointer" }}
                onClick={() => { handleSave(); setShowSaveModal(false); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); setShowSaveModal(false); } }}
              >
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Arrow Button (Right Side Sticky) ─── */}
      {isSessionReady && savedRecords.length > 0 && (
        <button
          onClick={() => setShowSidePanel(!showSidePanel)}
          style={{
            position: "fixed", right: showSidePanel ? "320px" : "12px", top: "50%", transform: "translateY(-50%)", zIndex: 1001,
            padding: "10px 14px", background: "#7c69ef", color: "#fff", border: "none",
            borderRadius: "50%", width: "44px", height: "44px", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "18px", fontWeight: 600, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(124,105,239,0.4)", transition: "all 0.3s"
          }}
        >
          {showSidePanel ? "◀" : "▶"}
        </button>
      )}

      {/* ─── Side Panel: Saved Records ─── */}
      {isSessionReady && savedRecords.length > 0 && showSidePanel && (
        <div style={{
          position: "fixed", right: 0, top: 0, bottom: 0, width: "320px",
          background: "#ffffff", boxShadow: "-4px 0 20px rgba(0,0,0,0.12)",
          display: "flex", flexDirection: "column",
          zIndex: 999, borderLeft: "1px solid #e2e8f0"
        }}>
          {/* Header */}
          <div style={{ padding: "16px 16px 8px 16px" }}>
            <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#1e293b", marginBottom: "12px", paddingBottom: "8px", borderBottom: "2px solid #7c69ef", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>📋 Saved Records ({savedRecords.length})</span>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  type="button"
                  onClick={promptDeleteAll}
                  disabled={isTransactionLocked}
                  style={{
                    background: isTransactionLocked ? "#f1f5f9" : "#fee2e2",
                    color: isTransactionLocked ? "#94a3b8" : "#dc2626",
                    border: isTransactionLocked ? "1px solid #cbd5e1" : "1px solid #fca5a5",
                    borderRadius: "5px",
                    padding: "3px 8px",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: isTransactionLocked ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "all 0.15s ease",
                  }}
                  title={isTransactionLocked ? "Transaction Locked: Deleting records is not allowed" : "Delete all saved payroll records with password security"}
                >
                  <i className="fas fa-trash-alt" /> Delete All
                </button>
                <span onClick={() => setShowSidePanel(false)} style={{ cursor: "pointer", fontSize: 13, color: "#94a3b8" }}>✕</span>
              </div>
            </div>
            <input
              type="text"
              placeholder="Search Employee..."
              value={panelSearch}
              onChange={(e) => setPanelSearch(e.target.value)}
              style={{
                width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1",
                borderRadius: "6px", fontSize: "12px", fontWeight: 500, boxSizing: "border-box", outline: "none"
              }}
            />
          </div>

          {/* Scrollable list */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
            {savedRecords.filter(record => {
              const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
              const searchLower = panelSearch.toLowerCase();
              return (emp?.Name || "").toLowerCase().includes(searchLower) || (emp?.FatherName || "").toLowerCase().includes(searchLower);
            }).map(record => {
              const emp = state.employees.find(e => String(e.Id) === String(record.F_EmployeeMaster));
              const isSelected = selectedRecordId === record.Id;
              return (
                <div
                  key={record.Id}
                  onClick={() => loadSavedRecord(record)}
                  style={{
                    padding: "9px 12px", marginBottom: "6px", borderRadius: "6px",
                    border: isSelected ? "1.5px solid #7c69ef" : "1px solid #e2e8f0",
                    background: isSelected ? "#f5f3ff" : "#f8fafc",
                    cursor: "pointer", transition: "all 0.15s",
                    fontSize: "12px", fontWeight: 500, color: isSelected ? "#6d28d9" : "#334155",
                    display: "flex", alignItems: "center", justifyContent: "space-between"
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 6 }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp?.Name || "N/A"}</div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Father: {emp?.FatherName || "N/A"}</div>
                  </div>
                  {isTransactionLocked ? (
                    <span title="Transaction Locked: Saved record cannot be deleted" style={{ fontSize: "13px", padding: "4px 8px" }}>🔒</span>
                  ) : (
                    <button
                      type="button"
                      style={{
                        border: "none",
                        background: "rgba(239, 68, 68, 0.1)",
                        color: "#ef4444",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        cursor: "pointer",
                        fontSize: "13px",
                        lineHeight: 1,
                        transition: "all 0.15s",
                      }}
                      title="Delete this payroll record"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(record.Id);
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sticky Bottom Footer: Update & Recalculate All + Delete All */}
          <div style={{
            padding: "12px 16px",
            borderTop: "1px solid #e2e8f0",
            background: "#f8fafc",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.03)",
            display: "flex",
            flexDirection: "column",
            gap: "8px"
          }}>
            <button
              type="button"
              disabled={isRecalculatingAll || state.isLoading || isTransactionLocked}
              onClick={() => setShowRecalculateModal(true)}
              style={{
                width: "100%",
                padding: "9px 12px",
                background: (isRecalculatingAll || isTransactionLocked) ? "#94a3b8" : "linear-gradient(135deg, #7c69ef 0%, #6366f1 100%)",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                fontSize: "12.5px",
                fontWeight: 600,
                cursor: (isRecalculatingAll || state.isLoading || isTransactionLocked) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                boxShadow: (isRecalculatingAll || isTransactionLocked) ? "none" : "0 3px 10px rgba(124,105,239,0.35)",
                transition: "all 0.2s ease"
              }}
              title={isTransactionLocked ? "Transaction Locked: Recalculating records is not allowed" : "Recalculate all saved records with latest master rates"}
            >
              <span>{isTransactionLocked ? "🔒" : "⚡"}</span>
              <span>{isRecalculatingAll ? "Recalculating..." : (isTransactionLocked ? "Transactions Locked" : `Update & Recalculate All (${savedRecords.length})`)}</span>
            </button>
            <button
              type="button"
              disabled={isDeletingAll || state.isLoading || savedRecords.length === 0 || isTransactionLocked}
              onClick={promptDeleteAll}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: isTransactionLocked ? "#f1f5f9" : "#ffffff",
                color: isTransactionLocked ? "#94a3b8" : "#ef4444",
                border: isTransactionLocked ? "1.5px solid #cbd5e1" : "1.5px solid #fca5a5",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 700,
                cursor: (isDeletingAll || state.isLoading || savedRecords.length === 0 || isTransactionLocked) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                boxShadow: isTransactionLocked ? "none" : "0 1px 3px rgba(239, 68, 68, 0.1)",
                transition: "all 0.2s ease"
              }}
              title={isTransactionLocked ? "Transaction Locked: Deleting records is not allowed" : "Delete all saved records with password protection"}
            >
              <i className="fas fa-trash-alt" />
              <span>Delete All Records ({savedRecords.length})</span>
            </button>
          </div>
        </div>
      )}

      {/* ─── Arrear Pension Statutory Higher Limit Validation Modal ─── */}
      <Modal
        show={!!arrearModalData}
        onHide={() => handleArrearModalChoice('capped')}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header
          closeButton
          style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}
        >
          <Modal.Title style={{ fontSize: "16px", fontWeight: 700, color: "#b45309", display: "flex", alignItems: "center", gap: "8px" }}>
            <span>⚠️</span>
            Pension Higher Limit Validation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "18px 22px" }}>
          {arrearModalData && (
            <div>
              <div style={{ fontSize: "13px", color: "#1e293b", marginBottom: "12px", lineHeight: "1.5" }}>
                Normal Pension aur Arrear Pension milakar statutory higher limit se upar ja raha hai:
                <br />
                <strong>{arrearModalData.cardNo ? `[${arrearModalData.cardNo}] ` : ""}{arrearModalData.empName}</strong>
              </div>

              {/* Summary Box */}
              <div style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "14px",
                fontSize: "12.5px"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ color: "#64748b" }}>Arrear Amount:</span>
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>₹{to2Dec(arrearModalData.arrear)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ color: "#64748b" }}>Employee Arrear PF (12%):</span>
                  <span style={{ fontWeight: 600, color: "#166534" }}>₹{to2Dec(arrearModalData.arrearPF)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ color: "#64748b" }}>Total Employer Share (12%):</span>
                  <span style={{ fontWeight: 600, color: "#1e293b" }}>₹{to2Dec(arrearModalData.totalEmployerArrear)}</span>
                </div>
                <div style={{ borderTop: "1px dashed #cbd5e1", margin: "8px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ color: "#64748b" }}>Normal Wages Pension:</span>
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>₹{to2Dec(arrearModalData.normalPension)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ color: "#64748b" }}>Arrear Pension (8.33%):</span>
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>₹{to2Dec(arrearModalData.fullArrPens)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", background: "#fef2f2", padding: "4px 8px", borderRadius: "4px" }}>
                  <span style={{ fontWeight: 700, color: "#991b1b" }}>Combined Pension:</span>
                  <span style={{ fontWeight: 700, color: "#dc2626" }}>₹{to2Dec(arrearModalData.combinedPension)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", padding: "4px 8px" }}>
                  <span style={{ fontWeight: 600, color: "#475569" }}>Statutory Higher Limit:</span>
                  <span style={{ fontWeight: 700, color: "#2563eb" }}>₹{to2Dec(arrearModalData.pensionCeilingLimit)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
                  <span style={{ fontWeight: 600, color: "#dc2626" }}>Exceeding Limit By:</span>
                  <span style={{ fontWeight: 700, color: "#dc2626" }}>₹{to2Dec(arrearModalData.combinedPension - arrearModalData.pensionCeilingLimit)}</span>
                </div>
              </div>

              {/* Options Explanation */}
              <div style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "6px",
                padding: "10px 12px",
                fontSize: "12px",
                color: "#1e40af",
                lineHeight: "1.5"
              }}>
                <div><strong>Choice Options:</strong></div>
                <div style={{ marginTop: "4px" }}>
                  • <strong>Yes (Calculate Full Pension):</strong> Calculate 8.33% pension on entire arrear (Pension: ₹{to2Dec(arrearModalData.fullArrPens)}, Employer EPF: ₹{to2Dec(arrearModalData.totalEmployerArrear - arrearModalData.fullArrPens)}).
                </div>
                <div style={{ marginTop: "4px" }}>
                  • <strong>No (Cap at Limit):</strong> Combined pension will not exceed ₹{to2Dec(arrearModalData.pensionCeilingLimit)} (Pension: ₹{to2Dec(arrearModalData.cappedArrPens)}, Employer EPF: ₹{to2Dec(arrearModalData.totalEmployerArrear - arrearModalData.cappedArrPens)}).
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f1f5f9", padding: "12px 20px", display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="success"
            onClick={() => handleArrearModalChoice('capped')}
            style={{ fontSize: "12.5px", fontWeight: 600, borderRadius: "6px" }}
          >
            No, Cap at Limit (₹{arrearModalData ? to2Dec(arrearModalData.cappedArrPens) : 0})
          </Button>
          <Button
            variant="warning"
            onClick={() => handleArrearModalChoice('full')}
            style={{ fontSize: "12.5px", fontWeight: 600, borderRadius: "6px", color: "#78350f" }}
          >
            Yes, Calculate Full Pension (₹{arrearModalData ? to2Dec(arrearModalData.fullArrPens) : 0})
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Bulk Recalculate Confirmation & Progress Modal ─── */}
      <Modal
        show={showRecalculateModal}
        onHide={() => !isRecalculatingAll && setShowRecalculateModal(false)}
        centered
        backdrop="static"
        keyboard={!isRecalculatingAll}
      >
        <Modal.Header
          closeButton={!isRecalculatingAll}
          style={{ background: "#f5f3ff", borderBottom: "1px solid #ddd6fe" }}
        >
          <Modal.Title style={{ fontSize: "16px", fontWeight: 700, color: "#6d28d9", display: "flex", alignItems: "center", gap: "8px" }}>
            <span>⚡</span>
            Update & Recalculate All Records
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          {!isRecalculatingAll ? (
            <div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", marginBottom: "12px" }}>
                Do you want to update and recalculate all <strong>{savedRecords.length}</strong> payroll records for <strong>{selectedMonth} {selectedYear}</strong>?
              </div>
              <div style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "14px",
                fontSize: "12.5px",
                lineHeight: "1.6",
                color: "#334155"
              }}>
                <div style={{ fontWeight: 600, color: "#475569", marginBottom: 6 }}>What will happen:</div>
                <ul style={{ margin: 0, paddingLeft: "18px" }}>
                  <li>Fetches latest <strong>Basic Salary, Allowances, and Deductions</strong> from Employee Master.</li>
                  <li>Applies updated <strong>Payroll Parameters</strong> (PF, ESI, OT, Bonus, Gratuity).</li>
                  <li><strong>Preserves attendance</strong>: Working Days, Hours, Holidays, Leaves (CL/ML), and Overtime.</li>
                  <li><strong>Preserves custom entries</strong>: Advance, Recovery, TDS, Arrear, Extra Pay, etc.</li>
                </ul>
              </div>
              <div style={{ fontSize: "12px", color: "#64748b" }}>
                💡 <em>Use this after modifying Employee Masters or Payroll Parameters to synchronize this month's payroll.</em>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", marginBottom: "12px", textAlign: "center" }}>
                🔄 Recalculating Payroll Records...
              </div>
              <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#64748b" }}>
                <span>Processing: <strong>{recalcProgress.empName || "Employee"}</strong></span>
                <span>{recalcProgress.current} / {recalcProgress.total}</span>
              </div>
              {/* Progress bar */}
              <div style={{
                width: "100%", height: "10px", background: "#e2e8f0", borderRadius: "5px", overflow: "hidden", marginBottom: "16px"
              }}>
                <div style={{
                  width: `${recalcProgress.total > 0 ? (recalcProgress.current / recalcProgress.total) * 100 : 0}%`,
                  height: "100%", background: "linear-gradient(90deg, #7c69ef, #4f46e5)",
                  transition: "width 0.2s ease"
                }} />
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", textAlign: "center" }}>
                Please do not close or refresh the browser until recalculation is complete.
              </div>
            </div>
          )}
        </Modal.Body>
        {!isRecalculatingAll && (
          <Modal.Footer style={{ borderTop: "1px solid #f1f5f9", padding: "12px 20px" }}>
            <Button
              variant="secondary"
              onClick={() => setShowRecalculateModal(false)}
              style={{ fontSize: "12.5px", fontWeight: 600, borderRadius: "6px" }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRecalculateAll}
              style={{
                fontSize: "12.5px", fontWeight: 600, borderRadius: "6px",
                background: "#7c69ef", borderColor: "#7c69ef"
              }}
            >
              ⚡ Yes, Recalculate All
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ─── Delete Confirmation Modal ("Are you sure?") ─── */}
      <Modal
        show={deleteModal.show}
        onHide={() => !state.isLoading && setDeleteModal(prev => ({ ...prev, show: false }))}
        centered
        backdrop="static"
        keyboard={!state.isLoading}
        restoreFocus={false}
      >
        <Modal.Header
          closeButton={!state.isLoading}
          style={{
            background: deleteModal.isClearingUnsaved ? "#fffbeb" : "#fff1f2",
            borderBottom: deleteModal.isClearingUnsaved ? "1px solid #fef3c7" : "1px solid #fecdd3",
          }}
        >
          <Modal.Title style={{ fontSize: "16px", fontWeight: 700, color: deleteModal.isClearingUnsaved ? "#b45309" : "#be123c", display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{deleteModal.isClearingUnsaved ? "⚠️" : "🗑️"}</span>
            {deleteModal.isClearingUnsaved ? "Clear Payroll Form Entry" : "Confirm Delete Payroll Record"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <div style={{ fontSize: "14.5px", fontWeight: 600, color: "#1e293b", marginBottom: "14px" }}>
            {deleteModal.isClearingUnsaved
              ? `No saved payroll record exists in the database for ${deleteModal.empName}. Are you sure you want to clear this entry from the form?`
              : "Are you sure you want to delete this payroll transaction record?"}
          </div>

          {!deleteModal.isClearingUnsaved && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "14px",
                fontSize: "12.5px",
                lineHeight: "1.6",
                color: "#334155",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "#64748b" }}>👤 Employee Name:</span>
                <strong style={{ color: "#1e293b" }}>{deleteModal.empName}</strong>
              </div>
              {deleteModal.empCode && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ color: "#64748b" }}>💳 Employee Code:</span>
                  <strong style={{ color: "#2563eb" }}>{deleteModal.empCode}</strong>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "#64748b" }}>📅 Payroll Session:</span>
                <strong>{deleteModal.month} {deleteModal.year}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ color: "#64748b" }}>💰 Gross Pay:</span>
                <span>₹{deleteModal.grossAmount}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px dashed #cbd5e1", paddingTop: "4px", marginTop: "4px" }}>
                <span style={{ fontWeight: 600, color: "#059669" }}>💵 Net Payable:</span>
                <strong style={{ color: "#059669", fontSize: "13px" }}>₹{deleteModal.netAmount}</strong>
              </div>
            </div>
          )}

          {!deleteModal.isClearingUnsaved && (
            <div
              style={{
                fontSize: "12px",
                color: "#dc2626",
                background: "#fee2e2",
                border: "1px solid #fca5a5",
                borderRadius: "6px",
                padding: "8px 12px",
                fontWeight: 500,
              }}
            >
              ⚠️ <strong>Warning:</strong> This action cannot be undone. The payroll calculation, attendance, allowances, and deductions for this employee in {deleteModal.month} {deleteModal.year} will be permanently removed.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f1f5f9", padding: "12px 20px" }}>
          <Button
            variant="secondary"
            onClick={() => setDeleteModal(prev => ({ ...prev, show: false }))}
            disabled={state.isLoading}
            style={{ fontSize: "12.5px", fontWeight: 600, borderRadius: "6px" }}
          >
            Cancel
          </Button>
          <Button
            variant={deleteModal.isClearingUnsaved ? "warning" : "danger"}
            onClick={executeDelete}
            disabled={state.isLoading}
            style={{
              fontSize: "12.5px",
              fontWeight: 600,
              borderRadius: "6px",
              background: deleteModal.isClearingUnsaved ? "#f59e0b" : "#dc2626",
              borderColor: deleteModal.isClearingUnsaved ? "#f59e0b" : "#dc2626",
            }}
          >
            {state.isLoading
              ? "Deleting..."
              : deleteModal.isClearingUnsaved
              ? "Yes, Clear Form"
              : "🗑️ Yes, Delete Record"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── 1. SECURE DELETE ALL PAYROLL RECORDS MODAL (PASSWORD PROTECTED) ─── */}
      <Modal
        show={showDeleteAllModal}
        onHide={() => !isDeletingAll && setShowDeleteAllModal(false)}
        centered
        backdrop="static"
        restoreFocus={false}
      >
        <Modal.Header closeButton={!isDeletingAll} className="bg-danger text-white">
          <Modal.Title className="h5 fw-bold mb-0">
            <i className="fas fa-exclamation-triangle me-2" />
            Confirm Delete All Payroll Records
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {!isDeletingAll ? (
            <>
              {/* Step 1: Warning Message */}
              <div className="alert alert-danger d-flex align-items-start gap-2 mb-3">
                <i className="fas fa-radiation-alt fa-2x flex-shrink-0 mt-1" />
                <div>
                  <strong>Permanent Data Removal Warning:</strong>
                  <p className="mb-0 mt-1" style={{ fontSize: "13px" }}>
                    Are you sure you want to delete all{" "}
                    <strong className="text-dark bg-white px-2 py-0.5 rounded border">
                      {deleteAllTarget.totalCount} payroll record{deleteAllTarget.totalCount > 1 ? "s" : ""}
                    </strong>{" "}
                    for <strong>{selectedMonth} {selectedYear}</strong> ({sessionFirm?.Name || "Active Firm"})?
                    This will permanently delete the calculated wages, allowances, deductions, and attendance records.
                  </p>
                </div>
              </div>

              {/* Target Preview */}
              <div
                className="bg-light p-2 px-3 rounded border mb-3"
                style={{ maxHeight: "110px", overflowY: "auto", fontSize: "12.5px" }}
              >
                <div className="text-muted fw-bold mb-1">Records to be deleted:</div>
                <div className="d-flex flex-wrap gap-1">
                  {deleteAllTarget.items.slice(0, 15).map((rec) => {
                    const emp = state.employees.find(e => String(e.Id) === String(rec.F_EmployeeMaster));
                    const label = emp ? `${emp.CardNo || emp.EmployeeCode || emp.SerialNo || ""}: ${emp.Name}` : (rec.EmployeeName || `ID ${rec.Id}`);
                    return (
                      <span key={rec.Id} className="badge bg-secondary">
                        {label}
                      </span>
                    );
                  })}
                  {deleteAllTarget.items.length > 15 && (
                    <span className="badge bg-dark">+{deleteAllTarget.items.length - 15} more</span>
                  )}
                </div>
              </div>

              {/* Reason / Notes (Optional) */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                  Reason for Deletion (Audit Trail Note):
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Recalculation required / Resetting payroll for month..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  style={{ fontSize: "13.5px" }}
                />
              </Form.Group>

              {/* Step 2: Master Security Password Verification */}
              <Form.Group className="mb-2">
                <Form.Label className="fw-bold text-danger d-flex justify-content-between align-items-center" style={{ fontSize: "13px" }}>
                  <span>
                    <i className="fas fa-lock me-1" />
                    Enter Security Master Password:
                  </span>
                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                    <i className="fas fa-shield-alt me-1" />
                    Authorization Required
                  </span>
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter security authorization password"
                    value={securityPassword}
                    onChange={(e) => {
                      setSecurityPassword(e.target.value);
                      setPasswordError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleConfirmDeleteAll();
                    }}
                    isInvalid={!!passwordError}
                    style={{ fontSize: "14px", letterSpacing: showPassword ? "normal" : "2px" }}
                    autoFocus
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                  </Button>
                </div>
                <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                  <i className="fas fa-info-circle me-1" />
                  Please enter the master delete password configured in <strong>Payroll Parameter</strong>.
                </Form.Text>
                {passwordError && (
                  <div className="text-danger mt-1 fw-semibold" style={{ fontSize: "12px" }}>
                    <i className="fas fa-times-circle me-1" />
                    {passwordError}
                  </div>
                )}
              </Form.Group>
            </>
          ) : (
            /* Progress state */
            <div className="text-center py-4">
              <div className="spinner-border text-danger mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Deleting...</span>
              </div>
              <h5 className="fw-bold mb-1">Deleting Payroll Records...</h5>
              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                Processed <strong>{deleteAllProgress.current}</strong> of <strong>{deleteAllProgress.total}</strong> records
              </p>
              <ProgressBar
                now={deleteAllProgress.percentage}
                label={`${deleteAllProgress.percentage}%`}
                variant="danger"
                animated
                style={{ height: "20px" }}
              />
            </div>
          )}
        </Modal.Body>
        {!isDeletingAll && (
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={() => setShowDeleteAllModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" className="fw-bold shadow-sm" onClick={handleConfirmDeleteAll}>
              <i className="fas fa-trash-alt me-1" />
              Verify &amp; Delete All Permanently
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ─── 2. DELETION RESULT SUMMARY MODAL ─── */}
      <Modal show={showDeleteAllResultModal} onHide={() => setShowDeleteAllResultModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-warning-subtle border-warning">
          <Modal.Title className="h5 fw-bold text-dark mb-0">
            <i className="fas fa-clipboard-check me-2 text-warning" />
            Payroll Deletion Summary Report
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-3 mb-3">
            <div className="flex-fill p-3 rounded bg-success-subtle border border-success-subtle text-center">
              <div className="h3 fw-bold text-success mb-0">{deleteAllResults.successCount}</div>
              <div className="text-success fw-semibold" style={{ fontSize: "12px" }}>
                Successfully Deleted
              </div>
            </div>
            <div className="flex-fill p-3 rounded bg-danger-subtle border border-danger-subtle text-center">
              <div className="h3 fw-bold text-danger mb-0">{deleteAllResults.failedCount}</div>
              <div className="text-danger fw-semibold" style={{ fontSize: "12px" }}>
                Failed to Delete
              </div>
            </div>
          </div>

          {deleteAllResults.failedItems.length > 0 && (
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Record / Employee</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {deleteAllResults.failedItems.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.empName}</td>
                      <td className="text-danger">{item.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteAllResultModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </RecordLockContext.Provider>
  );
};

export default Payroll;